import { Container, Graphics, Sprite, Texture, Text as PixiText } from 'pixi.js';

// ANALYSIS_MODE: detailed collision logging (set VITE_ANALYSIS_MODE=true in .env)
const ANALYSIS_MODE = import.meta.env.VITE_ANALYSIS_MODE === 'true';
const clientCollisionLogs: string[] = [];

// Player must be near-stationary (px/s) to open a loot container.
const LOOT_INTERACTION_MAX_SPEED = 20;
function clientLog(msg: string) {
  if (!ANALYSIS_MODE) return;
  clientCollisionLogs.push(msg);
  console.log(msg);
}
if (ANALYSIS_MODE) {
  // Expose download function on window for easy log retrieval
  (window as any).__dumpCollisionLogs = () => {
    const blob = new Blob([clientCollisionLogs.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `collision_client_${Date.now()}.log`;
    a.click();
    URL.revokeObjectURL(url);
    console.log(`Downloaded ${clientCollisionLogs.length} log entries`);
  };
  console.log('[ANALYSIS_MODE] Client collision logging active. Call __dumpCollisionLogs() in console to download.');
}
import { Scene, SceneData } from '../core/Scene';
import { SCENERY_PARALLAX } from '../core/Camera';
import { GAME_CONFIG } from '../config/constants';
import { Player } from '../entities/Player';
import { RemotePlayer } from '../entities/RemotePlayer';
import { NetworkAsteroid } from '../entities/NetworkAsteroid';
import { MiningLaser } from '../entities/MiningLaser';
import { WarpVisual } from '../entities/WarpVisual';
import { RESOURCE_COLORS } from '../entities/AsteroidGraphics';
import {
  NetworkHarvestable,
  NetworkIceAsteroid,
  NetworkGasPocket,
  NetworkWreckage,
  type HarvestableClientKind,
} from '../entities/NetworkHarvestable';
import { Projectile, type ProjectileData } from '../entities/Projectile';
import { Starfield } from '../entities/Starfield';
import { DepthLayerManager } from '../entities/DepthLayerManager';
import {
  networkManager,
  type RemotePlayerData,
  type AsteroidData,
  type HarvestableEntityData,
  type PlanetData,
  type StationData,
  type DropData,
  type SunData,
  type NebulaData,
  type GatewayData,
} from '../systems/NetworkManager';
import { ConstellationBoundaryWarning } from '../systems/ConstellationBoundaryWarning';
import { Drop } from '../entities/Drop';
import { Drone } from '../entities/Drone';
import type { DroneData } from '../systems/NetworkManager';
import { Station } from '../entities/Station';
import { Settlement } from '../entities/Settlement';
import { Gateway } from '../entities/Gateway';
import { InterdictionBeacon } from '../entities/InterdictionBeacon';
import { Sun } from '../entities/Sun';
import { Nebula } from '../entities/Nebula';
import { Planet } from '../entities/Planet';
import { CombatLaser, LaserTarget, LaserUpdateResult } from '../entities/CombatLaser';
import { getMiningZoneRadius, DISCONNECT_CODES, ENTITY_RADII, COLLISION_CONFIG, Weapon, GATEWAY_CONFIG, RESOURCE_TIERS, ResourceType, getEquipmentById, DROP_CONFIG, transformCircleToWorld, pointHitsCompoundShape, computeMiningTierMultiplier } from '@astra/shared';
import { ObjectPool } from '../core/ObjectPool';
import { tween, tweens } from '../core/TweenManager';
import { emitGameEvent, onGameEvent } from '../core/GameEvents';
import { assetLoader } from '../core/AssetLoader';
import { authService } from '../services/SupabaseClient';
import { audioManager } from '../audio/AudioManager';
import gsap from 'gsap';

// Local storage types for map display
interface StoredSystem {
  id: string;
  name: string;
  x: number;
  y: number;
  starType: string;
  sunRadius: number;
}

interface StoredPlanet {
  id: string;
  name: string;
  systemId: string;
  x: number;
  y: number;
  radius: number;
  planetType: string;
  orbitalDistance?: number;
  hasPlayerOutpost?: boolean;
}

interface StoredStation {
  id: string;
  name: string;
  x: number;
  y: number;
}

// Hit particle for pooling
interface HitParticle {
  sprite: Sprite;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}

// Selection entity type
interface SelectionEntity {
  type: 'player' | 'asteroid' | 'station' | 'drop' | 'gateway' | 'planet' | 'sun' | 'nebula' | 'drone' | 'settlement' | 'ice_asteroid' | 'gas_pocket' | 'wreckage' | 'empty';
  id?: string;
  name?: string;
  x: number;
  y: number;
  data?: Record<string, unknown>;
}

// Nearby entity for overview panel
interface NearbyEntity {
  id: string;
  type: 'player' | 'asteroid' | 'station' | 'drop' | 'gateway' | 'planet' | 'sun' | 'nebula' | 'drone' | 'settlement' | 'ice_asteroid' | 'gas_pocket' | 'wreckage';
  name: string;
  distance: number;
  x: number;
  y: number;
  data?: Record<string, unknown>;
  // Ship-specific fields (for players and bots)
  shipName?: string;
  shipClassType?: string;
  shipTier?: number;
  shipSize?: string;
  totalPower?: number;
  hull?: number;
  maxHull?: number;
  shield?: number;
  maxShield?: number;
}

export class GameScene extends Scene {
  // World container - holds all world-space objects, moved by camera
  private worldContainer!: Container;
  // Scenery container - environmental entities (stations, suns, nebulae, planets) with subtle parallax
  private sceneryContainer!: Container;

  // Player
  public player!: Player;
  private remotePlayers: Map<string, RemotePlayer> = new Map();

  // Asteroid AOI system: data (lightweight) vs sprites (rendered)
  private asteroidData: Map<string, AsteroidData> = new Map();
  private asteroids: Map<string, NetworkAsteroid> = new Map();
  private locallyDestroyedAsteroids: Set<string> = new Set();

  // V1 harvestable fields (ice / gas pockets / wreckage) — same AOI pattern as asteroids
  private iceAsteroidData: Map<string, HarvestableEntityData> = new Map();
  private iceAsteroids: Map<string, NetworkIceAsteroid> = new Map();
  private gasPocketData: Map<string, HarvestableEntityData> = new Map();
  private gasPockets: Map<string, NetworkGasPocket> = new Map();
  private wreckageData: Map<string, HarvestableEntityData> = new Map();
  private wreckages: Map<string, NetworkWreckage> = new Map();
  private locallyDestroyedHarvestables: Set<string> = new Set();

  // Other entities
  private drops: Map<string, Drop> = new Map();
  private drones: Map<string, Drone> = new Map();
  private planets: Map<string, Planet> = new Map();
  private planetData: Map<string, StoredPlanet> = new Map();
  private systems: Map<string, StoredSystem> = new Map();  // Solar systems for constellation map
  private stations: Map<string, Station> = new Map();
  private stationData: Map<string, StoredStation> = new Map();
  private privateStations: Map<string, Station> = new Map();
  private privateStationData: Map<string, StoredStation> = new Map();
  private settlements: Map<string, Settlement> = new Map();

  // Tutorial v2: in-world Aura beacons. Drawn as a teal proximity ring +
  // central transmitter dot in worldContainer. A per-frame distance check
  // emits `tutorialBeaconRange` events when the player crosses the radius.
  private tutorialBeacons: Map<string, { x: number; y: number; radius: number; name: string; ring: Graphics; dot: Graphics }> = new Map();
  private wasInTutorialBeacon: boolean = false;
  private gateways: Map<string, Gateway> = new Map();
  private gatewayData: Map<string, GatewayData> = new Map();
  private asteroidZones: Array<{ id: string; x: number; y: number; radius: number; name?: string }> = [];
  private resourceZones: Array<{ id: string; x: number; y: number; radius: number; kind: string }> = [];
  private suns: Map<string, Sun> = new Map();
  private nebulae: Map<string, Nebula> = new Map();
  private beacons: Map<string, InterdictionBeacon> = new Map();

  // Projectiles
  private projectiles: Projectile[] = [];
  private networkProjectiles: Projectile[] = []; // Projectiles from bots and remote players

  // Combat laser (for laser weapon type - continuous beam)
  private combatLaser: CombatLaser | null = null;
  private isLaserActive: boolean = false;
  private lastLaserDamageSend: number = 0;
  private readonly LASER_DAMAGE_SEND_INTERVAL = 100; // Send damage every 100ms
  private accumulatedLaserDamage: Map<string, number> = new Map(); // Accumulate damage between sends
  private lastLaserTargetId: string | null = null;
  private lastLaserTargetType: 'player' | 'drone' | 'settlement' | null = null;

  // Background systems
  private starfield!: Starfield;
  private depthLayers!: DepthLayerManager;
  private constellationBoundaryWarning!: ConstellationBoundaryWarning;

  // Station zone tracking
  private playerInSafeZone: boolean = false;
  private playerInDockingRange: boolean = false;
  private playerInNebula: boolean = false;
  private nearestDropId: string | null = null;
  private nearestDropOwnerOnly: boolean = false;
  private nearestDropAccessible: boolean = false;
  private nearestStation: Station | Settlement | null = null;

  // Gateway zone tracking
  private playerInGatewayRange: boolean = false;
  private nearestGateway: Gateway | null = null;

  // Planet zone tracking
  private playerInPlanetLandingRange: boolean = false;
  private nearestPlanet: Planet | null = null;
  private isPlayerManagingPlanet: boolean = false;
  private isPlanetEntryAnimation: boolean = false;

  // Docking state
  private isPlayerDocked: boolean = false;
  private isDockingAnimation: boolean = false;
  private dockingStationId: string | null = null;
  private pendingDockedState: { stationId: string } | null = null;

  // Mining state
  private isPlayerMining: boolean = false;
  private isMiningAnimation: boolean = false; // kept for harvestable compat
  private playerInMiningRange: boolean = false;
  private nearestMinableAsteroid: NetworkAsteroid | null = null;
  // Multi-target mining: one entry per active mining target
  private miningTargets: Map<string, { kind: string; laser: MiningLaser }> = new Map();
  // Legacy single-target aliases (kept for code that reads these)
  private get miningAsteroidId(): string | null { return this.miningTargets.size > 0 ? [...this.miningTargets.keys()][0] : null; }
  private get miningEntityKind(): string { return this.miningTargets.size > 0 ? [...this.miningTargets.values()][0].kind : 'asteroid'; }
  private miningRangeCircle: Graphics | null = null;     // Tool range indicator
  private miningRangeCircleTimer: number = 0;            // Fade timer for range circle
  private inspectRangeCircle: Graphics | null = null;    // Inspect scan range indicator
  private inspectRangeCircleTimer: number = 0;           // Fade timer for inspect range circle
  private scannedEntityIds: Set<string> = new Set();     // IDs discovered by radar scan
  private _inspectOpen: boolean = false;                  // Whether inspect panel is currently open
  private _inspectTargetId: string | null = null;         // ID of entity being inspected
  private scanOverlay!: Graphics;                        // Visual overlay for scanned entities
  private scanLabels: Map<string, PixiText> = new Map(); // "SCANNED" floating labels
  private scanAnimTimers: Map<string, number> = new Map(); // Timestamp when entity was first scanned (for 1s animation)
  private miningFractionalDisplay: number = 0;           // Accumulated fractional resources for floating text
  // Mining session tracking (resets after 60s of no mining)
  private miningSessionResources: Record<string, number> = {};  // resourceType → total units
  private miningSessionStartTime: number = 0;                   // Date.now() when session began
  private miningSessionResetTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly MINING_SESSION_TIMEOUT_MS = 60_000;
  // Tracks which remote players are mining which asteroids (playerId -> asteroidId)
  private remoteMinerAsteroids: Map<string, string> = new Map();
  // Mining laser beams for remote players (playerId -> MiningLaser)
  private remoteMiningLasers: Map<string, MiningLaser> = new Map();
  // Drone mining beams: ONE shared Graphics for all mining drones (no per-drone object, no
  // particles) — redrawn each frame from synced state. Super-light vs a MiningLaser each.
  private droneMiningBeams: Graphics = new Graphics();
  private droneBeamPulse: number = 0;
  // Warp animations for warping drones (droneId -> WarpVisual). Lazy: created only while a
  // drone is in a warp phase, destroyed when it leaves — so we don't add a Graphics per drone.
  private droneWarpVisuals: Map<string, WarpVisual> = new Map();

  // Hit particle pool
  private hitParticlePool!: ObjectPool<HitParticle>;
  private activeHitParticles: Set<HitParticle> = new Set();
  private hitParticleTextures: Map<string, Texture> = new Map();
  private readonly HIT_PARTICLE_POOL_SIZE = 100;

  // Floating text container (for damage numbers and reputation indicators)
  private floatingTextContainer!: Container;

  // Network state
  private connected: boolean = false;
  private initialLoadComplete: boolean = false;
  private lastSendTime: number = 0;
  private readonly SEND_RATE = 50; // 20 Hz
  // Dirty-check state for input sending (skip when nothing changed)
  private _lastSentX: number = 0;
  private _lastSentY: number = 0;
  private _lastSentRot: number = 0;
  private _lastSentVx: number = 0;
  private _lastSentVy: number = 0;
  private _lastSentThrusting: boolean = false;
  private _lastSentBoosting: boolean = false;

  // Adaptive interpolation delay update timer
  private _lastInterpolationUpdate: number = 0;
  private readonly INTERPOLATION_UPDATE_INTERVAL = 2000; // Update every 2s

  // Music state tracking for dynamic transitions
  private currentMusicTrack: string = 'space_calm';
  private lastThreatLevel: string = 'green';

  // AOI configuration
  private readonly AOI_RADIUS = 4000;
  private readonly AOI_HYSTERESIS = 500;
  private lastAOIUpdateTime: number = 0;
  private readonly AOI_UPDATE_INTERVAL = 200;

  // Debug mode
  private debugMode: boolean = false;
  private pendingDebugMode: boolean | null = null;
  private debugGridGraphics?: Graphics;
  private lastDebugLogTime: number = 0;
  private readonly DEBUG_LOG_INTERVAL = 5000;

  // Collision cooldown tracking
  private collisionCooldowns: Map<string, number> = new Map();
  private readonly COLLISION_COOLDOWN_MS = 500;

  // Selection system
  private selectedEntity: SelectionEntity | null = null;
  private selectionIndicator!: Graphics;

  // Waypoint system
  private waypointMarker: Container | null = null;
  private waypointGraphics: Graphics | null = null;
  private waypointText: PixiText | null = null;
  private currentWaypoint: { x: number; y: number; name: string } | null = null;

  // Approach target (separate from waypoint - used for approaching overview entities)
  private approachTarget: { x: number; y: number; name: string; entityRadius: number } | null = null;

  // Follow target — continuously tracks a moving entity by id+type.
  // State machine: chasing (move toward target), idle (within band), retreating (back away).
  private followTarget: {
    id: string;
    type: SelectionEntity['type'];
    name: string;
    entityRadius: number;     // cached target collision radius
    desiredCenterDistance: number; // distance from target CENTER where ship should sit
    mode: 'chasing' | 'idle' | 'retreating';
  } | null = null;
  private followClearance: number = 300; // user-set clearance from target edge (slider-controlled)
  private static readonly FOLLOW_CLEARANCE_MIN = 50;
  private static readonly FOLLOW_CLEARANCE_MAX = 1500;
  private static readonly FOLLOW_CHASE_HYSTERESIS = 200;  // re-chase when target this far past desired distance
  private static readonly FOLLOW_RETREAT_HYSTERESIS = 100; // retreat when target this far inside desired distance
  private static readonly FOLLOW_RETREAT_BUFFER = 50;     // overshoot retreat point so we stop near desired distance

  // Event listener cleanup tracking
  private wheelHandler: ((event: WheelEvent) => void) | null = null;
  private boundEventListeners: Array<{ event: string; callback: (...args: unknown[]) => void }> = [];

  constructor(key: string = 'GameScene') {
    super(key);
  }

  async create(): Promise<void> {
    const width = this.app.screen.width;
    const height = this.app.screen.height;

    // Get background textures
    const bgLayer1 = assetLoader.getTexture('bg_layer_1');
    const bgLayer2 = assetLoader.getTexture('bg_layer_2');
    const bgLayer3 = assetLoader.getTexture('bg_layer_3');

    // Create world container (camera moves this)
    this.worldContainer = new Container();
    this.worldContainer.sortableChildren = true;

    // Create depth layer manager (background layers, parallax)
    // Must be created before adding worldContainer so bgContainer is added first
    this.depthLayers = new DepthLayerManager(this.worldContainer, width, height, bgLayer1, bgLayer2, bgLayer3);

    // Add background container first (screen-fixed, parallax via tilePosition)
    const bgContainer = this.depthLayers.getBgContainer();
    this.addChild(bgContainer);

    // Create starfield background (also in screen space with parallax)
    this.starfield = new Starfield(width, height);
    this.starfield.zIndex = 10; // Above nebula but below world objects
    bgContainer.addChild(this.starfield);

    // Add scenery container (stations, suns, nebulae, planets — behind gameplay, subtle parallax)
    this.sceneryContainer = new Container();
    this.sceneryContainer.sortableChildren = true;
    this.sceneryContainer.alpha = 0.75;
    this.addChild(this.sceneryContainer);

    // Add world container (camera-controlled)
    this.addChild(this.worldContainer);

    // Set camera to control world container + scenery container
    this.camera.setWorldContainer(this.worldContainer);
    this.camera.setSceneryContainer(this.sceneryContainer);

    // Initialize hit particle pool
    this.initHitParticlePool();

    // Create constellation boundary warning system
    const constellationCoords = networkManager.getConstellationCoords();
    // Constellation size is unknown until state arrives — bootstrap with default,
    // re-call setConstellation() once the room state is populated (see below).
    this.constellationBoundaryWarning = new ConstellationBoundaryWarning(constellationCoords.x, constellationCoords.y);
    this.constellationBoundaryWarning.zIndex = 5;
    this.worldContainer.addChild(this.constellationBoundaryWarning);

    // Create player hidden — real position comes from server via onLocalPlayerLoaded
    const spawnPoint = this.findUnoccupiedSpawnPoint();
    this.player = new Player(spawnPoint.x, spawnPoint.y);
    this.player.init(this.input, this.camera);
    this.player.zIndex = 100;
    this.player.visible = false;
    this.player.inputEnabled = false;
    this.worldContainer.addChild(this.player);

    // Add player's world-space graphics (brake lines, shield arcs, visibility circles, warp trail, radar waves)
    const worldGfx = this.player.getWorldSpaceGraphics();
    this.worldContainer.addChild(worldGfx.brake);
    this.worldContainer.addChild(worldGfx.shield);
    this.worldContainer.addChild(worldGfx.visibility);
    this.worldContainer.addChild(worldGfx.warpTrail);
    this.worldContainer.addChild(worldGfx.radarWaves);
    this.worldContainer.addChild(worldGfx.disruptor);
    this.worldContainer.addChild(worldGfx.overhaulVfx);

    // Set player's constellation bounds
    this.player.setConstellationBounds(constellationCoords.x, constellationCoords.y);

    // Set up camera to follow player
    this.camera.startFollow(this.player, true, GAME_CONFIG.CAMERA.LERP, GAME_CONFIG.CAMERA.LERP);

    // Restore saved zoom level
    const savedZoom = localStorage.getItem('astra_camera_zoom');
    const initialZoom = savedZoom ? parseFloat(savedZoom) : GAME_CONFIG.CAMERA.ZOOM_DEFAULT;
    this.camera.setZoom(Math.max(GAME_CONFIG.CAMERA.ZOOM_MIN, Math.min(initialZoom, GAME_CONFIG.CAMERA.ZOOM_MAX)));
    this.camera.setZoomLimits(GAME_CONFIG.CAMERA.ZOOM_MIN, GAME_CONFIG.CAMERA.ZOOM_MAX);

    // Setup zoom controls
    this.setupZoomControls();

    // Create selection indicator
    this.selectionIndicator = new Graphics();
    this.selectionIndicator.zIndex = 1000;
    this.worldContainer.addChild(this.selectionIndicator);

    // Create scan overlay graphics (drawn over scanned entities)
    this.scanOverlay = new Graphics();
    this.scanOverlay.zIndex = 999;
    this.worldContainer.addChild(this.scanOverlay);

    // Create floating text container (for damage numbers and reputation indicators)
    this.floatingTextContainer = new Container();
    this.floatingTextContainer.zIndex = 2000; // Above everything else
    this.worldContainer.addChild(this.floatingTextContainer);

    // Setup game event listeners
    this.setupGameEventListeners();

    // Setup React game event listeners (from GameEvents.ts)
    this.setupReactEventListeners();

    // Load saved waypoint from localStorage
    this.loadWaypoint();

    // Setup network callbacks
    this.setupNetworkCallbacks();

    // Connect to the constellation room (planets are now managed via in-flight panel, no
    // separate planet room or reconnection state).
    {
      const playerConstellation = authService.getPlayerConstellation();
      const constellationX = playerConstellation?.x ?? 0;
      const constellationY = playerConstellation?.y ?? 0;
      this.connected = await networkManager.connect(constellationX, constellationY);

      if (!this.connected) {
        console.error('Failed to connect to server');
        emitGameEvent('notification', {
          message: 'Failed to connect to server',
          type: 'error',
        });
      } else {
        // Room joined — now waiting for server to send player state
        emitGameEvent('loadingProgress', { progress: 0.95, message: 'Loading pilot data...' });
        // Re-apply constellation bounds now that we know the per-constellation physical size
        // (state.constellationSize is server-authoritative; the tutorial constellation overrides
        // the global default to make the play area genuinely small).
        const realConstellationSize = networkManager.getConstellationSize();
        this.player.setConstellationBounds(constellationX, constellationY, realConstellationSize);
        this.constellationBoundaryWarning?.setConstellation(constellationX, constellationY, realConstellationSize);
      }

      // Initialize audio system (music starts when player data arrives)
      audioManager.init();
    }
  }

  private initHitParticlePool(): void {
    // Generate particle textures
    const colors: Record<string, number> = {
      laser: 0x00ffff,
      kinetic: 0xffaa00,
      missile: 0xff4400,
    };

    for (const [type, color] of Object.entries(colors)) {
      const gfx = new Graphics();
      gfx.circle(2, 2, 2);
      gfx.fill({ color, alpha: 1 });
      const texture = this.app.renderer.generateTexture(gfx);
      this.hitParticleTextures.set(`hit-particle-${type}`, texture);
      gfx.destroy();
    }

    // Create particle pool
    this.hitParticlePool = new ObjectPool<HitParticle>(
      () => {
        const sprite = new Sprite(this.hitParticleTextures.get('hit-particle-laser'));
        sprite.visible = false;
        sprite.zIndex = 5;
        this.worldContainer.addChild(sprite);
        return { sprite, vx: 0, vy: 0, life: 0, maxLife: 0 };
      },
      (particle) => {
        particle.sprite.visible = false;
        particle.sprite.alpha = 1;
        particle.vx = 0;
        particle.vy = 0;
        particle.life = 0;
        particle.maxLife = 0;
      },
      this.HIT_PARTICLE_POOL_SIZE,
      this.HIT_PARTICLE_POOL_SIZE
    );
  }

  private setupZoomControls(): void {
    // Mouse wheel zoom using fixed zoom levels
    this.wheelHandler = (event: WheelEvent) => {
      if (!this.isActive) return;
      event.preventDefault();

      const zoomLevels = this.getAvailableZoomLevels();
      const currentZoom = this.camera.zoom;

      // Find current level index (closest match)
      let currentIndex = 0;
      let minDiff = Math.abs(zoomLevels[0] - currentZoom);
      for (let i = 1; i < zoomLevels.length; i++) {
        const diff = Math.abs(zoomLevels[i] - currentZoom);
        if (diff < minDiff) {
          minDiff = diff;
          currentIndex = i;
        }
      }

      // Zoom in (scroll up) or out (scroll down)
      let newIndex: number;
      if (event.deltaY > 0) {
        // Scroll down = zoom out (clamp at minimum level)
        newIndex = Math.max(0, currentIndex - 1);
      } else {
        // Scroll up = zoom in
        newIndex = Math.min(currentIndex + 1, zoomLevels.length - 1);
      }

      const newZoom = zoomLevels[newIndex];
      this.camera.setZoom(newZoom);
      this.saveZoomLevel(newZoom);
    };
    this.app.canvas.addEventListener('wheel', this.wheelHandler);
  }

  private saveZoomLevel(zoom: number): void {
    localStorage.setItem('astra_camera_zoom', zoom.toString());
  }

  /** Update camera zoom limits based on current ship size */
  private updateZoomLimitsForShip(): void {
    const shipSize = this.player.shipClass?.size || 'medium';
    const minZoom = GAME_CONFIG.CAMERA.ZOOM_MIN_BY_SHIP_SIZE[shipSize] ?? GAME_CONFIG.CAMERA.ZOOM_MIN;
    this.camera.setZoomLimits(minZoom, GAME_CONFIG.CAMERA.ZOOM_MAX);
  }

  /** Get zoom levels available for the current ship */
  private getAvailableZoomLevels(): readonly number[] {
    const shipSize = this.player.shipClass?.size || 'medium';
    const minZoom = GAME_CONFIG.CAMERA.ZOOM_MIN_BY_SHIP_SIZE[shipSize] ?? GAME_CONFIG.CAMERA.ZOOM_MIN;
    return GAME_CONFIG.CAMERA.ZOOM_LEVELS.filter(z => z >= minZoom);
  }

  /**
   * Zoom to next/previous level in the fixed zoom levels array
   * @param direction 1 for zoom in, -1 for zoom out
   */
  private zoomToNextLevel(direction: number): void {
    const zoomLevels = this.getAvailableZoomLevels();
    const currentZoom = this.camera.zoom;

    // Find current level index (closest match)
    let currentIndex = 0;
    let minDiff = Math.abs(zoomLevels[0] - currentZoom);
    for (let i = 1; i < zoomLevels.length; i++) {
      const diff = Math.abs(zoomLevels[i] - currentZoom);
      if (diff < minDiff) {
        minDiff = diff;
        currentIndex = i;
      }
    }

    const newIndex = currentIndex + direction;

    // Check bounds (clamp, no map transitions)
    if (newIndex < 0 || newIndex >= zoomLevels.length) {
      // Already at max zoom
      return;
    }

    const newZoom = zoomLevels[newIndex];
    this.camera.setZoom(newZoom);
    this.saveZoomLevel(newZoom);
  }

  private transitionToConstellationMap(): void {
    // (tutorial v2 removed the map trigger — Continue is manual)

    const playerPosition = { x: this.player.x, y: this.player.y };
    const playerRotation = this.player.angle;

    localStorage.setItem('astra_camera_zoom', this.camera.zoom.toString());

    const outpostSet = networkManager.outpostPlanetIds;
    const planets = Array.from(this.planetData.values()).map(p => ({
      ...p,
      hasPlayerOutpost: outpostSet.has(p.id),
    }));
    const stations = Array.from(this.stationData.values());
    const asteroidZones = this.asteroidZones || [];
    const constellationName = networkManager.getConstellationName() || 'Unknown Constellation';

    // Build gateway data for the map
    const gateways = Array.from(this.gatewayData.values()).map(g => ({
      id: g.id,
      name: `Gate to ${g.targetConstellationName}`,
      x: g.x,
      y: g.y,
      targetConstellationName: g.targetConstellationName,
    }));

    // Build systems data for the map (solar systems shown as single icons)
    const systems = Array.from(this.systems.values());

    // Build private stations data for the map
    const room: any = (networkManager as any).room;
    const sid = networkManager.getLocalSessionId();
    const me = sid ? room?.state?.players?.get?.(sid) : null;
    const myPlayerId: string | undefined = me?.id;
    const privateStationsList = Array.from(this.privateStationData.values()).map(ps => ({
      id: ps.id,
      name: ps.name,
      x: ps.x,
      y: ps.y,
      isOwned: !!myPlayerId && this.privateStations.get(ps.id) !== undefined &&
        room?.state?.privateStations?.get?.(ps.id)?.ownerPlayerId === myPlayerId,
    }));

    // V1 harvestables — collect what client knows (AOI-filtered)
    const harvestables: Array<{ id: string; kind: 'ice_asteroid' | 'gas_pocket' | 'wreckage'; x: number; y: number; resourceType: string }> = [];
    for (const [id, d] of this.iceAsteroidData) harvestables.push({ id, kind: 'ice_asteroid', x: d.x, y: d.y, resourceType: d.resourceType });
    for (const [id, d] of this.gasPocketData)   harvestables.push({ id, kind: 'gas_pocket',   x: d.x, y: d.y, resourceType: d.resourceType });
    for (const [id, d] of this.wreckageData)    harvestables.push({ id, kind: 'wreckage',     x: d.x, y: d.y, resourceType: d.resourceType });

    // Collect nebulae as resource zones (gas pocket parent zones)
    const allResourceZones = [...(this.resourceZones || [])];
    for (const [id, nebula] of this.nebulae) {
      allResourceZones.push({
        id,
        x: nebula.x,
        y: nebula.y,
        radius: (nebula as any).nebulaRadius || 5000,
        kind: 'nebula',
      });
    }

    // Collect player-owned drones (ship drones + private station drones)
    const myShipId: string | undefined = me?.shipId;
    const myPrivateStationIds = new Set(
      privateStationsList.filter(ps => ps.isOwned).map(ps => ps.id)
    );
    const ownedDrones: Array<{ id: string; droneType: string; x: number; y: number; state: string; level: number }> = [];
    for (const [, drone] of this.drones) {
      const isShipDrone = drone.ownerType === 'ship' && myShipId && drone.ownerId === myShipId;
      const isPrivateStationDrone = drone.ownerType === 'private_station' && myPrivateStationIds.has(drone.ownerId);
      if (isShipDrone || isPrivateStationDrone) {
        ownedDrones.push({
          id: drone.id,
          droneType: drone.droneType,
          x: drone.x,
          y: drone.y,
          state: drone.droneState,
          level: drone.level,
        });
      }
    }

    // Tutorial v2: also surface Aura beacons on the constellation map so the player
    // can find their way back after roaming for missions.
    const tutorialBeacons = Array.from(this.tutorialBeacons.entries()).map(([id, b]) => ({
      id, name: b.name, x: b.x, y: b.y, radius: b.radius,
    }));

    // Emit event to React to open constellation map overlay
    emitGameEvent('openConstellationMap', {
      constellationName,
      constellationSize: networkManager.getConstellationSize(),
      playerPosition,
      playerRotation,
      planets,
      stations,
      asteroidZones,
      gateways,
      systems,
      privateStations: privateStationsList,
      waypoint: this.currentWaypoint,
      harvestables,
      resourceZones: allResourceZones,
      ownedDrones,
      tutorialBeacons,
    });
  }

  private setupGameEventListeners(): void {
    const events = this.game.events;

    // Helper to register and track local EventEmitter listeners for cleanup
    const trackOn = (event: string, handler: (...args: unknown[]) => void) => {
      events.on(event, handler);
      this.boundEventListeners.push({ event, callback: handler });
    };

    // Local EventEmitter events (from UI components, etc.)
    trackOn('setDebugMode', this.setDebugMode.bind(this) as (args: unknown) => void);
    trackOn('requestPlayerData', this.sendPlayerData.bind(this) as (args: unknown) => void);
    trackOn('setPlayerInput', this.setPlayerInput.bind(this) as (args: unknown) => void);
    trackOn('setShipColor', this.setShipColor.bind(this) as (args: unknown) => void);
    trackOn('requestRespawn', this.respawnPlayer.bind(this) as (args: unknown) => void);
    trackOn('requestDock', this.handleDockRequest.bind(this) as (args: unknown) => void);
    trackOn('requestUndock', this.handleUndockRequest.bind(this) as (args: unknown) => void);
    trackOn('requestStartMining', this.handleMiningRequest.bind(this) as (args: unknown) => void);
    trackOn('requestStopMining', this.handleStopMiningRequest.bind(this) as (args: unknown) => void);
    trackOn('equipmentChanged', this.handleEquipmentChanged.bind(this) as (args: unknown) => void);
    trackOn('waypointUpdate', this.updateWaypointMarker.bind(this) as (args: unknown) => void);
    trackOn('selectEntityById', this.handleSelectEntityById.bind(this) as (args: unknown) => void);
    trackOn('requestFastTravel', this.handleFastTravelRequest.bind(this) as (args: unknown) => void);
    trackOn('requestStopFastTravel', this.handleStopFastTravelRequest.bind(this) as (args: unknown) => void);

    // Global mitt events (from Player.ts - unified event system)
    this.reactEventUnsubscribers.push(
      onGameEvent('fireWeapon', (data) => this.handleFireWeapon(data))
    );
    this.reactEventUnsubscribers.push(
      onGameEvent('startLaserFire', (data) => this.handleStartLaserFire(data))
    );
    this.reactEventUnsubscribers.push(
      onGameEvent('stopLaserFire', () => this.handleStopLaserFire())
    );
    this.reactEventUnsubscribers.push(
      onGameEvent('selectionClick', (data) => this.handleSelectionClick(data))
    );
    this.reactEventUnsubscribers.push(
      onGameEvent('playerDamage', (data) => this.handlePlayerDamageForAutopilot(data))
    );
    this.reactEventUnsubscribers.push(
      onGameEvent('radarScanRequest', (data) => this.handleRadarScanRequest(data))
    );
  }

  // React game events (from GameEvents.ts mitt emitter)
  private reactEventUnsubscribers: Array<() => void> = [];

  private setupReactEventListeners(): void {
    // Handle waypoint set from constellation map (does NOT auto-start approach)
    this.reactEventUnsubscribers.push(
      onGameEvent('setWaypoint', (data) => {
        // If currently approaching, stop it first (old waypoint is being replaced)
        if (this.player?.isAutopiloting) {
          this.player.stopAutopilot('manual');
        }

        this.currentWaypoint = {
          x: data.x,
          y: data.y,
          name: data.name || 'Waypoint',
        };
        // Save to localStorage for persistence
        localStorage.setItem('astra_waypoint', JSON.stringify(this.currentWaypoint));

        // Start warp preparation immediately on waypoint set
        this.player?.setWarpWaypoint(data.x, data.y);

        // Immediately notify React of the new waypoint (don't wait for update() tick)
        // This ensures HUD shows correct waypoint immediately
        if (this.player) {
          const playerPos = this.player.getCoordinates();
          const dx = data.x - playerPos.x;
          const dy = data.y - playerPos.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const angle = Math.atan2(dy, dx);
          emitGameEvent('waypointUpdate', {
            x: data.x,
            y: data.y,
            name: data.name || 'Waypoint',
            distance,
            angle,
            isOnScreen: false, // Will be recalculated in update()
          });
        }
      })
    );

    // Combat-overhaul: cycle active weapon on Tab
    this.reactEventUnsubscribers.push(
      onGameEvent('cycleActiveWeapon', () => {
        if (this.player) this.player.cycleWeapon();
      })
    );

    // Handle clear waypoint
    this.reactEventUnsubscribers.push(
      onGameEvent('clearWaypoint', () => {
        this.currentWaypoint = null;
        this.updateWaypointMarker(null);
        localStorage.removeItem('astra_waypoint');
        // Also stop approach if active
        this.handleStopFastTravelRequest();
        // Clear warp preparation
        this.player?.clearWarpWaypoint();
        // Notify React
        emitGameEvent('waypointUpdate', null);
      })
    );

    // Handle start approach (F key pressed or APPROACH button clicked)
    // Priority: waypoint first, then selected entity (stations, asteroids, etc.)
    // Does NOT create waypoints - approaches directly to target
    this.reactEventUnsubscribers.push(
      onGameEvent('startFastTravel', () => {
        if (this.currentWaypoint) {
          // Approach the user-created waypoint (no entity radius - it's a point)
          this.approachTarget = {
            x: this.currentWaypoint.x,
            y: this.currentWaypoint.y,
            name: this.currentWaypoint.name,
            entityRadius: 0,
          };
          this.handleFastTravelRequest({
            targetX: this.currentWaypoint.x,
            targetY: this.currentWaypoint.y,
            entityRadius: 0,
          });
        } else if (this.selectedEntity && this.selectedEntity.type !== 'empty') {
          // Get the actual collision radius of the target entity
          const entityRadius = this.getEntityCollisionRadius(this.selectedEntity);
          // Approach the selected entity's current position (no waypoint created)
          this.approachTarget = {
            x: this.selectedEntity.x,
            y: this.selectedEntity.y,
            name: this.selectedEntity.name || this.selectedEntity.type,
            entityRadius,
          };
          this.handleFastTravelRequest({
            targetX: this.selectedEntity.x,
            targetY: this.selectedEntity.y,
            entityRadius,
          });
        }
      })
    );

    // Handle start approach to specific target (from Overview panel clicking on a specific entity)
    this.reactEventUnsubscribers.push(
      onGameEvent('startFastTravelToTarget', (data) => {
        // Look up entity radius from nearby entities
        const entityRadius = this.getEntityCollisionRadiusAt(data.x, data.y);
        this.approachTarget = {
          x: data.x,
          y: data.y,
          name: data.name,
          entityRadius,
        };
        this.handleFastTravelRequest({
          targetX: data.x,
          targetY: data.y,
          entityRadius,
        });
      })
    );

    // Handle stop approach (F key pressed again)
    this.reactEventUnsubscribers.push(
      onGameEvent('stopFastTravel', () => {
        this.handleStopFastTravelRequest();
      })
    );

    // Handle start follow — autopilot that continuously re-targets a moving entity
    this.reactEventUnsubscribers.push(
      onGameEvent('startFollow', (data) => {
        this.handleStartFollowRequest(data);
      })
    );

    // Handle stop follow
    this.reactEventUnsubscribers.push(
      onGameEvent('stopFollow', () => {
        this.handleStopFollowRequest();
      })
    );

    // Handle toggle follow from X key (Player.handleFollowKey emits this).
    // If already following → stop. Else if there's a valid selection → start.
    this.reactEventUnsubscribers.push(
      onGameEvent('toggleFollow', () => {
        if (this.followTarget) {
          this.handleStopFollowRequest();
          return;
        }
        const sel = this.selectedEntity;
        if (!sel || sel.type === 'empty' || !sel.id) {
          emitGameEvent('notification', { type: 'warning', message: 'Select a target first' });
          return;
        }
        this.handleStartFollowRequest({ id: sel.id, type: sel.type as never, name: sel.name || sel.type });
      })
    );

    // Handle follow distance slider updates from React
    this.reactEventUnsubscribers.push(
      onGameEvent('setFollowDistance', (data) => {
        this.handleSetFollowDistance(data);
      })
    );

    // If autopilot stops for a reason other than 'arrival', clear follow state too.
    // 'combat' = took damage, 'manual' = player thrust input — both should cancel follow.
    this.reactEventUnsubscribers.push(
      onGameEvent('fastTravelStopped', (data) => {
        if (this.followTarget && data.reason !== 'arrival') {
          this.followTarget = null;
          emitGameEvent('followUpdate', { isActive: false });
        }
      })
    );

    // Handle M key map request from React — emit fresh constellation map data
    this.reactEventUnsubscribers.push(
      onGameEvent('requestConstellationMapData', () => {
        this.transitionToConstellationMap();
      })
    );

    // Handle undock from React
    this.reactEventUnsubscribers.push(
      onGameEvent('undock', () => {
        if (this.isPlayerDocked && this.dockingStationId) {
          this.handleUndockRequest({ stationId: this.dockingStationId });
        }
      })
    );

    // Ship is already hidden by the disappear animation that played before
    // openPlanetPanel was emitted. Just set the managing flag.
    this.reactEventUnsubscribers.push(
      onGameEvent('planetPanelSnapshot', () => {
        this.isPlayerManagingPlanet = true;
        this.player.inputEnabled = false;
        this.player.velocity.x = 0;
        this.player.velocity.y = 0;
      })
    );

    // Show local ship when planet panel closes (optimistic from React).
    // Reset velocity so the player doesn't drift with stale pre-panel speed.
    this.reactEventUnsubscribers.push(
      onGameEvent('closePlanetPanel', () => {
        this.isPlayerManagingPlanet = false;
        this.player.velocity.x = 0;
        this.player.velocity.y = 0;
        this.player.inputEnabled = true;

        // Fade-in the ship
        this.playShipAppearAnimation();
      })
    );

    // Handle respawn request from React
    this.reactEventUnsubscribers.push(
      onGameEvent('requestRespawn', () => {
        this.handleRespawnRequest();
      })
    );

    // Handle select entity from React (Overview panel click)
    this.reactEventUnsubscribers.push(
      onGameEvent('selectEntity', (data) => {
        this.handleSelectEntityById({ id: data.id, type: data.type });
      })
    );

    // Handle warp engage request from React (ENGAGE WARP button clicked)
    this.reactEventUnsubscribers.push(
      onGameEvent('warpEngage', () => {
        if (this.player && this.player.warpPhase === 'ready') {
          const target = this.player.getWarpTarget();
          if (target) {
            networkManager.sendWarpEngage(target.x, target.y);
          }
        }
      })
    );

    // Handle warp cancel request from React (CANCEL or DISENGAGE button clicked)
    this.reactEventUnsubscribers.push(
      onGameEvent('warpCancel', () => {
        if (this.player) {
          networkManager.sendWarpCancel();
          this.player.cancelWarpReadiness();
        }
      })
    );

    // Handle mining start request from React HUD (Mine button) — unified for all entity types
    this.reactEventUnsubscribers.push(
      onGameEvent('requestStartMining', (data) => {
        if (!this.connected || !this.player) return;
        if (!data || !data.asteroidId) return;
        const kind = data.kind || 'asteroid';

        // Look up entity position and radius from the correct map
        let entityX: number | undefined, entityY: number | undefined, entityRadius = 80;
        if (kind === 'asteroid') {
          const a = this.asteroids.get(data.asteroidId);
          if (!a) return;
          entityX = a.x; entityY = a.y; entityRadius = a.getRadius();
        } else {
          // ice_asteroid, gas_pocket, wreckage
          const e = this.getHarvestableEntity(kind as any, data.asteroidId);
          if (!e) return;
          entityX = e.x; entityY = e.y; entityRadius = e.getRadius?.() || 80;
        }
        if (entityX === undefined || entityY === undefined) return;

        // Client-side range check — show range circle if too far
        const toolRange = this.getEquippedMiningToolRange();
        if (toolRange <= 0) {
          emitGameEvent('notification', { type: 'warning', message: 'No mining tool equipped', duration: 2000 });
          return;
        }
        const dx = this.player.x - entityX;
        const dy = this.player.y - entityY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > toolRange + entityRadius) {
          this.showMiningRangeCircle(toolRange);
          emitGameEvent('notification', { type: 'warning', message: 'Out of mining range', duration: 1500 });
          return;
        }
        networkManager.sendStartMining(data.asteroidId, kind);
      })
    );

    // Track scanned entity IDs for inspect prerequisite
    this.reactEventUnsubscribers.push(
      onGameEvent('radarEntitiesDiscovered', (data) => {
        const now = performance.now();
        for (const e of data.entities) {
          if (e.id && !this.scannedEntityIds.has(e.id)) {
            this.scannedEntityIds.add(e.id);
            this.scanAnimTimers.set(e.id, now);
          }
        }
      })
    );
    this.reactEventUnsubscribers.push(
      onGameEvent('radarClearEntities', () => {
        this.scannedEntityIds.clear();
        this.scanAnimTimers.clear();
      })
    );

    // Track inspect panel open/close state
    this.reactEventUnsubscribers.push(
      onGameEvent('inspectResult', (data) => {
        this._inspectOpen = !!data.success;
        this._inspectTargetId = data.success ? (data.targetId ?? null) : null;
      })
    );
    this.reactEventUnsubscribers.push(
      onGameEvent('inspectClose', () => {
        this._inspectOpen = false;
        this._inspectTargetId = null;
      })
    );
    this.reactEventUnsubscribers.push(
      onGameEvent('selectionChanged', () => {
        this._inspectOpen = false;
        this._inspectTargetId = null;
      })
    );

    // Handle mining stop request from React HUD (Stop button or G key).
    // Optional asteroidId stops just that target; otherwise stops all.
    this.reactEventUnsubscribers.push(
      onGameEvent('requestStopMining', (data) => {
        if (!this.connected || !this.isPlayerMining) return;
        networkManager.sendStopMining(data?.asteroidId);
      })
    );

    // Handle utility hotbar activation (from React clicks or Player.ts key presses)
    this.reactEventUnsubscribers.push(
      onGameEvent('activateUtilitySlot', (slotIndex) => {
        if (!this.player) return;
        // Positive values (0-4) from hotbar clicks — route through Player for type-specific handling
        if (slotIndex >= 0) {
          this.player.activateUtilitySlot(slotIndex);
          return;
        }
        // Special value -1 signals beacon deployment
        if (slotIndex === -1) {
          networkManager.sendDeployBeacon(this.player.x, this.player.y);
        }
        // Special value -2 signals warp disruptor activation
        if (slotIndex === -2) {
          networkManager.sendActivateDisruptor();
        }
        // Special value -3 signals cargo scan
        if (slotIndex === -3) {
          const target = this.player.getSelectedTarget();
          if (target && target.type === 'player') {
            networkManager.sendScanCargo(target.id);
          }
        }
        // Special value -4 signals drone bay toggle (deploy/recall all)
        if (slotIndex === -4) {
          networkManager.sendToggleShipDrones();
        }
      })
    );

    // Handle warp disruptor activation confirmation from server
    this.reactEventUnsubscribers.push(
      onGameEvent('disruptorActivated', (data) => {
        if (this.player) {
          this.player.startDisruptorEffect(data.duration, data.range);
        }
      })
    );

    // Handle reputation change (from killing players) - show floating indicator
    this.reactEventUnsubscribers.push(
      onGameEvent('reputationChange', (data) => {
        if (data.victimX !== undefined && data.victimY !== undefined && data.change !== 0) {
          this.createFloatingReputation(data.victimX, data.victimY, data.change);
        }
      })
    );
  }

  private setupNetworkCallbacks(): void {
    // Local player loaded from server
    networkManager.onLocalPlayerLoaded = (data: RemotePlayerData) => {
      // Set ship class from database (this must happen first to get correct stats)
      if (data.shipClassId) {
        this.player.setShipClass(data.shipClassId);
        this.updateZoomLimitsForShip();
      }

      // Set initial position from database
      this.player.x = data.x;
      this.player.y = data.y;
      this.player.angle = (data.rotation * 180) / Math.PI;
      this.player.velocity.x = 0;
      this.player.velocity.y = 0;
      // Initialize server position for Ghost Object Pattern (prevents initial lerp)
      this.player.initializeServerPosition(data.x, data.y, 0, 0);

      // Defer safe spawn check to allow asteroid data to load first
      // This is needed because Colyseus fires onAdd callbacks in schema order,
      // and asteroids may not be loaded yet when player loads
      if (!data.isDocked) {
        setTimeout(() => {
          const safePos = this.findSafeSpawnPoint(this.player.x, this.player.y);
          if (safePos.x !== this.player.x || safePos.y !== this.player.y) {
            this.player.x = safePos.x;
            this.player.y = safePos.y;
            // Re-initialize server position after adjustment
            this.player.initializeServerPosition(safePos.x, safePos.y, 0, 0);
          }
        }, 100); // Short delay to ensure asteroids are loaded
      }

      if (data.slots) {
        this.player.slots = data.slots;
        this.player.recalculateStats();
      }

      if (data.isDocked && data.dockedStationId) {
        this.isPlayerDocked = true;
        this.dockingStationId = data.dockedStationId;
        this.player.visible = false;
        this.player.inputEnabled = false;

        const stationDataEntry = this.stationData.get(data.dockedStationId);
        if (stationDataEntry) {
          // Notify React to show station UI
          emitGameEvent('dockingStateChange', {
            isDocked: true,
            stationId: data.dockedStationId,
            stationName: stationDataEntry.name,
          });
        } else {
          // Station data not loaded yet - wait for it
          this.pendingDockedState = { stationId: data.dockedStationId };
        }
      } else {
        // Not docked — reveal player and enable input
        this.player.visible = true;
        this.player.inputEnabled = true;
      }

      // On initial load, dismiss loading screen and start music
      if (!this.initialLoadComplete) {
        this.initialLoadComplete = true;
        emitGameEvent('loadingProgress', { progress: 1.0, message: 'Starting game...' });
        audioManager.playMusic('space_calm');
        emitGameEvent('gameReady');
      }
    };

    networkManager.onLocalPlayerStateChange = (data) => {
      this.player.hull = data.hull;
      this.player.maxHull = data.maxHull;
      this.player.shield = data.shield;
      this.player.maxShield = data.maxShield;
      // Combat-overhaul: cache ship-level heat & shield for HUD
      this.player.shipHeat = data.currentHeat;
      this.player.shipMaxHeat = data.maxHeat;
      const wasOverheated = this.player.isShipOverheated;
      this.player.isShipOverheated = data.isShipOverheated;
      if (data.isShipOverheated && !wasOverheated) {
        audioManager.playSFX('overload');
      }
      // Detect shield raise/break transitions
      const wasActive = (this.player as any)._shieldActive as boolean | undefined;
      (this.player as any)._shieldActive = data.shieldActive;
      if (data.shieldActive && !wasActive) {
        (this.player as any)._shieldRaisedAt = Date.now();
      }
      if (wasActive && !data.shieldActive && data.isShipOverheated) {
        // Shield broke from overload — shatter effect (not on manual lower)
        this.player.spawnShieldShatter();
      }
      (this.player as any)._powerCapacity = data.powerCapacity;
      (this.player as any)._shieldRadius = data.shieldRadius;
      (this.player as any)._shieldExpansionMs = data.shieldExpansionMs;
      // Hard flux + voluntary vent state. Mirror to the player so local input
      // gates (fire weapon, raise shield) match the server's vent lock, and
      // stop any active beam visual the moment a vent begins.
      const wasVenting = this.player.isVenting;
      this.player.isVenting = data.isVenting;
      if (data.isVenting && !wasVenting && this.isLaserActive) {
        this.player.stopLaserFire();
      }
      (this.player as any)._absorbedHeat = data.absorbedHeat;

      // Input sequencing: track which inputs server has acknowledged (Phase 1 networking)
      this.player.updateLastAckedInput(data.lastProcessedInput);

      // Sync stun state from server - but don't let server clear stun if client is still stunned locally
      // This prevents race conditions where client applies stun immediately but server hasn't caught up
      if (data.isStunned) {
        // Server says stunned - use the later end time between client and server
        if (data.stunEndTime > this.player.stunEndTime) {
          this.player.isStunned = true;
          this.player.stunEndTime = data.stunEndTime;
        }
      } else if (!this.player.isStunned || Date.now() >= this.player.stunEndTime) {
        // Server says not stunned AND client's local stun already expired - clear it
        this.player.isStunned = false;
        this.player.stunEndTime = 0;
      }
      // Otherwise: keep local stun (client detected collision first, server hasn't caught up)

      this.game.events.emit('playerDamage', {
        hull: data.hull,
        maxHull: data.maxHull,
        shield: data.shield,
        maxShield: data.maxShield,
      });

      if (data.hull <= 0 && !this.player.isDead()) {
        this.player.explode();
      }
    };

    // Track cloak state changes
    networkManager.onLocalPlayerCloakChange = (data) => {
      const now = Date.now();
      const remaining = data.isCloaked ? Math.max(0, data.cloakExpiresAt - now) : 0;
      emitGameEvent('cloakStateUpdate', {
        isCloaked: data.isCloaked,
        expiresAt: data.cloakExpiresAt,
        remaining,
      });
    };

    // Track combat state changes
    networkManager.onLocalPlayerCombatChange = (data) => {
      const now = Date.now();
      const COMBAT_TIMEOUT_MS = 5000; // Same as server REGEN_CONFIG.COMBAT_TIMEOUT
      const isInCombat = data.lastCombatTime > 0 && (now - data.lastCombatTime) < COMBAT_TIMEOUT_MS;
      const expiresAt = isInCombat ? data.lastCombatTime + COMBAT_TIMEOUT_MS : 0;
      const remaining = isInCombat ? Math.max(0, expiresAt - now) : 0;
      emitGameEvent('combatStateUpdate', {
        isInCombat,
        expiresAt,
        remaining,
      });
    };

    // Handle ship class changes (when switching ships at shipyard)
    networkManager.onLocalPlayerShipClassChange = (data) => {
      this.player.setShipClass(data.shipClassId);
      this.updateZoomLimitsForShip();
    };

    // Equipment slot changes (weapon/module swaps apply immediately)
    networkManager.onLocalPlayerEquipmentChange = (data) => {
      if (!this.player) return;
      this.player.slots = data.slots;
      this.player.recalculateStats();
    };

    // Track reputation state changes from PlayerState (persists across scene changes)
    networkManager.onLocalPlayerReputationChange = (data) => {
      emitGameEvent('reputationStateUpdate', {
        reputation: data.reputation,
      });
    };

    networkManager.onPlayerJoin = (data: RemotePlayerData) => {
      console.log('[DEBUG] GameScene.onPlayerJoin', { sessionId: data.sessionId, username: data.username, x: data.x, y: data.y });
      const remotePlayer = new RemotePlayer(data);
      remotePlayer.zIndex = 90;
      this.worldContainer.addChild(remotePlayer);
      // Add name tag and warp trail to world container (not child of player so they don't rotate)
      const nameTag = remotePlayer.getNameTag();
      if (nameTag) this.worldContainer.addChild(nameTag);
      const warpTrail = remotePlayer.getWarpTrailGraphics();
      if (warpTrail) this.worldContainer.addChild(warpTrail);
      this.remotePlayers.set(data.sessionId, remotePlayer);
      if (data.isDummy) emitGameEvent('dummyCountChanged', { count: this.getDummyCount() });
    };

    networkManager.onPlayerLeave = (sessionId: string) => {
      const remotePlayer = this.remotePlayers.get(sessionId);
      const wasDummy = remotePlayer?.isDummy;
      if (remotePlayer) {
        remotePlayer.destroy();
        this.remotePlayers.delete(sessionId);
      }
      if (wasDummy) emitGameEvent('dummyCountChanged', { count: this.getDummyCount() });
      // Clean up remote mining laser if any
      const minerLaser = this.remoteMiningLasers.get(sessionId);
      if (minerLaser) { minerLaser.stopEmitting(); minerLaser.destroy(); this.remoteMiningLasers.delete(sessionId); }
      this.remoteMinerAsteroids.delete(sessionId);
      this.clearSelectionIfEntityDestroyed(sessionId);
    };

    networkManager.onPlayerUpdate = (sessionId: string, data: Partial<RemotePlayerData>) => {
      const remotePlayer = this.remotePlayers.get(sessionId);
      if (remotePlayer) {
        remotePlayer.updateFromServer(data);
      }
    };

    networkManager.onAsteroidAdd = (data: AsteroidData) => {
      this.asteroidData.set(data.id, data);
    };

    networkManager.onAsteroidRemove = (asteroidId: string) => {
      this.asteroidData.delete(asteroidId);
      const asteroid = this.asteroids.get(asteroidId);
      if (asteroid) {
        asteroid.destroy();
        this.asteroids.delete(asteroidId);
      }
      this.clearSelectionIfEntityDestroyed(asteroidId);
    };

    networkManager.onAsteroidUpdate = (asteroidId: string, data: Partial<AsteroidData>) => {
      const storedData = this.asteroidData.get(asteroidId);
      if (storedData) Object.assign(storedData, data);
      const asteroid = this.asteroids.get(asteroidId);
      if (asteroid) asteroid.updateFromServer(data);

      // Live-update inspect panel if this asteroid is being inspected
      if (this._inspectOpen && this._inspectTargetId === asteroidId && storedData) {
        const toolTier = this.getEquippedMiningToolTier();
        const miningPower = this.getEquippedMiningToolPower();
        const resourceTier = RESOURCE_TIERS[storedData.resourceType as ResourceType] ?? 1;
        let miningTimeEstimate: number | undefined;
        if (toolTier > 0 && miningPower > 0) {
          const tierMult = computeMiningTierMultiplier(toolTier, resourceTier);
          const dmgPerTick = miningPower * tierMult;
          miningTimeEstimate = Math.ceil(storedData.health / (dmgPerTick * 2));
        }
        emitGameEvent('inspectResult', {
          success: true, targetId: asteroidId,
          targetName: `Asteroid (${storedData.resourceType})`,
          shipClass: 'asteroid', shipName: storedData.size,
          hull: storedData.health, maxHull: storedData.maxHealth,
          reputation: 0, equipment: [],
          entityType: 'asteroid',
          resourceType: storedData.resourceType,
          resourceAmount: storedData.resourceAmount,
          resourceTier,
          miningTimeEstimate,
        });
      }
    };

    networkManager.onAsteroidDestroyed = (data) => {
      if (!this.locallyDestroyedAsteroids.has(data.asteroidId)) {
        this.createAsteroidExplosion(data.x, data.y, data.resourceType);
        audioManager.playSFX('explosion_asteroid');
      }
      this.locallyDestroyedAsteroids.delete(data.asteroidId);
    };

    // ===== V1 HARVESTABLE FIELD LISTENERS =====
    // Mirror the asteroid pattern: stash data on add, instantiate via AOI culling.

    networkManager.onIceAsteroidAdd = (data) => { this.iceAsteroidData.set(data.id, data); };
    networkManager.onIceAsteroidRemove = (id) => {
      this.iceAsteroidData.delete(id);
      const entity = this.iceAsteroids.get(id);
      if (entity) { entity.destroy(); this.iceAsteroids.delete(id); }
      this.clearSelectionIfEntityDestroyed(id);
    };
    networkManager.onIceAsteroidUpdate = (id, data) => {
      const stored = this.iceAsteroidData.get(id);
      if (stored) Object.assign(stored, data);
      const entity = this.iceAsteroids.get(id);
      if (entity) entity.updateFromServer(data);
    };

    networkManager.onGasPocketAdd = (data) => { this.gasPocketData.set(data.id, data); };
    networkManager.onGasPocketRemove = (id) => {
      this.gasPocketData.delete(id);
      const entity = this.gasPockets.get(id);
      if (entity) { entity.destroy(); this.gasPockets.delete(id); }
      this.clearSelectionIfEntityDestroyed(id);
    };
    networkManager.onGasPocketUpdate = (id, data) => {
      const stored = this.gasPocketData.get(id);
      if (stored) Object.assign(stored, data);
      const entity = this.gasPockets.get(id);
      if (entity) entity.updateFromServer(data);
    };

    networkManager.onWreckageAdd = (data) => { this.wreckageData.set(data.id, data); };
    networkManager.onWreckageRemove = (id) => {
      this.wreckageData.delete(id);
      const entity = this.wreckages.get(id);
      if (entity) { entity.destroy(); this.wreckages.delete(id); }
      this.clearSelectionIfEntityDestroyed(id);
    };
    networkManager.onWreckageUpdate = (id, data) => {
      const stored = this.wreckageData.get(id);
      if (stored) Object.assign(stored, data);
      const entity = this.wreckages.get(id);
      if (entity) entity.updateFromServer(data);
    };

    networkManager.onPlanetAdd = (data: PlanetData) => {
      // Store data for map display (with systemId for solar system grouping)
      this.planetData.set(data.id, {
        id: data.id,
        name: data.name,
        systemId: data.systemId,
        x: data.x,
        y: data.y,
        radius: data.radius,
        planetType: data.planetType,
        orbitalDistance: data.orbitalDistance,
      });

      // Create visual entity
      const existingPlanet = this.planets.get(data.id);
      if (existingPlanet) existingPlanet.destroy();
      const planet = new Planet(data);
      planet.zIndex = -8; // Behind stations (-5), in front of deep scenery

      // Set sun position for shadow calculation
      const system = this.systems.get(data.systemId);
      if (system) planet.setSunPosition(system.x, system.y);

      this.worldContainer.addChild(planet);
      this.planets.set(data.id, planet);
    };

    // Handle planet position updates (orbital movement)
    networkManager.onPlanetChange = (planetId: string, data: Partial<PlanetData>) => {
      const planet = this.planets.get(planetId);
      if (planet) {
        planet.updateFromServer(data);
      }
      // Update stored data for map
      const stored = this.planetData.get(planetId);
      if (stored) {
        if (data.x !== undefined) stored.x = data.x;
        if (data.y !== undefined) stored.y = data.y;
      }
      // Emit orbital update for the constellation map overlay (live position)
      if (data.x !== undefined && data.y !== undefined) {
        emitGameEvent('planetOrbitalUpdate', { id: planetId, x: data.x, y: data.y });
      }
    };

    networkManager.onStationAdd = (data: StationData) => {
      this.stationData.set(data.id, { id: data.id, name: data.name, x: data.x, y: data.y });
      const existingStation = this.stations.get(data.id);
      if (existingStation) existingStation.destroy();
      const station = new Station(data.x, data.y, data.id, 'station', data.name);
      station.zIndex = -5;
      station.alpha = 0.75;
      this.worldContainer.addChild(station);
      this.stations.set(data.id, station);

      if (this.pendingDockedState?.stationId === data.id) {
        this.pendingDockedState = null;
        // Notify React to show station UI
        emitGameEvent('dockingStateChange', {
          isDocked: true,
          stationId: data.id,
          stationName: data.name,
        });
      }
    };

    // Private stations (player-owned outposts) — visible to all, dockable only by owner
    networkManager.onPrivateStationAdd = (data) => {
      this.privateStationData.set(data.id, { id: data.id, name: `${data.name} [${data.ownerName}]`, x: data.x, y: data.y });
      const existing = this.privateStations.get(data.id);
      if (existing) existing.destroy();
      // Tint: green = your own outpost, cyan = someone else's
      const room: any = (networkManager as any).room;
      const sid = networkManager.getLocalSessionId();
      const me = sid ? room?.state?.players?.get?.(sid) : null;
      const myPlayerId: string | undefined = me?.id;
      const tint = myPlayerId && data.ownerPlayerId === myPlayerId ? 0x4ade80 : 0x22d3ee;
      const ps = new Station(data.x, data.y, data.id, 'station_2', `${data.name} [${data.ownerName}]`, tint);
      ps.zIndex = -5;
      ps.alpha = 0.75;
      this.worldContainer.addChild(ps);
      this.privateStations.set(data.id, ps);

      if (this.pendingDockedState?.stationId === data.id) {
        this.pendingDockedState = null;
        emitGameEvent('dockingStateChange', {
          isDocked: true,
          stationId: data.id,
          stationName: data.name,
        });
      }
    };

    networkManager.onPrivateStationUpdate = (psId, partial) => {
      const stored = this.privateStationData.get(psId);
      if (stored && partial.name !== undefined) stored.name = partial.name;
    };

    networkManager.onPrivateStationRemove = (psId) => {
      const ps = this.privateStations.get(psId);
      if (ps) { ps.destroy(); this.privateStations.delete(psId); }
      this.privateStationData.delete(psId);
      this.clearSelectionIfEntityDestroyed(psId);
    };

    // Settlements (emergent rogue-drone nests). AOI-synced, so these fire when the
    // local player gets close enough to discover / leave one (docs/drone_settlement.md).
    networkManager.onSettlementAdd = (data) => {
      const existing = this.settlements.get(data.id);
      if (existing) existing.destroy();
      const s = new Settlement(data);
      s.zIndex = 60;
      this.worldContainer.addChild(s);
      this.settlements.set(data.id, s);
    };
    networkManager.onSettlementUpdate = (id, partial) => {
      const s = this.settlements.get(id);
      if (s) s.updateFromServer(partial);
    };
    networkManager.onSettlementRemove = (id) => {
      const s = this.settlements.get(id);
      if (s) { s.destroy(); this.settlements.delete(id); }
      this.clearSelectionIfEntityDestroyed(id);
    };

    // Tutorial v2: in-world Aura beacons. Static once placed. We draw a teal
    // proximity ring + center dot; per-frame distance check (updateTutorialBeacons)
    // emits tutorialBeaconRange events so the modal can mount/unmount.
    networkManager.onTutorialBeaconAdd = (data) => {
      const existing = this.tutorialBeacons.get(data.id);
      if (existing) {
        this.worldContainer.removeChild(existing.ring);
        this.worldContainer.removeChild(existing.dot);
        existing.ring.destroy();
        existing.dot.destroy();
      }
      // Two separate Graphics so the dot can pulse (scale + alpha) without
      // affecting the ring. Both static draws — only their transforms/alpha
      // change per frame, which is cheap.
      const ring = new Graphics();
      ring.zIndex = 55;
      ring.circle(0, 0, data.radius).stroke({ color: 0x20a5a6, alpha: 0.4, width: 3 });
      ring.x = data.x;
      ring.y = data.y;
      this.worldContainer.addChild(ring);

      const dot = new Graphics();
      dot.zIndex = 56;
      dot.circle(0, 0, 60).fill({ color: 0x20a5a6, alpha: 0.85 });
      dot.x = data.x;
      dot.y = data.y;
      this.worldContainer.addChild(dot);

      this.tutorialBeacons.set(data.id, { x: data.x, y: data.y, radius: data.radius, name: data.name, ring, dot });
    };
    networkManager.onTutorialBeaconRemove = (id) => {
      const b = this.tutorialBeacons.get(id);
      if (b) {
        this.worldContainer.removeChild(b.ring);
        this.worldContainer.removeChild(b.dot);
        b.ring.destroy();
        b.dot.destroy();
        this.tutorialBeacons.delete(id);
      }
    };
    networkManager.onSettlementHit = (data) => {
      // Server only deducts core HP once the fighter screen is down; suppress the
      // floating number on shielded (blocked) hits so it isn't misleading.
      if (!data.blocked) this.createFloatingDamage(data.x, data.y, data.damage);
      // Reveal the HP bar momentarily — even on blocked hits, so the attacker gets
      // feedback that they're hitting *something* (the fighter screen).
      const s = this.settlements.get(data.settlementId);
      if (s) s.showHpBar();
    };
    networkManager.onSettlementDestroyed = (data) => {
      const s = this.settlements.get(data.settlementId);
      if (s) s.explode();
      audioManager.playSFX('explosion_ship');
    };

    networkManager.onAsteroidZones = (zones) => { this.asteroidZones = zones; };
    networkManager.onResourceZones = (zones) => { this.resourceZones = zones; };

    // Handle solar system additions (for constellation map display)
    networkManager.onSystemAdd = (data) => {
      this.systems.set(data.id, {
        id: data.id,
        name: data.name,
        x: data.x,
        y: data.y,
        starType: data.starType,
        sunRadius: data.sunRadius,
      });
    };

    networkManager.onSystemRemove = (systemId: string) => {
      this.systems.delete(systemId);
    };

    networkManager.onSunAdd = (data: SunData) => {
      const sun = new Sun(data);
      sun.zIndex = -10;
      this.worldContainer.addChild(sun);
      this.suns.set(data.id, sun);
    };

    networkManager.onSunRemove = (sunId: string) => {
      const sun = this.suns.get(sunId);
      if (sun) { sun.destroy(); this.suns.delete(sunId); }
    };

    networkManager.onNebulaAdd = (data: NebulaData) => {
      const nebula = new Nebula(data);
      nebula.zIndex = 20;
      this.sceneryContainer.addChild(nebula);
      this.nebulae.set(data.id, nebula);
    };

    networkManager.onNebulaRemove = (nebulaId: string) => {
      const nebula = this.nebulae.get(nebulaId);
      if (nebula) { nebula.destroy(); this.nebulae.delete(nebulaId); }
    };

    networkManager.onGatewayAdd = (data: GatewayData) => {
      this.gatewayData.set(data.id, data);
      const existingGateway = this.gateways.get(data.id);
      if (existingGateway) existingGateway.destroy();
      const gateway = new Gateway(
        data.x, data.y, data.id,
        data.targetConstellationName, data.targetConstellationX, data.targetConstellationY,
        data.travelCost
      );
      gateway.zIndex = 45;
      this.worldContainer.addChild(gateway);
      this.gateways.set(data.id, gateway);
    };

    networkManager.onGatewayRemove = (gatewayId: string) => {
      const gateway = this.gateways.get(gatewayId);
      if (gateway) { gateway.destroy(); this.gateways.delete(gatewayId); }
      this.gatewayData.delete(gatewayId);
    };

    // Interdiction beacons - visible in world with range circle
    networkManager.onInterdictionBeaconAdd = (data) => {
      const existing = this.beacons.get(data.id);
      if (existing) existing.destroy();
      const beacon = new InterdictionBeacon(
        data.x, data.y, data.id,
        data.ownerId, data.ownerName,
        data.radius, data.expiresAt,
      );
      beacon.zIndex = 40;
      this.worldContainer.addChild(beacon);
      this.beacons.set(data.id, beacon);
    };

    networkManager.onInterdictionBeaconRemove = (beaconId) => {
      const beacon = this.beacons.get(beaconId);
      if (beacon) { beacon.destroy(); this.beacons.delete(beaconId); }
    };

    networkManager.onGatewayTravelStart = (data) => {
      this.handleGatewayTravelStart(data);
    };

    networkManager.onGatewayTravelError = (data) => {
      emitGameEvent('notification', { type: 'error', message: data.error });
    };

    networkManager.onResourceHarvested = (_data) => {
      // Resource harvested - UI handles display
    };

    networkManager.onRadiationDamage = (data) => {
      // Emit radiation damage to HUD for warning display
      emitGameEvent('radiationDamage', { proximity: data.proximity, damage: data.damage });

      // Visual feedback - damage flash when taking radiation damage
      if (data.damage > 0 && this.player) {
        this.player.triggerDamageFlash();
      }
    };

    networkManager.onDockingStarted = (data) => { this.startDockingAnimation(data); };

    networkManager.onDocked = (data) => {
      this.isPlayerDocked = true;
      this.isDockingAnimation = false;
      this.dockingStationId = data.stationId;
      this.player.x = data.x;
      this.player.y = data.y;
      this.player.velocity.x = 0;
      this.player.velocity.y = 0;
      // Sync server position for Ghost Object Pattern
      this.player.initializeServerPosition(data.x, data.y, 0, 0);
      this.player.visible = false; // Hide player - they're inside the station
      this.player.inputEnabled = false;

      // Clear scanned radar entities when docking at station
      emitGameEvent('radarClearEntities', undefined);

      audioManager.playUI('dock_complete');

      // Notify React to show station UI
      emitGameEvent('dockingStateChange', {
        isDocked: true,
        stationId: data.stationId,
        stationName: data.stationName,
      });
    };

    networkManager.onUndocked = (data) => {
      this.isPlayerDocked = false;
      this.isDockingAnimation = false;
      this.dockingStationId = null;
      this.player.x = data.x;
      this.player.y = data.y;
      this.player.velocity.x = data.velocityX;
      this.player.velocity.y = data.velocityY;
      // Sync server position for Ghost Object Pattern
      this.player.initializeServerPosition(data.x, data.y, data.velocityX, data.velocityY);
      this.player.inputEnabled = true;

      audioManager.playUI('undock');

      // Notify React to hide station UI
      emitGameEvent('dockingStateChange', {
        isDocked: false,
      });

      // Fade-in the ship
      this.playShipAppearAnimation();
    };

    // Drone bay toggle result — show notification
    networkManager.onToggleShipDronesResult = (data) => {
      if (data.error) {
        emitGameEvent('notification', { type: 'warning', message: `Drone bay: ${data.error}` });
      } else if (data.action === 'deploy') {
        emitGameEvent('notification', { type: 'info', message: `Deployed ${data.count} drones` });
      } else if (data.action === 'recall') {
        emitGameEvent('notification', { type: 'info', message: `Recalling ${data.count} drones` });
      }
    };

    // DEV: Teleport snap position
    networkManager.onDevTeleported = (data) => {
      this.player.x = data.x;
      this.player.y = data.y;
      this.player.velocity.x = 0;
      this.player.velocity.y = 0;
      this.player.initializeServerPosition(data.x, data.y, 0, 0);
    };

    networkManager.onMiningStarted = (data) => { this.startMiningDirect(data); };
    networkManager.onMiningStopped = (data: any) => { this.stopMiningVisuals(data?.asteroidId); };
    networkManager.onMiningTick = (data) => {
      this.handleMiningTick({
        asteroidId: data.asteroidId,
        damage: data.damage,
        health: data.remainingHealth,
        maxHealth: data.maxHealth,
        resourcesGained: data.resourcesGained,
        resourceType: data.resourceType,
        fractionalProgress: data.fractionalProgress,
        pendingProgress: data.pendingProgress,
      });
    };
    networkManager.onAsteroidExploded = (data) => {
      this.handleMiningAsteroidExplosion({
        asteroidId: data.asteroidId,
        pushVelocityX: data.pushVelocityX,
        pushVelocityY: data.pushVelocityY,
      });
    };
    networkManager.onMiningFailed = (data) => {
      // Re-enable input if mining request was rejected
      this.player.inputEnabled = true;
      emitGameEvent('notification', { type: 'warning', message: data.reason || 'Mining failed', duration: 2000 });
    };

    networkManager.onPlayerMiningStarted = (data) => { this.handleRemotePlayerMiningStarted(data); };
    networkManager.onPlayerMiningStopped = (data) => { this.handleRemotePlayerMiningStopped(data); };

    // Warp travel callbacks
    networkManager.onWarpStateChanged = (data) => {
      if (this.player) {
        this.player.setWarpState(
          data.phase as import('@astra/shared').WarpPhase,
          data.targetX,
          data.targetY,
          data.warpSpeed,
          data.cooldownUntil
        );
        // Emit to React UI with calculation timing info
        emitGameEvent('warpStateChanged', {
          phase: data.phase as 'none' | 'ready' | 'calculating' | 'traveling' | 'cooldown',
          targetX: data.targetX,
          targetY: data.targetY,
          cooldownUntil: data.cooldownUntil,
          calculationStartTime: Date.now(),
          calculationDuration: data.phase === 'calculating' && data.calculationEndTime
            ? data.calculationEndTime - Date.now()
            : undefined,
        });
      }
    };

    networkManager.onWarpInterrupted = (data) => {
      // Notify React UI of interruption
      emitGameEvent('warpInterrupted', {
        reason: data.reason,
        beaconOwnerName: data.attackerName,
      });
      // Show notification
      emitGameEvent('notification', {
        message: `Warp interrupted: ${data.reason}`,
        type: 'warning',
        duration: 4000,
      });
    };

    // Gateway cloak broken
    networkManager.onCloakBroken = (data) => {
      // Notify React UI that cloak is broken
      emitGameEvent('cloakBroken', { reason: data.reason });
      // Show notification
      const reasonText = data.reason === 'fired' ? 'You opened fire' :
                        data.reason === 'collision' ? 'Collision detected' :
                        'Cloak expired';
      emitGameEvent('notification', {
        message: `Cloak broken: ${reasonText}`,
        type: 'warning',
        duration: 3000,
      });
    };

    // New player protection events
    networkManager.onTargetProtected = (data) => {
      emitGameEvent('notification', {
        message: 'Target is protected - new player cannot be attacked',
        type: 'warning',
        duration: 3000,
      });
    };

    networkManager.onProtectionLost = (data) => {
      emitGameEvent('notification', {
        message: 'Protection lost! You attacked first - vulnerable for 60 seconds',
        type: 'warning',
        duration: 5000,
      });
    };

    networkManager.onEmergencyShipProvided = (data) => {
      emitGameEvent('notification', {
        message: 'Emergency ship provided! You lost all ships and received a free Sparrow',
        type: 'info',
        duration: 8000,
      });
    };

    // Handle remote player projectiles (visual only - damage is server-authoritative)
    networkManager.onPlayerProjectile = (data) => {
      const projectile = new Projectile({
        ownerId: data.playerId,
        x: data.x,
        y: data.y,
        angle: data.angle,
        damage: data.damage,
        speed: data.speed,
        weaponType: data.weaponType,
        isHostile: true, // Other players' projectiles are hostile to local player
      });
      projectile.zIndex = 95;
      this.worldContainer.addChild(projectile);
      this.networkProjectiles.push(projectile);
      audioManager.playSFX(data.weaponType === 'kinetic' ? 'weapon_kinetic' : 'weapon_laser');
    };

    networkManager.onDroneProjectile = (data) => {
      const projectile = new Projectile({
        ownerId: data.droneId,
        x: data.x,
        y: data.y,
        angle: data.angle,
        damage: data.damage,
        speed: data.speed,
        weaponType: data.weaponType,
        isHostile: true,
      });
      projectile.zIndex = 95;
      this.worldContainer.addChild(projectile);
      this.networkProjectiles.push(projectile);
      audioManager.playSFX('weapon_kinetic');
    };

    networkManager.onPlayerHit = (data) => {
      if (data.playerId === networkManager.getLocalSessionId()) {
        // Handle collision bounce from server (authoritative physics)
        if (data.isCollision && data.velocityX !== undefined && data.velocityY !== undefined) {
          if (ANALYSIS_MODE) {
            const dx = data.x - this.player.x;
            const dy = data.y - this.player.y;
            const posDiff = Math.sqrt(dx * dx + dy * dy);
            const dvx = data.velocityX - this.player.velocityX;
            const dvy = data.velocityY - this.player.velocityY;
            const velDiff = Math.sqrt(dvx * dvx + dvy * dvy);
            clientLog(`[COLLISION:SERVER_CORRECTION] ` + JSON.stringify({
              ts: Date.now(),
              serverPos: { x: data.x.toFixed(1), y: data.y.toFixed(1) },
              clientPos: { x: this.player.x.toFixed(1), y: this.player.y.toFixed(1) },
              posDiff: posDiff.toFixed(1),
              serverVel: { vx: data.velocityX.toFixed(1), vy: data.velocityY.toFixed(1) },
              clientVel: { vx: this.player.velocityX.toFixed(1), vy: this.player.velocityY.toFixed(1) },
              velDiff: velDiff.toFixed(1),
              stunned: this.player.isStunned,
            }));
          }
          // GHOST PATTERN: Use smooth correction instead of instant snap
          // Server position is authoritative; visual will lerp toward it
          this.player.applyServerCorrection(data.x, data.y, data.velocityX, data.velocityY);
          this.player.applyStun();
        } else {
          // Regular projectile hit
          this.createHitParticles(data.x, data.y, 'laser');
          if (data.shieldDamage > 0) { this.player.spawnShieldHitEffect(data.hitAngle); audioManager.playSFX('hit_shield'); }
          if (data.hullDamage > 0) { this.player.triggerDamageFlash(); audioManager.playSFX('hit_hull'); }
          this.game.events.emit('playerDamage', { damage: data.damage });
        }
      } else {
        const remotePlayer = this.remotePlayers.get(data.playerId);
        if (remotePlayer) remotePlayer.showDamageIndicator(data.shieldDamage > 0);
      }
    };

    // Player died in Green/Yellow zone (ship kept)
    networkManager.onPlayerDestroyed = (data) => {
      audioManager.playSFX('explosion_ship');
      if (data.playerId === networkManager.getLocalSessionId()) {
        this.clearLocalCombatTargeting();
        this.player.explode();

        // Emit death event to React UI
        emitGameEvent('playerDied', {
          nearestStationName: data.nearestStationName,
          nearestStationId: data.nearestStationId,
          threatLevel: data.threatLevel as 'green' | 'yellow' | 'red',
          itemsDropped: data.itemsDropped || [],
        });
      } else {
        const remotePlayer = this.remotePlayers.get(data.playerId);
        if (remotePlayer) remotePlayer.explode();
      }
    };

    // Player died in Red zone (ship lost permanently)
    networkManager.onShipLost = (data) => {
      audioManager.playSFX('explosion_ship');
      if (data.playerId === networkManager.getLocalSessionId()) {
        this.clearLocalCombatTargeting();
        this.player.explode();

        // Emit ship lost event to React UI
        emitGameEvent('shipLost', {
          lostShipClass: data.lostShipClass,
          nearestStationName: data.nearestStationName,
          nearestStationId: data.nearestStationId,
          itemsDropped: data.itemsDropped || [],
        });
      } else {
        const remotePlayer = this.remotePlayers.get(data.playerId);
        if (remotePlayer) remotePlayer.explode();
      }
    };

    networkManager.onDropAdd = (data: DropData) => {
      const drop = new Drop(data);
      drop.zIndex = 60;
      this.worldContainer.addChild(drop);
      this.drops.set(data.id, drop);
    };

    networkManager.onDropRemove = (dropId: string) => {
      const drop = this.drops.get(dropId);
      if (drop) { drop.destroy(); this.drops.delete(dropId); }
      this.clearSelectionIfEntityDestroyed(dropId);
      emitGameEvent('dropRemoved', { dropId });
    };

    networkManager.onDroneAdd = (data: DroneData) => {
      const drone = new Drone(data);
      drone.zIndex = 70;
      this.worldContainer.addChild(drone);
      this.drones.set(data.id, drone);
      if (data.shipDefId) emitGameEvent('dummyCountChanged', { count: this.getDummyCount() });
    };

    networkManager.onDroneUpdate = (droneId: string, data: Partial<DroneData>) => {
      const drone = this.drones.get(droneId);
      if (drone) drone.updateFromServer(data);
    };

    networkManager.onDroneRemove = (droneId: string) => {
      const wasDummy = this.drones.get(droneId)?.isDummy;
      const drone = this.drones.get(droneId);
      if (drone) { drone.destroy(); this.drones.delete(droneId); }
      this.removeDroneWarpVisual(droneId);
      this.clearSelectionIfEntityDestroyed(droneId);
      if (wasDummy) emitGameEvent('dummyCountChanged', { count: this.getDummyCount() });
    };

    networkManager.onDroneDestroyed = (data) => {
      const drone = this.drones.get(data.droneId);
      if (drone) drone.explode();
      this.removeDroneWarpVisual(data.droneId);
      audioManager.playSFX('explosion_ship');
    };

    networkManager.onDropUpdate = (dropId, data) => {
      const drop = this.drops.get(dropId);
      if (drop) drop.updateInventory(data.items);
      // Tell the modal (if open for this drop) to refresh
      emitGameEvent('dropInventoryUpdate', { dropId, items: data.items });
    };

    networkManager.onCreditsChange = (credits: number) => {
      this.player.credits = credits;
      this.game.events.emit('creditsChange', credits);
    };

    networkManager.onDisconnected = (code: number) => {
      this.connected = false;
      const message = code === DISCONNECT_CODES.DUPLICATE_SESSION
        ? 'You logged in from another browser.'
        : `Disconnected from server (${code})`;
      emitGameEvent('notification', { message, type: code === DISCONNECT_CODES.DUPLICATE_SESSION ? 'error' : 'warning' });

      for (const [, p] of this.remotePlayers) p.destroy();
      this.remotePlayers.clear();
      for (const proj of this.networkProjectiles) proj.destroy();
      this.networkProjectiles = [];
      for (const [, d] of this.drops) d.destroy();
      this.drops.clear();
    };

    networkManager.onReconnecting = (attempt: number, maxAttempts: number) => {
      emitGameEvent('notification', { message: `Reconnecting... (${attempt}/${maxAttempts})`, type: 'info' });
    };

    networkManager.onConnected = () => {
      this.connected = true;
      emitGameEvent('notification', { message: 'Connected to server', type: 'info' });
    };

    networkManager.onReconnectFailed = () => {
      emitGameEvent('notification', { message: 'Failed to reconnect. Please refresh.', type: 'error' });
    };
  }

  update(delta: number): void {
    if (!this.player) return;

    const time = performance.now();

    // E key for interact (dock/mine/land/loot)
    if (this.input.isKeyJustDown('E')) {
      // If in docking range and not docked, dock
      if (this.playerInDockingRange && !this.isPlayerDocked && this.nearestStation) {
        networkManager.sendDock(this.nearestStation.stationId);
      } else if (this.nearestDropId && !this.isPlayerDocked && this.nearestDropAccessible) {
        const drop = this.drops.get(this.nearestDropId);
        if (drop) {
          const speed = Math.hypot(this.player.velocity.x, this.player.velocity.y);
          if (speed > LOOT_INTERACTION_MAX_SPEED) {
            emitGameEvent('notification', {
              type: 'warning',
              message: 'Hold still to access loot',
              duration: 1500,
            });
          } else {
            emitGameEvent('openDropModal', { dropId: this.nearestDropId, items: drop.itemEntries });
          }
        }
      } else if (this.playerInPlanetLandingRange && !this.isPlayerDocked && !this.isPlayerManagingPlanet && this.nearestPlanet && !this.isPlanetEntryAnimation) {
        // Play disappear animation, then open planet panel after it completes.
        this.isPlanetEntryAnimation = true;
        this.player.inputEnabled = false;
        this.player.velocity.x = 0;
        this.player.velocity.y = 0;
        const planetId = this.nearestPlanet.planetId;
        this.playShipDisappearAnimation(() => {
          emitGameEvent('openPlanetPanel', { planetId });
          this.isPlanetEntryAnimation = false;
        });
      }
    }

    // J key for gateway travel
    if (this.input.isKeyJustDown('J')) {
      if (this.playerInGatewayRange && !this.isPlayerDocked && this.nearestGateway) {
        networkManager.sendUseGateway(this.nearestGateway.gatewayId);
      }
    }

    // B key: toggle ship drone bay overlay (capital-ship drone management in flight)
    if (this.input.isKeyJustDown('B') && !this.isPlayerDocked) {
      emitGameEvent('toggleShipBay');
    }

    // Y key: toggle inspect panel / inspect selected target (must be scanned first)
    if (this.input.isKeyJustDown('Y')) {
      if (this._inspectOpen) {
        // Close panel
        emitGameEvent('inspectClose', undefined);
        this._inspectOpen = false;
      } else if (this.selectedEntity && this.selectedEntity.id) {
        if (!this.scannedEntityIds.has(this.selectedEntity.id)) {
          emitGameEvent('notification', { type: 'warning', message: 'Target not scanned' });
        } else {
          this.handleInspectRequest(this.selectedEntity.id);
        }
      }
    }

    // Note: Inventory toggle (I key) is handled by App.tsx

    // Note: Camera.update() is called by SceneManager before scene updates
    // So no need to call it here again

    const cameraX = this.camera.scrollX;
    const cameraY = this.camera.scrollY;
    const cameraZoom = this.camera.zoom;
    const playerSpeed = Math.sqrt(this.player.velocity.x ** 2 + this.player.velocity.y ** 2);

    // No spatial audio in space (no sound in vacuum)

    const isBoosting = this.player.getIsBoosting();
    this.depthLayers.update(cameraX, cameraY, playerSpeed, cameraZoom);
    this.starfield.update(cameraX, cameraY, playerSpeed, cameraZoom, isBoosting);
    this.constellationBoundaryWarning.update(this.player.x, this.player.y, delta);

    // Provide obstacles data to player for autopilot collision avoidance
    if (this.player.isAutopiloting) {
      this.player.setData('obstaclesForAvoidance', this.getObstaclesForAvoidance());
    }

    this.player.update(delta);

    // Force stop during harvestable dock mining only (not asteroid mining)
    if (this.isMiningAnimation) {
      this.player.stop();
    }

    // Projectiles
    this.projectiles = this.projectiles.filter((p) => {
      const alive = p.update(delta);
      if (!alive) p.destroy();
      return alive;
    });

    this.checkProjectileRemotePlayerCollisions();
    this.checkProjectileDroneCollisions();
    this.checkProjectileAsteroidCollisions();
    this.updateNetworkProjectiles(delta);
    this.checkNetworkProjectilePlayerCollisions();
    this.checkPlayerAsteroidCollisions();
    this.checkPlayerRemotePlayerCollisions();

    if (this.connected && this.initialLoadComplete) this.sendPlayerStateToServer(time);

    // Update entities with visibility culling for performance
    // Entities outside viewport skip update() and are hidden
    const CULL_MARGIN = 500; // Extra margin for entities about to enter view
    for (const [, rp] of this.remotePlayers) {
      const visible = this.camera.isVisible(rp.x, rp.y, CULL_MARGIN);
      rp.visible = visible;
      if (visible) rp.update(delta);
    }
    for (const [, drop] of this.drops) {
      const visible = this.camera.isVisible(drop.x, drop.y, CULL_MARGIN);
      drop.visible = visible;
      if (visible) drop.update(delta);
    }
    // Drone mining beams: clear the shared Graphics once, then draw a light line per visible
    // mining drone (no per-drone object, no spark particles). One global pulse for shimmer.
    if (!this.droneMiningBeams.parent) { this.droneMiningBeams.zIndex = 15; this.worldContainer.addChild(this.droneMiningBeams); }
    this.droneMiningBeams.clear();
    this.droneBeamPulse += delta / 1000;
    const beamPulse = 0.7 + 0.3 * Math.sin(this.droneBeamPulse * 6);
    for (const [, drone] of this.drones) {
      const visible = this.camera.isVisible(drone.x, drone.y, CULL_MARGIN);
      drone.visible = visible;
      if (visible) drone.update(delta);
      if (visible && drone.droneState === 'mining' && drone.targetId) {
        const e = this.findMinableById(drone.targetId);
        if (e) this.drawDroneMiningBeam(drone, e, beamPulse);
      }
      this.updateDroneWarpVisual(drone, visible);
    }
    for (const [, asteroid] of this.asteroids) {
      const visible = this.camera.isVisible(asteroid.x, asteroid.y, CULL_MARGIN);
      asteroid.visible = visible;
      if (visible) asteroid.update(delta);
    }
    for (const [, e] of this.iceAsteroids) {
      const visible = this.camera.isVisible(e.x, e.y, CULL_MARGIN);
      e.visible = visible;
      if (visible) e.update(delta);
    }
    for (const [, e] of this.gasPockets) {
      const visible = this.camera.isVisible(e.x, e.y, CULL_MARGIN);
      e.visible = visible;
      if (visible) e.update(delta);
    }
    for (const [, e] of this.wreckages) {
      const visible = this.camera.isVisible(e.x, e.y, CULL_MARGIN);
      e.visible = visible;
      if (visible) e.update(delta);
    }
    for (const [, station] of this.stations) station.update(delta);
    for (const [, ps] of this.privateStations) ps.update(delta);
    for (const [, s] of this.settlements) s.update(delta);
    for (const [, sun] of this.suns) sun.update(delta);
    for (const [, planet] of this.planets) planet.update(delta);
    for (const [, nebula] of this.nebulae) nebula.update(delta);
    for (const [, gateway] of this.gateways) gateway.update(delta);
    for (const [, beacon] of this.beacons) beacon.update(delta);
    this.updateTutorialBeacons(delta);

    this.updateHitParticles(delta);
    this.updateSelectionIndicator();
    this.updateScanOverlays();
    this.checkStationZones();
    this.checkNebulaZone();
    this.checkGatewayZones();
    this.checkPlanetZones();
    this.checkDropProximity();
    this.checkPlayerZones();
    if (!this.isPlayerDocked) {
      this.checkAsteroidMiningZones();
    }

    // Update mining range circle (fade out)
    if (this.miningRangeCircle && this.miningRangeCircleTimer > 0) {
      this.miningRangeCircleTimer -= delta;
      this.miningRangeCircle.x = this.player.x;
      this.miningRangeCircle.y = this.player.y;
      this.miningRangeCircle.alpha = Math.max(0, this.miningRangeCircleTimer / 1500);
      if (this.miningRangeCircleTimer <= 0) {
        this.miningRangeCircle.destroy();
        this.miningRangeCircle = null;
      }
    }

    // Update inspect range circle (fade out)
    if (this.inspectRangeCircle && this.inspectRangeCircleTimer > 0) {
      this.inspectRangeCircleTimer -= delta;
      this.inspectRangeCircle.x = this.player.x;
      this.inspectRangeCircle.y = this.player.y;
      this.inspectRangeCircle.alpha = Math.max(0, this.inspectRangeCircleTimer / 1500);
      if (this.inspectRangeCircleTimer <= 0) {
        this.inspectRangeCircle.destroy();
        this.inspectRangeCircle = null;
      }
    }

    // Update all active mining lasers + entity miner positions (multi-target)
    if (this.isPlayerMining) {
      for (const [entityId, target] of this.miningTargets) {
        const entity = this.findMinableEntity(target.kind, entityId);
        if (entity) {
          if (entity.updateMinerPosition) entity.updateMinerPosition('local', this.player.x, this.player.y);
          target.laser.updateSource({ x: this.player.x, y: this.player.y, rotation: this.player.rotation });
          target.laser.updateTarget(entity.x, entity.y);
          target.laser.update(delta);
        }
      }
    }

    // Update remote miners' positions on their asteroids (for spark direction + laser beam)
    for (const [minerId, asteroidId] of this.remoteMinerAsteroids) {
      const asteroid = this.asteroids.get(asteroidId);
      if (!asteroid) continue;

      const remotePlayer = this.remotePlayers.get(minerId);
      if (remotePlayer) {
        asteroid.updateMinerPosition(minerId, remotePlayer.x, remotePlayer.y);
        // Update remote mining laser beam position
        const laser = this.remoteMiningLasers.get(minerId);
        if (laser) {
          laser.updateSource({ x: remotePlayer.x, y: remotePlayer.y, rotation: remotePlayer.rotation });
          laser.updateTarget(asteroid.x, asteroid.y);
          laser.update(delta);
        }
      }
    }

    // Update combat laser (continuous beam weapon)
    this.updateCombatLaser(delta);

    // Adaptive interpolation delay: adjust based on smoothed RTT (every 2s)
    if (time - this._lastInterpolationUpdate > this.INTERPOLATION_UPDATE_INTERVAL) {
      this._lastInterpolationUpdate = time;
      const rtt = networkManager.getSmoothedRTT();
      if (rtt > 0) {
        // Target: 1.5x RTT, clamped [50, 250], gives minimal-yet-safe buffer
        const targetDelay = Math.max(50, Math.min(250, Math.round(rtt * 1.5)));
        for (const [, rp] of this.remotePlayers) {
          rp.setInterpolationDelay(targetDelay);
        }
        // Drones are remote entities fed at the server tick rate too — give them
        // the same RTT-derived buffer instead of the hardcoded 100ms default, so
        // dogfighting a drone on a low-latency link doesn't feel sluggish.
        for (const [, drone] of this.drones) {
          drone.setInterpolationDelay(targetDelay);
        }
      }
    }

    this.updateAsteroidAOI(time);
    this.updateDebugGrid();
    this.logEntityCountsIfNeeded(time);
    this.emitUIData();
    this.input.update();
  }

  private checkProjectileAsteroidCollisions(): void {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const proj = this.projectiles[i];
      for (const [asteroidId, asteroid] of this.asteroids) {
        const dx = proj.x - asteroid.x;
        const dy = proj.y - asteroid.y;
        if (Math.sqrt(dx * dx + dy * dy) < asteroid.getRadius()) {
          this.createHitParticles(proj.x, proj.y, 'laser');
          const destroyed = asteroid.applyDamage(proj.damage, false);
          proj.destroy();
          this.projectiles.splice(i, 1);
          if (destroyed) this.handleLocalAsteroidDestruction(asteroidId, false);
          break;
        }
      }
    }
  }

  // Client-side collision detection with LOCAL PREDICTION
  // Server is authoritative, but client applies bounce locally to prevent "passing through"
  // Server correction will reconcile via smooth lerp
  private checkPlayerAsteroidCollisions(): void {
    if (this.player.isDead() || this.playerInSafeZone) return;
    if (this.isMiningAnimation) return; // No collision during harvestable dock animation
    if (this.player.warpPhase === 'calculating' || this.player.warpPhase === 'traveling') return;
    const now = Date.now();

    for (const [asteroidId, asteroid] of this.asteroids) {
      // Check for collision
      const result = this.player.calculateCollisionWith(asteroid, false, true);
      if (!result) continue;

      // During stun: only separate (prevent overlap), don't apply bounce velocity or re-stun
      // Server handles stun-period collisions via stunSourceId tracking
      if (this.player.isStunned) {
        this.player.separateFrom(asteroid.x, asteroid.y, asteroid.collisionRadius);
        continue;
      }

      // Check cooldown (only for stun/camera shake, not separation)
      const cooldownKey = `player:asteroid:${asteroidId}`;
      const lastCollision = this.collisionCooldowns.get(cooldownKey) || 0;
      const onCooldown = now - lastCollision < this.COLLISION_COOLDOWN_MS;

      // LOCAL PREDICTION: Apply bounce/separation immediately to prevent passing through
      // Server will override with authoritative correction via onPlayerHit
      if (ANALYSIS_MODE) {
        clientLog(`[COLLISION:CLIENT_PREDICT] player <-> asteroid:${asteroidId} ` + JSON.stringify({
          ts: now,
          impact: result.impactSpeed.toFixed(1),
          prePos: { x: this.player.x.toFixed(1), y: this.player.y.toFixed(1) },
          preVel: { vx: this.player.velocityX.toFixed(1), vy: this.player.velocityY.toFixed(1) },
          newVel: { vx: result.velocityA.x.toFixed(1), vy: result.velocityA.y.toFixed(1) },
          sep: { x: result.separationA.x.toFixed(1), y: result.separationA.y.toFixed(1) },
          stunned: this.player.isStunned,
        }));
      }

      this.player.applyLocalCollisionPrediction(
        result.velocityA,
        result.separationA
      );

      // Only apply stun and camera shake if not on cooldown (prevents spam)
      // But always apply the velocity/separation above
      if (!onCooldown) {
        this.collisionCooldowns.set(cooldownKey, now);

        // Visual: Apply stun indicator
        this.player.applyStun();

        // Visual: Camera shake for immediate feedback
        const asteroidVelocity = asteroid.getVelocity();
        this.player.handleAsteroidCollision(asteroidVelocity, asteroid.mass);
      }
    }
  }


  // Client-side collision detection with LOCAL PREDICTION
  // Server is authoritative, but client applies bounce locally to prevent "passing through"
  private checkPlayerRemotePlayerCollisions(): void {
    if (this.player.isDead() || this.playerInSafeZone) return;
    if (this.player.warpPhase === 'calculating' || this.player.warpPhase === 'traveling') return;
    const now = Date.now();

    for (const [sessionId, remotePlayer] of this.remotePlayers) {
      if (remotePlayer.isDead() || remotePlayer.isDocked || remotePlayer.isManagingPlanet) continue;
      // Skip collision if remote player is in safe zone
      if (this.isInAnySafeZone(remotePlayer.x, remotePlayer.y)) continue;

      // Check for collision
      const result = this.player.calculateCollisionWith(remotePlayer, false, false);
      if (!result) continue;

      // During stun: only separate (prevent overlap), don't apply bounce velocity or re-stun
      if (this.player.isStunned) {
        this.player.separateFrom(remotePlayer.x, remotePlayer.y, remotePlayer.collisionRadius);
        continue;
      }

      // Check cooldown (only for stun/camera shake, not separation)
      const cooldownKey = `player:remoteplayer:${sessionId}`;
      const lastCollision = this.collisionCooldowns.get(cooldownKey) || 0;
      const onCooldown = now - lastCollision < this.COLLISION_COOLDOWN_MS;

      // LOCAL PREDICTION: Apply bounce/separation immediately to prevent passing through
      // Server will override with authoritative correction via onPlayerHit
      this.player.applyLocalCollisionPrediction(
        result.velocityA,
        result.separationA
      );

      // Only apply stun and camera shake if not on cooldown (prevents spam)
      if (!onCooldown) {
        this.collisionCooldowns.set(cooldownKey, now);

        // Visual: Apply stun indicator
        this.player.applyStun();

        // Visual: Camera shake for immediate feedback
        const otherVelocity = { x: remotePlayer.velocityX, y: remotePlayer.velocityY };
        this.player.handlePlayerCollision(otherVelocity);
      }
    }
  }

  private updateNetworkProjectiles(delta: number): void {
    this.networkProjectiles = this.networkProjectiles.filter((p) => {
      const alive = p.update(delta);
      if (!alive) p.destroy();
      return alive;
    });
  }

  // Check if bot/remote player projectiles hit the local player
  private checkNetworkProjectilePlayerCollisions(): void {
    if (this.player.isDead() || this.isPlayerDocked || this.isPlayerManagingPlanet || this.playerInSafeZone) return;

    // Build world-space collision circles for the local player
    const playerWorldCircles = this.player.collisionShapes.length > 0
      ? this.player.collisionShapes.map(c => transformCircleToWorld(c, this.player.x, this.player.y, this.player.rotation))
      : [{ x: this.player.x, y: this.player.y, r: this.player.collisionRadius }];

    for (let i = this.networkProjectiles.length - 1; i >= 0; i--) {
      const proj = this.networkProjectiles[i];

      // Skip projectiles from the local player (shouldn't happen, but safety check)
      if (proj.ownerId === networkManager.getLocalSessionId()) continue;

      // Check natural shield bubble first, then fall back to ship body
      const shieldActive = (this.player as any)._shieldActive as boolean | undefined;
      const shieldRadius = ((this.player as any)._shieldRadius as number | undefined) ?? 80;
      const shieldRaisedAt = (this.player as any)._shieldRaisedAt as number | undefined;
      const shieldExpansionMs = ((this.player as any)._shieldExpansionMs as number | undefined) ?? 300;
      let hitShield = false;
      let effectiveShieldR = 0;

      if (shieldActive) {
        const sinceRaise = shieldRaisedAt ? Date.now() - shieldRaisedAt : shieldExpansionMs;
        const t = Math.min(1, sinceRaise / Math.max(1, shieldExpansionMs));
        effectiveShieldR = shieldRadius * t;
        if (effectiveShieldR > 0) {
          const dx = proj.x - this.player.x;
          const dy = proj.y - this.player.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist <= effectiveShieldR + proj.collisionRadius) {
            // Check coverage arc — is the hit within the shield's angular range?
            const coverageDeg = this.player.shipClass?.stats?.shieldCoverage ?? 360;
            if (coverageDeg >= 360) {
              hitShield = true;
            } else {
              const noseAngle = (this.player.angle - 90) * Math.PI / 180;
              // dx/dy is direction from ship to projectile = impact point angle
              const impactAngle = Math.atan2(dy, dx);
              let diff = impactAngle - noseAngle;
              diff = ((diff + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
              if (Math.abs(diff) <= (coverageDeg / 2) * (Math.PI / 180)) {
                hitShield = true;
              }
            }
          }
        }
      }

      if (!hitShield && !pointHitsCompoundShape(proj.x, proj.y, proj.collisionRadius, playerWorldCircles)) continue;

      const hitAngle = Math.atan2(this.player.y - proj.y, this.player.x - proj.x);
      if (hitShield) {
        // Place particles at the shield surface
        const impactX = this.player.x - Math.cos(hitAngle) * effectiveShieldR;
        const impactY = this.player.y - Math.sin(hitAngle) * effectiveShieldR;
        this.createHitParticles(impactX, impactY, 'laser');
        this.player.spawnShieldHitEffect(hitAngle);
      } else {
        this.createHitParticles(proj.x, proj.y, 'laser');
      }
      proj.destroy();
      this.networkProjectiles.splice(i, 1);
      // Actual damage is handled by server 'playerHit' message
    }
  }

  // Check if player's projectiles hit remote players (PvP)
  private checkProjectileRemotePlayerCollisions(): void {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const proj = this.projectiles[i];

      for (const [sessionId, remotePlayer] of this.remotePlayers) {
        if (remotePlayer.isDead() || remotePlayer.isDocked || remotePlayer.isManagingPlanet) continue;

        // If target has natural shield active, check shield bubble radius first
        const shieldR = remotePlayer.getEffectiveShieldRadius();
        let hitShield = false;
        if (shieldR > 0) {
          const dx = proj.x - remotePlayer.x;
          const dy = proj.y - remotePlayer.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist <= shieldR + proj.collisionRadius) {
            // Check coverage arc
            const coverageDeg = remotePlayer.shipClass?.stats?.shieldCoverage ?? 360;
            if (coverageDeg >= 360) {
              hitShield = true;
            } else {
              const noseAngle = remotePlayer.rotation - Math.PI / 2;
              // dx/dy is direction from ship to projectile = impact point angle
              const impactAngle = Math.atan2(dy, dx);
              let diff = impactAngle - noseAngle;
              diff = ((diff + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
              if (Math.abs(diff) <= (coverageDeg / 2) * (Math.PI / 180)) {
                hitShield = true;
              }
            }
          }
        }

        // Fall back to compound ship shapes if no shield hit
        if (!hitShield) {
          const rpWorldCircles = remotePlayer.collisionShapes.length > 0
            ? remotePlayer.collisionShapes.map(c => transformCircleToWorld(c, remotePlayer.x, remotePlayer.y, remotePlayer.rotation))
            : [{ x: remotePlayer.x, y: remotePlayer.y, r: remotePlayer.collisionRadius }];
          if (!pointHitsCompoundShape(proj.x, proj.y, proj.collisionRadius, rpWorldCircles)) continue;
        }

        const hitAngle = Math.atan2(remotePlayer.y - proj.y, remotePlayer.x - proj.x);

        // Visual feedback — place particles at shield surface if shield absorbed
        if (hitShield) {
          const impactX = remotePlayer.x - Math.cos(hitAngle) * shieldR;
          const impactY = remotePlayer.y - Math.sin(hitAngle) * shieldR;
          this.createHitParticles(impactX, impactY, 'laser');
          this.createFloatingDamage(impactX, impactY, proj.damage);
          remotePlayer.showShieldHit(hitAngle + Math.PI);
        } else {
          this.createHitParticles(proj.x, proj.y, 'laser');
          this.createFloatingDamage(proj.x, proj.y, proj.damage);
        }
        this.createFloatingHealthBar(proj.x, proj.y, remotePlayer.hull, remotePlayer.maxHull, remotePlayer.shield ?? 0, remotePlayer.maxShield ?? 0);
        remotePlayer.showDamageIndicator(hitShield || remotePlayer.shield > 0);

        // Send damage to server for validation
        networkManager.sendPlayerAttack(sessionId, proj.damage, hitAngle, proj.x, proj.y);

        // Destroy projectile
        proj.destroy();
        this.projectiles.splice(i, 1);
        break;
      }
    }
  }

  // Check if player's projectiles hit drones
  private checkProjectileDroneCollisions(): void {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const proj = this.projectiles[i];

      for (const [droneId, drone] of this.drones) {
        if (drone.hull <= 0) continue;

        const dx = proj.x - drone.x;
        const dy = proj.y - drone.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const hitRadius = drone.selectionRadius + proj.collisionRadius;

        if (dist <= hitRadius) {
          const hitAngle = Math.atan2(drone.y - proj.y, drone.x - proj.x);

          // Visual feedback
          this.createHitParticles(proj.x, proj.y, 'laser');
          this.createFloatingDamage(proj.x, proj.y, proj.damage);
          this.createFloatingHealthBar(proj.x, proj.y, drone.hull, drone.maxHull, drone.shield, drone.maxShield);

          // Send damage to server for validation
          networkManager.sendPlayerAttack(droneId, proj.damage, hitAngle, proj.x, proj.y, 'drone');

          // Destroy projectile
          proj.destroy();
          this.projectiles.splice(i, 1);
          break;
        }
      }

      // Settlement cores (static structures) — same projectile treatment as drones.
      // Skip if a drone hit above already consumed this projectile.
      if (this.projectiles[i] !== proj) continue;
      for (const [settlementId, settlement] of this.settlements) {
        if (settlement.coreHull <= 0) continue;
        const dx = proj.x - settlement.x;
        const dy = proj.y - settlement.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist <= settlement.selectionRadius + proj.collisionRadius) {
          const hitAngle = Math.atan2(settlement.y - proj.y, settlement.x - proj.x);
          this.createHitParticles(proj.x, proj.y, 'laser');
          networkManager.sendPlayerAttack(settlementId, proj.damage, hitAngle, proj.x, proj.y, 'settlement');
          proj.destroy();
          this.projectiles.splice(i, 1);
          break;
        }
      }
    }
  }

  private updateHitParticles(delta: number): void {
    for (const particle of this.activeHitParticles) {
      particle.life -= delta;
      if (particle.life <= 0) {
        this.activeHitParticles.delete(particle);
        this.hitParticlePool.release(particle);
        continue;
      }
      particle.sprite.x += particle.vx * (delta / 16.67);
      particle.sprite.y += particle.vy * (delta / 16.67);
      particle.sprite.alpha = particle.life / particle.maxLife;
    }
  }

  private updateAsteroidAOI(time: number): void {
    if (time - this.lastAOIUpdateTime < this.AOI_UPDATE_INTERVAL) return;
    this.lastAOIUpdateTime = time;

    const px = this.player.x, py = this.player.y;
    const enterR = this.AOI_RADIUS, exitR = this.AOI_RADIUS + this.AOI_HYSTERESIS;

    for (const [id, data] of this.asteroidData) {
      const distSq = (data.x - px) ** 2 + (data.y - py) ** 2;
      if (!this.asteroids.has(id)) {
        if (distSq <= enterR * enterR) {
          const asteroid = new NetworkAsteroid(data);
          asteroid.zIndex = 70;
          asteroid.setSparkParent(this.worldContainer); // For mining sparks
          this.worldContainer.addChild(asteroid);
          // Add health bar container to world (separate so it doesn't rotate with asteroid)
          const healthBar = asteroid.getHealthBarContainer();
          if (healthBar) {
            healthBar.zIndex = 100;
            this.worldContainer.addChild(healthBar);
          }
          this.asteroids.set(id, asteroid);
        }
      } else if (distSq > exitR * exitR) {
        this.asteroids.get(id)!.destroy();
        this.asteroids.delete(id);
      }
    }

    // V1 harvestables — same AOI pattern, factory function picks the right subclass
    this.updateHarvestableAOI(this.iceAsteroidData, this.iceAsteroids, (d) => new NetworkIceAsteroid(d), px, py, enterR, exitR);
    this.updateHarvestableAOI(this.gasPocketData, this.gasPockets, (d) => new NetworkGasPocket(d), px, py, enterR, exitR);
    this.updateHarvestableAOI(this.wreckageData, this.wreckages, (d) => new NetworkWreckage(d), px, py, enterR, exitR);
  }

  private updateHarvestableAOI<T extends NetworkHarvestable>(
    dataMap: Map<string, HarvestableEntityData>,
    instanceMap: Map<string, T>,
    factory: (data: HarvestableEntityData) => T,
    px: number, py: number, enterR: number, exitR: number,
  ): void {
    for (const [id, data] of dataMap) {
      const distSq = (data.x - px) ** 2 + (data.y - py) ** 2;
      if (!instanceMap.has(id)) {
        if (distSq <= enterR * enterR) {
          const entity = factory(data);
          entity.zIndex = 70;
          entity.setSparkParent(this.worldContainer);
          this.worldContainer.addChild(entity);
          const healthBar = entity.getHealthBarContainer();
          if (healthBar) {
            healthBar.zIndex = 100;
            this.worldContainer.addChild(healthBar);
          }
          instanceMap.set(id, entity);
        }
      } else if (distSq > exitR * exitR) {
        instanceMap.get(id)!.destroy();
        instanceMap.delete(id);
      }
    }
  }

  // Effects
  private createHitParticles(x: number, y: number, weaponType: 'laser' | 'kinetic' | 'missile'): void {
    const texture = this.hitParticleTextures.get(`hit-particle-${weaponType}`);
    if (!texture) return;
    const count = weaponType === 'laser' ? 8 : weaponType === 'kinetic' ? 12 : 20;

    for (let i = 0; i < count; i++) {
      const particle = this.hitParticlePool.acquire();
      particle.sprite.texture = texture;
      particle.sprite.position.set(x, y);
      particle.sprite.alpha = 1;
      particle.sprite.visible = true;

      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
      const speed = 50 + Math.random() * 100;
      particle.vx = Math.cos(angle) * speed;
      particle.vy = Math.sin(angle) * speed;
      particle.life = 300 + Math.random() * 200;
      particle.maxLife = particle.life;
      this.activeHitParticles.add(particle);
    }
  }

  private createAsteroidExplosion(x: number, y: number, resourceType: string): void {
    let color = 0xffffff;
    switch (resourceType) {
      case 'iron': color = 0x8b8b8b; break;
      case 'copper': color = 0xb87333; break;
      case 'GOLD': color = 0xffd700; break;
      case 'CRYSTAL': color = 0x00ffff; break;
    }

    for (let i = 0; i < 20; i++) {
      const particle = this.hitParticlePool.acquire();
      particle.sprite.tint = color;
      particle.sprite.position.set(x, y);
      particle.sprite.alpha = 1;
      particle.sprite.visible = true;
      const angle = (Math.PI * 2 * i) / 20;
      const speed = 50 + Math.random() * 150;
      particle.vx = Math.cos(angle) * speed;
      particle.vy = Math.sin(angle) * speed;
      particle.life = 500;
      particle.maxLife = 500;
      this.activeHitParticles.add(particle);
    }
  }

  /**
   * Create floating damage number at the specified position.
   * Number floats up and fades out over ~1 second.
   */
  private createFloatingDamage(x: number, y: number, damage: number): void {
    const text = new PixiText({
      text: Math.ceil(damage).toString(),
      style: {
        fontFamily: 'monospace',
        fontSize: 14,
        fontWeight: 'bold',
        fill: 0xffcc00, // Yellow/gold for damage
        stroke: { color: 0x000000, width: 2 },
      },
    });
    text.anchor.set(0.5, 0.5);
    text.position.set(x + (Math.random() - 0.5) * 20, y); // Slight random offset
    this.floatingTextContainer.addChild(text);

    // Animate: float up and fade out
    gsap.to(text, {
      y: y - 40,
      alpha: 0,
      duration: 0.8,
      ease: 'power2.out',
      onComplete: () => {
        text.destroy();
      },
    });
  }

  /**
   * Show a temporary floating health bar below the damage number.
   * Shows shield (cyan) + hull (red/green) as a compact bar with text.
   */
  private createFloatingHealthBar(x: number, y: number, hull: number, maxHull: number, shield: number, maxShield: number): void {
    const container = new Container();
    container.position.set(x, y + 12);
    this.floatingTextContainer.addChild(container);

    const barW = 48;
    const barH = 4;

    // Shield bar (only if target has shields)
    if (maxShield > 0) {
      const shieldBg = new Graphics();
      shieldBg.roundRect(-(barW / 2), 0, barW, barH, 1);
      shieldBg.fill({ color: 0x000000, alpha: 0.6 });
      container.addChild(shieldBg);

      const shieldFrac = Math.max(0, shield / maxShield);
      if (shieldFrac > 0) {
        const shieldFill = new Graphics();
        shieldFill.roundRect(-(barW / 2), 0, barW * shieldFrac, barH, 1);
        shieldFill.fill({ color: 0x44ccff, alpha: 0.9 });
        container.addChild(shieldFill);
      }
    }

    // Hull bar
    const hullY = maxShield > 0 ? barH + 1 : 0;
    const hullBg = new Graphics();
    hullBg.roundRect(-(barW / 2), hullY, barW, barH, 1);
    hullBg.fill({ color: 0x000000, alpha: 0.6 });
    container.addChild(hullBg);

    const hullFrac = Math.max(0, hull / maxHull);
    const hullColor = hullFrac > 0.5 ? 0x44ff44 : hullFrac > 0.25 ? 0xffaa00 : 0xff3333;
    if (hullFrac > 0) {
      const hullFill = new Graphics();
      hullFill.roundRect(-(barW / 2), hullY, barW * hullFrac, barH, 1);
      hullFill.fill({ color: hullColor, alpha: 0.9 });
      container.addChild(hullFill);
    }

    // Text label: "hull/maxHull"
    const label = new PixiText({
      text: `${Math.ceil(hull)}/${maxHull}`,
      style: { fontFamily: 'monospace', fontSize: 9, fill: 0xcccccc, stroke: { color: 0x000000, width: 1 } },
    });
    label.anchor.set(0.5, 0);
    label.y = hullY + barH + 2;
    container.addChild(label);

    gsap.to(container, {
      y: y + 12 - 25,
      alpha: 0,
      duration: 1.2,
      ease: 'power2.out',
      onComplete: () => container.destroy(),
    });
  }

  // Track floating resource text vertical offset for stacking
  private floatingResourceOffset = 0;
  private floatingResourceResetTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Create floating resource number at the specified position.
   * Shows +X resource floating up and fading out. Stacks vertically when mining continuously.
   */
  private createFloatingResource(x: number, y: number, amount: number, resourceType: string): void {
    // Format resource type for display (e.g., "iron_ore" -> "IRON")
    const displayName = resourceType.split('_')[0].toUpperCase();
    const displayAmount = Number.isInteger(amount) ? `${amount}` : amount.toFixed(1);

    const text = new PixiText({
      text: `+${displayAmount} ${displayName}`,
      style: {
        fontFamily: 'monospace',
        fontSize: 22,
        fontWeight: 'bold',
        fill: 0x44ffaa, // Cyan/green for resources
        stroke: { color: 0x000000, width: 3 },
      },
    });
    text.anchor.set(0.5, 0.5);

    // Stack texts vertically, each new one appears lower
    const startY = y - 40 - this.floatingResourceOffset;
    text.position.set(x + (Math.random() - 0.5) * 20, startY);
    this.floatingTextContainer.addChild(text);

    // Increment offset for next text, cap at reasonable amount
    this.floatingResourceOffset = Math.min(this.floatingResourceOffset + 28, 150);

    // Reset offset after mining stops (no new texts for a while)
    if (this.floatingResourceResetTimer) clearTimeout(this.floatingResourceResetTimer);
    this.floatingResourceResetTimer = setTimeout(() => {
      this.floatingResourceOffset = 0;
    }, 800);

    // Animate: float up slowly and fade out over longer duration
    gsap.to(text, {
      y: startY - 50,
      alpha: 0,
      duration: 2.0,
      ease: 'power1.out',
      onComplete: () => {
        text.destroy();
      },
    });
  }

  /**
   * Create floating reputation indicator at the specified position.
   * Shows +rep (green) or -rep (red) floating up and disappearing.
   */
  private createFloatingReputation(x: number, y: number, repChange: number): void {
    const isPositive = repChange > 0;
    const text = new PixiText({
      text: `${isPositive ? '+' : ''}${repChange} REP`,
      style: {
        fontFamily: 'monospace',
        fontSize: 16,
        fontWeight: 'bold',
        fill: isPositive ? 0x44ff44 : 0xff4444, // Green for positive, red for negative
        stroke: { color: 0x000000, width: 3 },
      },
    });
    text.anchor.set(0.5, 0.5);
    text.position.set(x, y - 30); // Start above the target
    this.floatingTextContainer.addChild(text);

    // Animate: float up and fade out (slightly longer than damage)
    gsap.to(text, {
      y: y - 80,
      alpha: 0,
      duration: 1.2,
      ease: 'power2.out',
      onComplete: () => {
        text.destroy();
      },
    });
  }

  // Helpers
  /**
   * Find a safe spawn point that doesn't collide with any obstacles.
   * If a preferred position is provided, try to use it or find nearest safe spot.
   */
  private findSafeSpawnPoint(preferredX?: number, preferredY?: number): { x: number; y: number } {
    const PLAYER_RADIUS = ENTITY_RADII.PLAYER_SHIP;
    const SAFETY_MARGIN = 20; // Extra buffer
    const CHECK_RADIUS = PLAYER_RADIUS + SAFETY_MARGIN;
    const MAX_ATTEMPTS = 50;
    const NUDGE_DISTANCE = 50; // How far to nudge away from collision

    // Start with preferred position or random near origin
    let x = preferredX ?? Math.random() * 200 - 100;
    let y = preferredY ?? Math.random() * 200 - 100;

    // Size to radius mapping for asteroids
    const sizeToRadius: Record<string, number> = { small: 20, medium: 35, large: 55, huge: 80 };

    // Check for collision at a given point
    const hasCollision = (px: number, py: number): { collision: boolean; pushX: number; pushY: number } => {
      // Check asteroids
      for (const [, data] of this.asteroidData) {
        const asteroidRadius = sizeToRadius[data.size] || 35;
        const dx = px - data.x;
        const dy = py - data.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const minDist = CHECK_RADIUS + asteroidRadius;

        if (dist < minDist) {
          // Calculate push direction (away from asteroid)
          const pushDist = minDist - dist + NUDGE_DISTANCE;
          const norm = dist > 0 ? 1 / dist : 1;
          return {
            collision: true,
            pushX: dx * norm * pushDist,
            pushY: dy * norm * pushDist,
          };
        }
      }

      // Check stations (don't spawn inside stations)
      for (const [, station] of this.stations) {
        const stationRadius = 100;
        const dx = px - station.x;
        const dy = py - station.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const minDist = CHECK_RADIUS + stationRadius;

        if (dist < minDist) {
          const pushDist = minDist - dist + NUDGE_DISTANCE;
          const norm = dist > 0 ? 1 / dist : 1;
          return {
            collision: true,
            pushX: dx * norm * pushDist,
            pushY: dy * norm * pushDist,
          };
        }
      }

      // Check other players (important for populated MMO)
      for (const [, player] of this.remotePlayers) {
        const playerRadius = ENTITY_RADII.PLAYER_SHIP;
        const pos = player.getPosition();
        const dx = px - pos.x;
        const dy = py - pos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const minDist = CHECK_RADIUS + playerRadius;

        if (dist < minDist) {
          const pushDist = minDist - dist + NUDGE_DISTANCE;
          const norm = dist > 0 ? 1 / dist : 1;
          return {
            collision: true,
            pushX: dx * norm * pushDist,
            pushY: dy * norm * pushDist,
          };
        }
      }

      return { collision: false, pushX: 0, pushY: 0 };
    };

    // Try to find a safe position
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      const result = hasCollision(x, y);

      if (!result.collision) {
        // Found a safe spot
        return { x, y };
      }

      // Push away from collision
      x += result.pushX;
      y += result.pushY;
    }

    // If still colliding after max attempts, just return current position
    // (better than infinite loop)
    console.warn('Could not find completely safe spawn point after', MAX_ATTEMPTS, 'attempts');
    return { x, y };
  }

  // Legacy method for backwards compatibility
  private findUnoccupiedSpawnPoint(): { x: number; y: number } {
    return this.findSafeSpawnPoint();
  }

  private handleLocalAsteroidDestruction(asteroidId: string, fromMining: boolean): void {
    const asteroid = this.asteroids.get(asteroidId);
    if (!asteroid) return;
    this.asteroidData.delete(asteroidId);
    asteroid.destroy();
    this.asteroids.delete(asteroidId);
    this.locallyDestroyedAsteroids.add(asteroidId);
    audioManager.playSFX('explosion_asteroid');
  }

  private sendPlayerStateToServer(time: number): void {
    if (time - this.lastSendTime < this.SEND_RATE) return;

    const x = this.player.x;
    const y = this.player.y;
    const rot = this.player.getRotation();
    const vx = this.player.velocity.x;
    const vy = this.player.velocity.y;
    const thrusting = this.player.getIsThrusting();
    const boosting = this.player.getIsBoosting();

    // Skip sending if nothing meaningful changed (position within 0.5px, rotation within 0.5deg, velocity within 0.5)
    const posDelta = Math.abs(x - this._lastSentX) + Math.abs(y - this._lastSentY);
    const velDelta = Math.abs(vx - this._lastSentVx) + Math.abs(vy - this._lastSentVy);
    const rotDelta = Math.abs(rot - this._lastSentRot);
    if (
      posDelta < 0.5 &&
      velDelta < 0.5 &&
      rotDelta < 0.5 &&
      thrusting === this._lastSentThrusting &&
      boosting === this._lastSentBoosting
    ) {
      return;
    }

    this.lastSendTime = time;
    this._lastSentX = x;
    this._lastSentY = y;
    this._lastSentRot = rot;
    this._lastSentVx = vx;
    this._lastSentVy = vy;
    this._lastSentThrusting = thrusting;
    this._lastSentBoosting = boosting;

    networkManager.sendInput(x, y, rot, vx, vy, thrusting, boosting);
  }

  // Check if a position is within any station's safe zone
  private isInAnySafeZone(x: number, y: number): boolean {
    for (const [, station] of this.stations) {
      if (station.isInSafeZone(x, y)) return true;
    }
    return false;
  }

  // Zone checks
  private checkStationZones(): void {
    let inSafeZone = false, inDockingRange = false;
    let nearest: Station | Settlement | null = null, nearestDist = Infinity;
    const px = this.player.x, py = this.player.y;

    for (const [, station] of this.stations) {
      const dist = Math.sqrt((station.x - px) ** 2 + (station.y - py) ** 2);
      if (dist < nearestDist) { nearestDist = dist; nearest = station; }
      if (station.isInSafeZone(px, py)) inSafeZone = true;
      if (station.isInDockingRange(px, py)) inDockingRange = true;
    }

    // Also check private stations for docking range (no safe zone for private stations)
    for (const [, station] of this.privateStations) {
      const dist = Math.sqrt((station.x - px) ** 2 + (station.y - py) ** 2);
      if (dist < nearestDist) { nearestDist = dist; nearest = station; }
      if (station.isInDockingRange(px, py)) inDockingRange = true;
    }

    // Settlements are dockable (you can trade with them) but are NEVER a safe zone.
    for (const [, settlement] of this.settlements) {
      const dist = Math.sqrt((settlement.x - px) ** 2 + (settlement.y - py) ** 2);
      if (dist < nearestDist) { nearestDist = dist; nearest = settlement; }
      if (settlement.isInDockingRange(px, py)) inDockingRange = true;
    }

    if (this.playerInSafeZone !== inSafeZone || this.playerInDockingRange !== inDockingRange) {
      this.playerInSafeZone = inSafeZone;
      this.playerInDockingRange = inDockingRange;
      this.nearestStation = nearest;
      this.player.inSafeZone = inSafeZone;
      this.game.events.emit('zoneChange', { inSafeZone, inDockingRange, nearestStationId: nearest?.stationId || null, nearestStationName: nearest?.stationName || null });

      // Emit dock prompt to React HUD
      emitGameEvent('dockPrompt', {
        show: inDockingRange && !this.isPlayerDocked,
        stationName: nearest?.stationName || undefined,
      });
    }
  }

  private checkNebulaZone(): void {
    const px = this.player.x, py = this.player.y;
    let inNebula = false;
    let nebulaName = '';
    for (const [, nebula] of this.nebulae) {
      // Use 50% of radius so debuff zone matches the visible cloud (PNG has transparent edges)
      const dx = px - nebula.x, dy = py - nebula.y;
      if (dx * dx + dy * dy < (nebula.nebulaRadius * 0.5) ** 2) {
        inNebula = true;
        nebulaName = nebula.nebulaName;
        break;
      }
    }
    if (inNebula !== this.playerInNebula) {
      this.playerInNebula = inNebula;
      emitGameEvent('nebulaStateChange', { isInNebula: inNebula, nebulaName });
    }
  }

  private checkDropProximity(): void {
    if (!this.player || this.player.isDead()) return;
    const px = this.player.x, py = this.player.y;
    const localId = networkManager.getLocalSessionId();
    const now = Date.now();

    let nearestId: string | null = null;
    let nearestDist = Infinity;
    let ownerOnly = false;
    let accessible = false;

    for (const [id, drop] of this.drops) {
      const dist = Math.hypot(drop.x - px, drop.y - py);
      if (dist > DROP_CONFIG.INTERACTION_RANGE) continue;
      if (dist < nearestDist) {
        nearestDist = dist;
        nearestId = id;
        const inProtectionWindow = drop.ownerSessionId && drop.ownerUntil > now;
        ownerOnly = !!inProtectionWindow;
        accessible = !inProtectionWindow || drop.ownerSessionId === localId;
      }
    }

    if (nearestId !== this.nearestDropId || ownerOnly !== this.nearestDropOwnerOnly || accessible !== this.nearestDropAccessible) {
      this.nearestDropId = nearestId;
      this.nearestDropOwnerOnly = ownerOnly;
      this.nearestDropAccessible = accessible;
      emitGameEvent('dropPrompt', {
        show: !!nearestId && !this.isPlayerDocked,
        dropId: nearestId ?? undefined,
        ownerOnly,
        canAccess: accessible,
      });
    }
  }

  private checkGatewayZones(): void {
    let inGatewayRange = false;
    let nearest: Gateway | null = null;
    let nearestDist = Infinity;
    const px = this.player.x, py = this.player.y;

    for (const [, gateway] of this.gateways) {
      const dist = gateway.getDistanceTo(px, py);
      if (dist < nearestDist) { nearestDist = dist; nearest = gateway; }
      if (gateway.isInActivationRange(px, py)) {
        inGatewayRange = true;
      }
      // Update gateway visual state
      gateway.setInRange(gateway.isInActivationRange(px, py));
    }

    if (this.playerInGatewayRange !== inGatewayRange) {
      this.playerInGatewayRange = inGatewayRange;
      this.nearestGateway = inGatewayRange ? nearest : null;

      // Emit gateway prompt to React HUD
      emitGameEvent('gatewayPrompt', {
        show: inGatewayRange && !this.isPlayerDocked,
        targetConstellationName: nearest?.targetConstellationName || undefined,
        travelCost: nearest?.travelCost || 0,
        gatewayId: nearest?.gatewayId || undefined,
      });
    }
  }

  private checkPlanetZones(): void {
    let inLandingRange = false;
    let nearest: Planet | null = null;
    let nearestDist = Infinity;
    const px = this.player.x, py = this.player.y;

    for (const [, planet] of this.planets) {
      const dist = planet.getDistanceTo(px, py);
      if (dist < nearestDist) { nearestDist = dist; nearest = planet; }
      if (planet.isInLandingRange(px, py)) {
        inLandingRange = true;
      }
    }

    if (this.playerInPlanetLandingRange !== inLandingRange) {
      this.playerInPlanetLandingRange = inLandingRange;
      this.nearestPlanet = inLandingRange ? nearest : null;

      // Emit planet landing prompt to React HUD
      emitGameEvent('planetLandingPrompt', {
        show: inLandingRange && !this.isPlayerDocked,
        planetName: nearest?.planetName || undefined,
        planetId: nearest?.planetId || undefined,
      });
    }
  }

  private checkAsteroidMiningZones(): void {
    let inMiningRange = false;
    let nearestAsteroid: NetworkAsteroid | null = null, nearestDist = Infinity;
    const px = this.player.x, py = this.player.y;

    for (const [, asteroid] of this.asteroids) {
      const asteroidRadius = asteroid.getRadius();
      const miningRange = getMiningZoneRadius(asteroidRadius);
      const dist = Math.sqrt((asteroid.x - px) ** 2 + (asteroid.y - py) ** 2);
      if (dist < miningRange && dist < nearestDist) {
        nearestDist = dist;
        nearestAsteroid = asteroid;
        inMiningRange = true;
      }
    }

    if (this.playerInMiningRange !== inMiningRange || this.nearestMinableAsteroid !== nearestAsteroid) {
      this.playerInMiningRange = inMiningRange;
      this.nearestMinableAsteroid = nearestAsteroid;
      this.game.events.emit('miningZoneChange', { inMiningRange, nearestAsteroidId: nearestAsteroid?.asteroidId || null });
      // Asteroid mining prompt removed — mining is now triggered from overview panel Mine button
    }
  }

  /** Tracks which terrain zones the player is inside and emits to HUD. */
  private lastPlayerZonesKey = '';
  private playerZoneDebugTimer = 0;
  private checkPlayerZones(): void {
    const px = this.player.x, py = this.player.y;
    const zones: Array<{ kind: string; name?: string }> = [];

    // Station safe zones
    for (const [, station] of this.stations) {
      if (station.isInSafeZone(px, py)) {
        zones.push({ kind: 'safe_zone', name: station.stationName });
      }
    }

    // Nebula
    for (const [, nebula] of this.nebulae) {
      const dx = px - nebula.x, dy = py - nebula.y;
      if (dx * dx + dy * dy < (nebula.nebulaRadius * 0.5) ** 2) {
        zones.push({ kind: 'nebula', name: nebula.nebulaName });
      }
    }

    // Asteroid zones (fields)
    for (const zone of this.asteroidZones) {
      const dx = px - zone.x, dy = py - zone.y;
      if (dx * dx + dy * dy < zone.radius * zone.radius) {
        zones.push({ kind: 'asteroid_field', name: zone.name });
      }
    }

    // Resource zones (ice, wreckage, rich asteroid, etc.)
    for (const zone of this.resourceZones) {
      const dx = px - zone.x, dy = py - zone.y;
      if (dx * dx + dy * dy < zone.radius * zone.radius) {
        zones.push({ kind: zone.kind });
      }
    }

    // Rogue settlement zones of influence (fighters patrol inside this radius).
    for (const [, s] of this.settlements) {
      const dx = px - s.x, dy = py - s.y;
      const zoi = s.zoiRadius;
      if (dx * dx + dy * dy < zoi * zoi) {
        zones.push({ kind: 'settlement_zoi' });
      }
    }

    // Build a stable key — emit on change OR every ~1s to handle late React subscribers
    const key = zones.map(z => `${z.kind}:${z.name || ''}`).join('|');
    this.playerZoneDebugTimer++;
    if (key !== this.lastPlayerZonesKey || this.playerZoneDebugTimer >= 60) {
      this.lastPlayerZonesKey = key;
      this.playerZoneDebugTimer = 0;
      emitGameEvent('playerZones', { zones });
    }
  }

  // Show a brief range circle around the player when out of mining range
  private showMiningRangeCircle(toolRange: number): void {
    // Remove existing circle if any
    if (this.miningRangeCircle) {
      this.miningRangeCircle.destroy();
      this.miningRangeCircle = null;
    }

    const circle = new Graphics();
    circle.circle(0, 0, toolRange);
    circle.stroke({ width: 1.5, color: 0xff8800, alpha: 0.6 });
    circle.x = this.player.x;
    circle.y = this.player.y;
    circle.zIndex = 5;
    this.worldContainer.addChild(circle);
    this.miningRangeCircle = circle;
    this.miningRangeCircleTimer = 1500; // Fade over 1.5s
  }

  // Show a brief range circle around the player when inspect target is out of range
  private showInspectRangeCircle(scanRange: number): void {
    if (this.inspectRangeCircle) {
      this.inspectRangeCircle.destroy();
      this.inspectRangeCircle = null;
    }

    const circle = new Graphics();
    circle.circle(0, 0, scanRange);
    circle.stroke({ width: 1.5, color: 0x22c55e, alpha: 0.5 });
    circle.x = this.player.x;
    circle.y = this.player.y;
    circle.zIndex = 5;
    this.worldContainer.addChild(circle);
    this.inspectRangeCircle = circle;
    this.inspectRangeCircleTimer = 1500; // Fade over 1.5s
  }

  // Inspect target — range check with live positions, then send or show circle
  private handleInspectRequest(targetId: string): void {
    if (!this.connected || !this.player) return;
    this._inspectOpen = true;

    // (was tutorial 'scan' trigger — moved to handleRadarScanRequest where
    // the *active* radar scan is fired. Inspect ≠ scan.)

    // Players & drones → server inspect (has equipment/cargo data)
    const rp = this.remotePlayers.get(targetId);
    if (rp) {
      networkManager.sendInspectTarget(targetId);
      return;
    }
    const drone = this.drones.get(targetId);
    if (drone) {
      networkManager.sendInspectTarget(targetId);
      return;
    }

    // Everything else → build inspect data client-side
    const station = this.stations.get(targetId);
    if (station) {
      emitGameEvent('inspectResult', {
        success: true, targetId, targetName: station.stationName,
        shipClass: 'station', shipName: 'Station',
        hull: 0, maxHull: 0, reputation: 0, equipment: [],
      });
      return;
    }
    const asteroid = this.asteroids.get(targetId);
    if (asteroid) {
      const ad = this.asteroidData.get(targetId);
      // Compute mining time estimate
      const toolTier = this.getEquippedMiningToolTier();
      const miningPower = this.getEquippedMiningToolPower();
      const resourceTier = RESOURCE_TIERS[ad?.resourceType as ResourceType] ?? 1;
      let miningTimeEstimate: number | undefined;
      if (toolTier > 0 && miningPower > 0 && ad) {
        const tierMult = computeMiningTierMultiplier(toolTier, resourceTier);
        const dmgPerTick = miningPower * tierMult;
        miningTimeEstimate = Math.ceil(ad.health / (dmgPerTick * 2)); // 2 ticks/sec
      }
      emitGameEvent('inspectResult', {
        success: true, targetId,
        targetName: `Asteroid (${ad?.resourceType ?? 'unknown'})`,
        shipClass: 'asteroid', shipName: ad?.size ?? 'unknown',
        hull: ad?.health ?? 0, maxHull: ad?.maxHealth ?? 0,
        reputation: 0, equipment: [],
        entityType: 'asteroid',
        resourceType: ad?.resourceType,
        resourceAmount: ad?.resourceAmount ?? 0,
        resourceTier,
        miningTimeEstimate,
      });
      return;
    }
    // Harvestables (ice / gas / wreckage) — reuse the asteroid inspect UI so they
    // surface resource type, yield, health, and mining-time estimate.
    const harvData = this.iceAsteroidData.get(targetId)
      ?? this.gasPocketData.get(targetId)
      ?? this.wreckageData.get(targetId);
    if (harvData) {
      const kindLabel = this.iceAsteroidData.has(targetId) ? 'Ice'
        : this.gasPocketData.has(targetId) ? 'Gas Pocket'
        : 'Wreckage';
      const toolTier = this.getEquippedMiningToolTier();
      const miningPower = this.getEquippedMiningToolPower();
      const resourceTier = harvData.resourceTier ?? 1;
      let miningTimeEstimate: number | undefined;
      if (toolTier > 0 && miningPower > 0) {
        const tierMult = computeMiningTierMultiplier(toolTier, resourceTier);
        const dmgPerTick = miningPower * tierMult;
        miningTimeEstimate = Math.ceil(harvData.health / (dmgPerTick * 2));
      }
      emitGameEvent('inspectResult', {
        success: true, targetId,
        targetName: `${kindLabel} (${harvData.resourceType})`,
        shipClass: 'asteroid', shipName: harvData.size,
        hull: harvData.health, maxHull: harvData.maxHealth,
        reputation: 0, equipment: [],
        entityType: 'asteroid',
        resourceType: harvData.resourceType,
        resourceAmount: harvData.resourceAmount,
        resourceTier,
        miningTimeEstimate,
      });
      return;
    }
    const planet = this.planets.get(targetId);
    if (planet) {
      emitGameEvent('inspectResult', {
        success: true, targetId,
        targetName: (planet as any).planetName || 'Planet',
        shipClass: 'planet', shipName: (planet as any).planetType || 'Planet',
        hull: 0, maxHull: 0, reputation: 0, equipment: [],
      });
      return;
    }
    const sun = this.suns.get(targetId);
    if (sun) {
      emitGameEvent('inspectResult', {
        success: true, targetId,
        targetName: (sun as any).starType || 'Star',
        shipClass: 'sun', shipName: 'Star',
        hull: 0, maxHull: 0, reputation: 0, equipment: [],
      });
      return;
    }
    const gateway = this.gateways.get(targetId);
    if (gateway) {
      const gd = this.gatewayData.get(targetId);
      emitGameEvent('inspectResult', {
        success: true, targetId,
        targetName: `Gateway → ${gd?.targetConstellationName ?? '?'}`,
        shipClass: 'gateway', shipName: 'Gateway',
        hull: 0, maxHull: 0, reputation: 0, equipment: [],
      });
      return;
    }
  }

  // Mining — no dock animation, start visuals immediately. Ship stays controllable.
  private startMiningDirect(data: {
    asteroidId: string;
    asteroidX: number;
    asteroidY: number;
    asteroidRadius: number;
    miningSlot: number;
    resourceType: string;
    maxHealth: number;
    currentHealth: number;
    kind?: string;
  }): void {
    // Hide mining prompt
    emitGameEvent('miningPrompt', { show: false });
    // Start visuals immediately — player keeps control
    this.startMiningVisuals(data);
  }

  private startMiningVisuals(data: { asteroidId: string; asteroidX: number; asteroidY: number; asteroidRadius: number; miningSlot: number; resourceType: string; maxHealth: number; currentHealth: number; kind?: string }): void {
    const kind = data.kind || 'asteroid';

    // Don't add duplicate
    if (this.miningTargets.has(data.asteroidId)) return;

    // Try to register as miner on the entity (for health bar display)
    const entity = this.findMinableEntity(kind, data.asteroidId);
    if (entity && entity.addMiner) {
      entity.addMiner('local', this.player.x, this.player.y);
    }

    // Create the visible laser beam + sparks
    const laser = new MiningLaser(
      { x: this.player.x, y: this.player.y, rotation: this.player.rotation },
      data.asteroidX,
      data.asteroidY,
      data.asteroidRadius,
      data.resourceType,
    );
    laser.setParticleParent(this.worldContainer);
    this.worldContainer.addChild(laser);

    // Add to multi-target map
    this.miningTargets.set(data.asteroidId, { kind, laser });
    this.isPlayerMining = true;
    if (this.miningTargets.size === 1) audioManager.playLoopSFX('mining_loop');
  }

  /** Stop mining visuals. If entityId given, stop just that target; otherwise stop all. */
  private stopMiningVisuals(entityId?: string): void {
    if (entityId) {
      // Stop a specific target
      const target = this.miningTargets.get(entityId);
      if (target) {
        target.laser.stopEmitting();
        target.laser.destroy();
        const entity = this.findMinableEntity(target.kind, entityId);
        if (entity && entity.removeMiner) entity.removeMiner('local');
        this.miningTargets.delete(entityId);
      }
    } else {
      // Stop ALL targets
      for (const [id, target] of this.miningTargets) {
        target.laser.stopEmitting();
        target.laser.destroy();
        const entity = this.findMinableEntity(target.kind, id);
        if (entity && entity.removeMiner) entity.removeMiner('local');
      }
      this.miningTargets.clear();
    }

    // Update state
    if (this.miningTargets.size === 0) {
      audioManager.stopLoopSFX('mining_loop');
      this.isPlayerMining = false;
      this.miningFractionalDisplay = 0;
      this.player.inputEnabled = true;
    }
  }

  // V1: lookup helper for harvestable entities by client kind + id
  private getHarvestableEntity(kind: HarvestableClientKind, id: string): NetworkHarvestable | null {
    if (kind === 'ice_asteroid') return this.iceAsteroids.get(id) || null;
    if (kind === 'gas_pocket')   return this.gasPockets.get(id) || null;
    if (kind === 'wreckage')     return this.wreckages.get(id) || null;
    return null;
  }

  /** Reset the mining session inactivity timer. After 60s of no mining, session totals clear. */
  private resetMiningSessionTimer(): void {
    if (this.miningSessionResetTimer) clearTimeout(this.miningSessionResetTimer);
    this.miningSessionResetTimer = setTimeout(() => {
      this.miningSessionResources = {};
      this.miningSessionStartTime = 0;
      this.miningSessionResetTimer = null;
    }, this.MINING_SESSION_TIMEOUT_MS);
  }

  /** Unified lookup for any minable entity by kind + id. Returns entity with addMiner/removeMiner or null. */
  private findMinableEntity(kind: string, id: string): any {
    if (kind === 'asteroid') return this.asteroids.get(id) || null;
    return this.getHarvestableEntity(kind as HarvestableClientKind, id);
  }

  /** Resolve a minable entity by id across every collection (kind unknown — drones don't send it). */
  private findMinableById(id: string): any {
    return this.asteroids.get(id) || this.iceAsteroids.get(id) || this.gasPockets.get(id)
      || this.wreckages.get(id) || null;
  }

  /**
   * Draw one drone's mining beam into the shared `droneMiningBeams` Graphics. Two thin
   * strokes (resource color + white core), NO particles — deliberately cheap so hundreds of
   * mining drones cost ~nothing. `pulse` is a single per-frame shimmer shared by all beams.
   */
  private drawDroneMiningBeam(drone: Drone, entity: any, pulse: number): void {
    const g = this.droneMiningBeams;
    const color = RESOURCE_COLORS[entity.resourceType as string] || 0x00ccff;
    // Aim at the asteroid surface, not its centre.
    const dx = entity.x - drone.x, dy = entity.y - drone.y;
    const ang = Math.atan2(dy, dx);
    const r = entity.collisionRadius ?? 20;
    const hx = entity.x - Math.cos(ang) * r;
    const hy = entity.y - Math.sin(ang) * r;

    g.moveTo(drone.x, drone.y);
    g.lineTo(hx, hy);
    g.stroke({ width: 2.5, color, alpha: 0.55 * pulse });
    g.moveTo(drone.x, drone.y);
    g.lineTo(hx, hy);
    g.stroke({ width: 1, color: 0xffffff, alpha: 0.85 * pulse });
  }

  /**
   * Render a drone's warp animation (same as the player's) while it's warping. Lazy: the
   * WarpVisual + its Graphics exist only during a warp phase and are torn down afterward,
   * so idle/non-warping drones cost nothing.
   */
  private updateDroneWarpVisual(drone: Drone, visible: boolean): void {
    const warping = visible && (drone.warpPhase === 'calculating' || drone.warpPhase === 'traveling');
    if (!warping) { this.removeDroneWarpVisual(drone.id); return; }

    let viz = this.droneWarpVisuals.get(drone.id);
    if (!viz) {
      viz = new WarpVisual();
      this.worldContainer.addChild(viz.graphics);
      this.droneWarpVisuals.set(drone.id, viz);
    }
    viz.update(drone.x, drone.y, drone.velocityX, drone.velocityY, drone.warpPhase);
  }

  private removeDroneWarpVisual(droneId: string): void {
    const viz = this.droneWarpVisuals.get(droneId);
    if (viz) {
      viz.destroy();
      this.droneWarpVisuals.delete(droneId);
    }
  }

  private handleMiningTick(data: {
    asteroidId: string;
    damage: number;
    health: number;
    maxHealth: number;
    resourcesGained: number;
    resourceType: string;
    fractionalProgress?: number;
    pendingProgress?: number;
  }): void {
    // Look up which target this tick is for
    const target = this.miningTargets.get(data.asteroidId);
    if (!target) return;

    const entity = this.findMinableEntity(target.kind, data.asteroidId);
    if (entity) {
      if (entity.updateFromServer) {
        entity.updateFromServer({ health: data.health, maxHealth: data.maxHealth });
      }

      // Drive the per-asteroid mining progress bar (0..1 toward next +1)
      if (entity.updateMiningProgress) {
        entity.updateMiningProgress(data.pendingProgress ?? 0, data.resourceType);
      }

      // Show floating text only for whole units (what actually goes to inventory)
      if (data.resourcesGained > 0) {
        target.laser.triggerHitBurst();
        this.createFloatingResource(entity.x, entity.y, data.resourcesGained, data.resourceType);

        // Track mining session totals
        if (!this.miningSessionStartTime) this.miningSessionStartTime = Date.now();
        this.miningSessionResources[data.resourceType] = (this.miningSessionResources[data.resourceType] || 0) + data.resourcesGained;
        this.resetMiningSessionTimer();

        // The server persists each whole unit immediately (see
        // ConstellationRoom.processMiningTicks). Notify React so any open inventory
        // panel can re-fetch instead of showing stale data.
        emitGameEvent('cargoChanged', { reason: 'mining' });
      }
    }
  }

  private handleMiningAsteroidExplosion(data: { asteroidId: string; pushVelocityX: number; pushVelocityY: number }): void {
    // Check if this was one of the player's mining targets
    const target = this.miningTargets.get(data.asteroidId);
    const wasPlayerMiningThis = !!target;

    // Trigger explosion animation on the entity
    if (target) {
      const entity = this.findMinableEntity(target.kind, data.asteroidId);
      if (entity && entity.explode) entity.explode();
    }
    audioManager.playSFX('explosion_asteroid');
    audioManager.playSFX('mining_complete');

    // Stop just this target's mining visuals (not all)
    this.stopMiningVisuals(data.asteroidId);

    // If this was the player's mining target, apply push velocity from server
    if (wasPlayerMiningThis && this.player) {
      // Apply server push velocity
      this.player.velocity.x = data.pushVelocityX;
      this.player.velocity.y = data.pushVelocityY;

      // Keep player input disabled briefly during push
      this.player.inputEnabled = false;

      // Re-enable input after a short delay
      setTimeout(() => {
        if (this.player) {
          this.player.inputEnabled = true;
        }
      }, 800);
    }

    // Show notification
    emitGameEvent('notification', {
      type: 'success',
      message: 'Asteroid depleted!',
      duration: 2000,
    });
  }

  private handleRemotePlayerMiningStarted(data: { playerId: string; asteroidId: string; miningSlot: number }): void {
    const remotePlayer = this.remotePlayers.get(data.playerId);
    const asteroid = this.asteroids.get(data.asteroidId);
    if (!remotePlayer || !asteroid) return;
    // Add miner to asteroid - sparks will be rendered from asteroid surface toward miner
    asteroid.addMiner(data.playerId, remotePlayer.x, remotePlayer.y);
    // Track which asteroid this player is mining for position updates
    this.remoteMinerAsteroids.set(data.playerId, data.asteroidId);

    // Create visible mining laser beam from remote player to asteroid
    const existingLaser = this.remoteMiningLasers.get(data.playerId);
    if (existingLaser) { existingLaser.stopEmitting(); existingLaser.destroy(); }
    const laser = new MiningLaser(
      { x: remotePlayer.x, y: remotePlayer.y, rotation: remotePlayer.rotation },
      asteroid.x, asteroid.y,
      asteroid.collisionRadius ?? 20,
      asteroid.resourceType ?? 'iron',
    );
    laser.setParticleParent(this.worldContainer);
    this.worldContainer.addChild(laser);
    this.remoteMiningLasers.set(data.playerId, laser);
  }

  private handleRemotePlayerMiningStopped(data: { playerId: string; asteroidId: string }): void {
    const asteroid = this.asteroids.get(data.asteroidId);
    if (asteroid) {
      asteroid.removeMiner(data.playerId);
    }
    this.remoteMinerAsteroids.delete(data.playerId);
    // Remove mining laser beam
    const laser = this.remoteMiningLasers.get(data.playerId);
    if (laser) {
      laser.stopEmitting();
      laser.destroy();
      this.remoteMiningLasers.delete(data.playerId);
    }
  }

  // Docking — fade+scale disappear animation, then confirm to server
  private startDockingAnimation(data: { stationId: string; stationName: string; dockPosition: number; targetX: number; targetY: number; targetRotation: number; animationDuration: number }): void {
    this.isDockingAnimation = true;
    this.dockingStationId = data.stationId;
    this.player.inputEnabled = false;
    this.player.velocity.x = 0;
    this.player.velocity.y = 0;

    this.playShipDisappearAnimation(() => {
      // Snap to dock target (server expects us there)
      this.player.x = data.targetX;
      this.player.y = data.targetY;
      networkManager.sendDockingComplete();
    });
  }

  /** Shared fade-out + scale-down animation for entering stations/planets. */
  private playShipDisappearAnimation(onComplete: () => void): void {
    this.player.alpha = 1;
    this.player.scale.set(1);

    tween({
      targets: this.player,
      alpha: 0,
      duration: 500,
      ease: 'Sine.easeIn',
    });

    tween({
      targets: this.player.scale,
      x: 0.3,
      y: 0.3,
      duration: 500,
      ease: 'Sine.easeIn',
      onComplete: () => {
        this.player.visible = false;
        this.player.alpha = 1;
        this.player.scale.set(1);
        onComplete();
      },
    });
  }

  /** Shared fade-in + scale-up animation for leaving stations/planets. */
  private playShipAppearAnimation(): void {
    this.player.alpha = 0;
    this.player.scale.set(0.3);
    this.player.visible = true;

    tween({
      targets: this.player,
      alpha: 1,
      duration: 400,
      ease: 'Sine.easeOut',
    });

    tween({
      targets: this.player.scale,
      x: 1,
      y: 1,
      duration: 400,
      ease: 'Back.easeOut',
    });
  }

  // Gateway travel
  private handleGatewayTravelStart(data: { targetConstellationX: number; targetConstellationY: number; targetConstellationName: string; spawnX: number; spawnY: number }): void {
    // Disable player input during transition
    this.player.inputEnabled = false;
    this.player.velocity.x = 0;
    this.player.velocity.y = 0;

    // Clear scanned radar entities when leaving constellation
    emitGameEvent('radarClearEntities', undefined);

    // Dismiss the gateway prompt and reset tracking so the next zone check
    // in the new constellation emits a fresh event (player typically spawns next to
    // the reverse gateway, so playerInGatewayRange would otherwise stay true
    // and the stale prompt with the old target constellation name would persist).
    emitGameEvent('gatewayPrompt', { show: false });
    this.playerInGatewayRange = false;
    this.nearestGateway = null;

    // Emit transition event to React UI for fade effect
    emitGameEvent('gatewayTransition', {
      show: true,
      targetConstellationName: data.targetConstellationName,
    });

    // Disconnect and reconnect to new constellation after short delay for fade
    setTimeout(async () => {
      await networkManager.disconnect();

      // Clear local entity caches
      this.clearAllEntities();

      // Connect to new constellation
      const success = await networkManager.connect(data.targetConstellationX, data.targetConstellationY);
      if (!success) {
        emitGameEvent('notification', { type: 'error', message: 'Failed to connect to target constellation' });
      }

      // Hide transition overlay after connection
      setTimeout(() => {
        emitGameEvent('gatewayTransition', { show: false });
        this.player.inputEnabled = true;
      }, 500);
    }, 500); // 500ms fade out
  }

  private clearAllEntities(): void {
    // Clear all entity maps and destroy visuals
    for (const [, player] of this.remotePlayers) player.destroy();
    this.remotePlayers.clear();

    for (const [, asteroid] of this.asteroids) asteroid.destroy();
    this.asteroids.clear();
    this.asteroidData.clear();

    // V1 harvestables
    for (const [, e] of this.iceAsteroids) e.destroy();
    this.iceAsteroids.clear();
    this.iceAsteroidData.clear();
    for (const [, e] of this.gasPockets) e.destroy();
    this.gasPockets.clear();
    this.gasPocketData.clear();
    for (const [, e] of this.wreckages) e.destroy();
    this.wreckages.clear();
    this.wreckageData.clear();
    this.locallyDestroyedHarvestables.clear();

    for (const [, drop] of this.drops) drop.destroy();
    for (const [, drone] of this.drones) drone.destroy();
    this.drones.clear();
    this.drops.clear();
    this.droneMiningBeams.clear();
    for (const [, viz] of this.droneWarpVisuals) viz.destroy();
    this.droneWarpVisuals.clear();

    for (const [, station] of this.stations) station.destroy();
    this.stations.clear();
    this.stationData.clear();

    for (const [, gateway] of this.gateways) gateway.destroy();
    this.gateways.clear();
    this.gatewayData.clear();

    for (const [, beacon] of this.beacons) beacon.destroy();
    this.beacons.clear();

    for (const [, sun] of this.suns) sun.destroy();
    this.suns.clear();

    for (const [, nebula] of this.nebulae) nebula.destroy();
    this.nebulae.clear();

    for (const [, planet] of this.planets) planet.destroy();
    this.planets.clear();
    this.planetData.clear();
    this.asteroidZones = [];
    this.resourceZones = [];

    // Reset zone tracking
    this.playerInSafeZone = false;
    this.playerInDockingRange = false;
    this.nearestStation = null;
    this.playerInGatewayRange = false;
    this.nearestGateway = null;
    this.playerInMiningRange = false;
    this.nearestMinableAsteroid = null;
  }

  // Event handlers
  private setDebugMode(enabled: boolean): void {
    if (!this.isActive) { this.pendingDebugMode = enabled; return; }
    this.debugMode = enabled;
    if (!enabled && this.debugGridGraphics) { this.debugGridGraphics.destroy(); this.debugGridGraphics = undefined; }
  }

  private sendPlayerData(): void {
    this.game.events.emit('playerData', { shipName: this.player.shipClass.name, shipClassId: this.player.shipClass.id, slots: this.player.slots, shipColor: this.player.getShipColor() });
  }

  private setShipColor(color: number): void { this.player.setShipColor(color); }
  private setPlayerInput(enabled: boolean): void { this.player.inputEnabled = enabled; }

  private respawnPlayer(): void {
    // Find nearest station to respawn at
    let nearestStation: { x: number; y: number } | null = null;
    let nearestDist = Infinity;
    for (const [, station] of this.stations) {
      const dx = station.x - this.player.x;
      const dy = station.y - this.player.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearestStation = { x: station.x, y: station.y };
      }
    }

    // Spawn near the station (offset by station safe zone radius + some buffer)
    let preferredX = 0;
    let preferredY = 0;
    if (nearestStation) {
      // Spawn 400 units away from station center (just outside safe zone)
      const angle = Math.random() * Math.PI * 2;
      preferredX = nearestStation.x + Math.cos(angle) * 400;
      preferredY = nearestStation.y + Math.sin(angle) * 400;
    }

    // Find safe spawn point that avoids collisions
    const spawnPoint = this.findSafeSpawnPoint(preferredX, preferredY);
    if (this.connected) networkManager.sendRespawn(spawnPoint.x, spawnPoint.y);
    this.player.respawn(spawnPoint.x, spawnPoint.y);
    emitGameEvent('notification', { message: 'Ship reconstructed. Fly safe!', type: 'success' });
  }

  private handleFireWeapon(data: { x: number; y: number; angle: number; weapon: Weapon; playerId: string; shipVelocityX?: number; shipVelocityY?: number; targetId?: string; targetType?: string }): void {
    const projectile = Projectile.fromWeapon(
      data.x,
      data.y,
      data.angle,
      data.weapon,
      data.playerId,
      data.shipVelocityX ?? 0,
      data.shipVelocityY ?? 0,
      data.targetId,
      data.targetType as 'bot' | 'player' | 'asteroid' | undefined
    );

    // Play weapon fire SFX
    if (data.weapon.weaponType === 'kinetic') audioManager.playSFX('weapon_kinetic');
    else if (data.weapon.weaponType === 'missile') audioManager.playSFX('weapon_missile');

    // For missiles, set up target position getter for tracking
    if (data.weapon.weaponType === 'missile' && data.targetId && data.targetType) {
      projectile.setTargetPositionGetter((id: string, type: string) => {
        return this.getEntityPosition(id, type);
      });
    }

    projectile.zIndex = 95;
    this.worldContainer.addChild(projectile);
    this.projectiles.push(projectile);

    // Send fire event to server for visual sync to other players
    // Only send for laser/kinetic - missiles are handled separately
    if (this.connected && (data.weapon.weaponType === 'laser' || data.weapon.weaponType === 'kinetic')) {
      const weaponType = data.weapon.weaponType;
      const shipColor = this.player?.getShipColor() ?? 0x20a5a6;
      // Convert angle from degrees to radians for network message
      const angleRadians = data.angle * (Math.PI / 180);
      networkManager.sendFire(
        data.x,
        data.y,
        angleRadians,
        data.weapon.stats.damage,
        data.weapon.stats.projectileSpeed,
        weaponType,
        shipColor
      );
    }
  }

  // Start laser beam fire
  private handleStartLaserFire(data: { weapon: Weapon; playerId: string }): void {
    if (this.playerInSafeZone) return;

    // Destroy existing laser if any
    if (this.combatLaser) {
      this.combatLaser.destroy();
      this.combatLaser = null;
    }

    // Create new combat laser
    this.combatLaser = new CombatLaser(
      {
        x: this.player.x,
        y: this.player.y,
        rotation: this.player.angle,
        noseOffset: this.player.noseOffset,
      },
      data.weapon
    );
    this.combatLaser.setParticleParent(this.worldContainer);
    this.worldContainer.addChild(this.combatLaser);
    this.combatLaser.activate();
    this.isLaserActive = true;
    audioManager.playLoopSFX('weapon_laser');
  }

  // Stop laser beam fire
  private handleStopLaserFire(): void {
    if (this.combatLaser) {
      this.combatLaser.deactivate();
      this.isLaserActive = false;
      audioManager.stopLoopSFX('weapon_laser');
    }
  }

  // Get entity position by ID and type (used by missiles for tracking)
  private getEntityPosition(id: string, type: string): { x: number; y: number } | null {
    if (type === 'player') {
      const player = this.remotePlayers.get(id);
      if (player) {
        const pos = player.getPosition();
        return { x: pos.x, y: pos.y };
      }
      return null;
    } else if (type === 'asteroid') {
      const asteroid = this.asteroids.get(id);
      return asteroid ? { x: asteroid.x, y: asteroid.y } : null;
    } else if (type === 'drone') {
      const drone = this.drones.get(id);
      return drone ? { x: drone.x, y: drone.y } : null;
    }
    return null;
  }

  // Update combat laser (continuous beam weapon)
  private updateCombatLaser(delta: number): void {
    if (!this.combatLaser || !this.player) return;

    // Update source position to follow player
    this.combatLaser.updateSource({
      x: this.player.x,
      y: this.player.y,
      rotation: this.player.angle,
      noseOffset: this.player.noseOffset,
    });

    // Update selected target for missile tracking
    this.updatePlayerTargetSelection();

    if (this.isLaserActive && !this.playerInSafeZone) {
      // Get pointer world position for beam direction
      const pointerWorldX = this.camera.getWorldX(this.input.pointer.x);
      const pointerWorldY = this.camera.getWorldY(this.input.pointer.y);

      const result = this.combatLaser.update(delta, pointerWorldX, pointerWorldY);

      if (result && result.isActive) {
        // Check for laser beam hits (this may call setHitPoint on the laser)
        this.checkLaserBeamHits(delta, result.beamStartX, result.beamStartY, result.beamEndX, result.beamEndY);
        // Draw beam after hit checking (so visual endpoint is correct)
        this.combatLaser.drawBeamVisual(delta);
      }
    } else {
      // Not firing - just update for cooldown/particles
      this.combatLaser.update(delta, this.player.x, this.player.y);
    }
  }

  // Check if laser beam hits any targets
  private checkLaserBeamHits(delta: number, startX: number, startY: number, endX: number, endY: number): void {
    if (!this.combatLaser) return;

    // Find closest hit along beam path
    let closestHit: { dist: number; x: number; y: number; type: 'player' | 'asteroid' | 'drone' | 'settlement'; id: string; targetX: number; targetY: number; radius: number } | null = null;

    // Check remote players (PvP)
    for (const [sessionId, remotePlayer] of this.remotePlayers) {
      if (remotePlayer.isDead() || remotePlayer.isDocked || remotePlayer.isManagingPlanet) continue;
      const pos = remotePlayer.getPosition();

      const hitPoint = this.getLineCircleIntersection(startX, startY, endX, endY, pos.x, pos.y, ENTITY_RADII.PLAYER_SHIP);
      if (hitPoint) {
        const dist = Math.sqrt((hitPoint.x - startX) ** 2 + (hitPoint.y - startY) ** 2);
        if (!closestHit || dist < closestHit.dist) {
          closestHit = { dist, x: hitPoint.x, y: hitPoint.y, type: 'player', id: sessionId, targetX: pos.x, targetY: pos.y, radius: ENTITY_RADII.PLAYER_SHIP };
        }
      }
    }

    // Check drones
    for (const [droneId, drone] of this.drones) {
      if (drone.hull <= 0) continue;
      const hitPoint = this.getLineCircleIntersection(startX, startY, endX, endY, drone.x, drone.y, drone.selectionRadius);
      if (hitPoint) {
        const dist = Math.sqrt((hitPoint.x - startX) ** 2 + (hitPoint.y - startY) ** 2);
        if (!closestHit || dist < closestHit.dist) {
          closestHit = { dist, x: hitPoint.x, y: hitPoint.y, type: 'drone', id: droneId, targetX: drone.x, targetY: drone.y, radius: drone.selectionRadius };
        }
      }
    }

    // Check asteroids
    for (const [asteroidId, asteroid] of this.asteroids) {
      const radius = asteroid.getRadius();
      const hitPoint = this.getLineCircleIntersection(startX, startY, endX, endY, asteroid.x, asteroid.y, radius);
      if (hitPoint) {
        const dist = Math.sqrt((hitPoint.x - startX) ** 2 + (hitPoint.y - startY) ** 2);
        if (!closestHit || dist < closestHit.dist) {
          closestHit = { dist, x: hitPoint.x, y: hitPoint.y, type: 'asteroid', id: asteroidId, targetX: asteroid.x, targetY: asteroid.y, radius };
        }
      }
    }

    // Check settlement cores (static structures)
    for (const [settlementId, settlement] of this.settlements) {
      if (settlement.coreHull <= 0) continue;
      const hitPoint = this.getLineCircleIntersection(startX, startY, endX, endY, settlement.x, settlement.y, settlement.selectionRadius);
      if (hitPoint) {
        const dist = Math.sqrt((hitPoint.x - startX) ** 2 + (hitPoint.y - startY) ** 2);
        if (!closestHit || dist < closestHit.dist) {
          closestHit = { dist, x: hitPoint.x, y: hitPoint.y, type: 'settlement', id: settlementId, targetX: settlement.x, targetY: settlement.y, radius: settlement.selectionRadius };
        }
      }
    }

    // Process hit if found
    if (closestHit) {
      // Set visual hit point on laser
      this.combatLaser.setHitPoint(closestHit.x, closestHit.y);

      // Register damage (CombatLaser tracks 'bot' for any non-player non-asteroid target)
      const damage = this.combatLaser.registerHit({
        id: closestHit.id,
        type: closestHit.type === 'drone' || closestHit.type === 'settlement' ? 'bot' : closestHit.type,
        x: closestHit.targetX,
        y: closestHit.targetY,
        radius: closestHit.radius,
      }, delta);

      // Handle target-specific logic
      if (closestHit.type === 'player') {
        this.sendLaserDamage('player', closestHit.id, damage);
      } else if (closestHit.type === 'drone') {
        this.sendLaserDamage('drone', closestHit.id, damage);
      } else if (closestHit.type === 'settlement') {
        this.sendLaserDamage('settlement', closestHit.id, damage);
      } else if (closestHit.type === 'asteroid') {
        const asteroid = this.asteroids.get(closestHit.id);
        if (asteroid) {
          const destroyed = asteroid.applyDamage(damage, false);
          if (destroyed) {
            this.handleLocalAsteroidDestruction(closestHit.id, false);
          }
        }
      }

      this.combatLaser.triggerHitBurst();
      return;
    }

    // No hit - clear target tracking (resets damage multiplier)
    this.combatLaser.clearTarget();
  }

  // Get intersection point of line with circle (returns closest point or null)
  private getLineCircleIntersection(
    x1: number, y1: number, x2: number, y2: number,
    cx: number, cy: number, r: number
  ): { x: number; y: number } | null {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const fx = x1 - cx;
    const fy = y1 - cy;

    const a = dx * dx + dy * dy;
    const b = 2 * (fx * dx + fy * dy);
    const c = fx * fx + fy * fy - r * r;

    const discriminant = b * b - 4 * a * c;
    if (discriminant < 0) return null;

    const sqrtDisc = Math.sqrt(discriminant);
    const t1 = (-b - sqrtDisc) / (2 * a);
    const t2 = (-b + sqrtDisc) / (2 * a);

    // Use closest intersection that's on the line segment (t in [0, 1])
    let t = -1;
    if (t1 >= 0 && t1 <= 1) t = t1;
    else if (t2 >= 0 && t2 <= 1) t = t2;

    if (t < 0) return null;

    return {
      x: x1 + t * dx,
      y: y1 + t * dy,
    };
  }

  // Line-circle intersection test for laser beam
  private lineIntersectsCircle(
    x1: number, y1: number, x2: number, y2: number,
    cx: number, cy: number, r: number
  ): boolean {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const fx = x1 - cx;
    const fy = y1 - cy;

    const a = dx * dx + dy * dy;
    const b = 2 * (fx * dx + fy * dy);
    const c = fx * fx + fy * fy - r * r;

    const discriminant = b * b - 4 * a * c;
    if (discriminant < 0) return false;

    const sqrtDisc = Math.sqrt(discriminant);
    const t1 = (-b - sqrtDisc) / (2 * a);
    const t2 = (-b + sqrtDisc) / (2 * a);

    // Check if intersection is within line segment (0 <= t <= 1)
    return (t1 >= 0 && t1 <= 1) || (t2 >= 0 && t2 <= 1);
  }

  // Rate-limited laser damage sending with accumulation
  private sendLaserDamage(targetType: 'player' | 'drone' | 'settlement', targetId: string, damage: number): void {
    // If target changed, send accumulated damage for previous target immediately
    if (this.lastLaserTargetId && (this.lastLaserTargetId !== targetId || this.lastLaserTargetType !== targetType)) {
      const accumulatedDamage = this.accumulatedLaserDamage.get(this.lastLaserTargetId) ?? 0;
      if (accumulatedDamage > 0 && this.lastLaserTargetType) {
        networkManager.sendPlayerAttack(this.lastLaserTargetId, accumulatedDamage, 0, this.player.x, this.player.y, this.lastLaserTargetType);
      }
      this.accumulatedLaserDamage.clear();
    }

    // Update current target
    this.lastLaserTargetId = targetId;
    this.lastLaserTargetType = targetType;

    // Accumulate damage
    const currentAccumulated = this.accumulatedLaserDamage.get(targetId) ?? 0;
    this.accumulatedLaserDamage.set(targetId, currentAccumulated + damage);

    // Check if it's time to send
    const now = Date.now();
    if (now - this.lastLaserDamageSend < this.LASER_DAMAGE_SEND_INTERVAL) return;

    this.lastLaserDamageSend = now;

    // Send accumulated damage
    const totalDamage = this.accumulatedLaserDamage.get(targetId) ?? 0;
    if (totalDamage > 0) {
      // Get target position for floating damage
      let targetX = this.player.x;
      let targetY = this.player.y;
      if (targetType === 'player') {
        const remotePlayer = this.remotePlayers.get(targetId);
        if (remotePlayer) { targetX = remotePlayer.x; targetY = remotePlayer.y; }
      } else if (targetType === 'drone') {
        const drone = this.drones.get(targetId);
        if (drone) { targetX = drone.x; targetY = drone.y; }
      } else if (targetType === 'settlement') {
        const s = this.settlements.get(targetId);
        if (s) { targetX = s.x; targetY = s.y; }
      }
      networkManager.sendPlayerAttack(targetId, totalDamage, 0, this.player.x, this.player.y, targetType);
      // Show floating damage + health bar at target position
      this.createFloatingDamage(targetX, targetY, totalDamage);
      if (targetType === 'player') {
        const rp = this.remotePlayers.get(targetId);
        if (rp) this.createFloatingHealthBar(targetX, targetY, rp.hull, rp.maxHull, rp.shield ?? 0, rp.maxShield ?? 0);
      } else if (targetType === 'drone') {
        const dr = this.drones.get(targetId);
        if (dr) this.createFloatingHealthBar(targetX, targetY, dr.hull, dr.maxHull, dr.shield, dr.maxShield);
      }
      this.accumulatedLaserDamage.set(targetId, 0);
    }
  }

  // Update player's selected target for missile tracking
  private updatePlayerTargetSelection(): void {
    if (!this.player) return;

    if (this.selectedEntity && this.selectedEntity.id) {
      const type = this.selectedEntity.type;
      if (type === 'player' || type === 'asteroid' || type === 'drone') {
        this.player.setSelectedTarget({
          id: this.selectedEntity.id,
          type,
          x: this.selectedEntity.x,
          y: this.selectedEntity.y,
        });
      } else {
        this.player.setSelectedTarget(null);
      }
    } else {
      this.player.setSelectedTarget(null);
    }
  }

  private handleDockRequest(data: { stationId: string; stationName: string }): void {
    if (this.connected) networkManager.sendDock(data.stationId);
  }

  private handleUndockRequest(data: { stationId: string }): void {
    if (this.connected) networkManager.sendUndock();
  }

  private handleMiningRequest(data: { asteroidId: string; kind?: string }): void {
    if (!this.connected || !this.player || this.isMiningAnimation) return;
    const kind = data.kind || 'asteroid';
    // Verify entity exists in the correct map
    const entity = this.findMinableEntity(kind, data.asteroidId);
    if (!entity) return;
    networkManager.sendStartMining(data.asteroidId, kind);
  }

  private handleStopMiningRequest(): void {
    if (this.connected && (this.isPlayerMining || this.isMiningAnimation)) networkManager.sendStopMining();
  }

  private handleEquipmentChanged(data: { slots: import('@astra/shared').SlotDefinition[] }): void {
    if (!this.player) return;
    this.player.slots = data.slots;
    this.player.recalculateStats();
  }

  private handleSelectionClick(data: { worldX: number; worldY: number; screenX: number; screenY: number }): void {
    const { worldX: cx, worldY: cy } = data;
    const CLICK_R = 50;

    // Convert click coords to scenery-space (compensate for parallax offset)
    const parallaxDelta = 1 - SCENERY_PARALLAX;
    const sx = cx - this.camera.scrollX * parallaxDelta;
    const sy = cy - this.camera.scrollY * parallaxDelta;

    for (const [id, station] of this.stations) {
      if (Math.sqrt((sx - station.x) ** 2 + (sy - station.y) ** 2) < 100) {
        this.selectEntity({ type: 'station', id, name: station.stationName, x: station.x, y: station.y, data: { stationId: id } });
        return;
      }
    }

    for (const [id, player] of this.remotePlayers) {
      const pos = player.getPosition();
      if (Math.sqrt((cx - pos.x) ** 2 + (cy - pos.y) ** 2) < CLICK_R) {
        this.selectEntity({ type: 'player', id, name: player.sessionId, x: pos.x, y: pos.y, data: { sessionId: player.sessionId } });
        return;
      }
    }

    const sizeToRadius: Record<string, number> = { small: 20, medium: 35, large: 55, huge: 80 };
    const harvestableSizeToRadius: Record<string, number> = { small: 80, medium: 120, large: 180, huge: 250 };
    for (const [id, ad] of this.asteroidData) {
      if (Math.sqrt((cx - ad.x) ** 2 + (cy - ad.y) ** 2) < Math.max(sizeToRadius[ad.size] || 30, CLICK_R)) {
        this.selectEntity({ type: 'asteroid', id, name: `Asteroid (${ad.resourceType})`, x: ad.x, y: ad.y, data: { resourceType: ad.resourceType, health: ad.health, maxHealth: ad.maxHealth, size: ad.size } });
        return;
      }
    }

    // V1 harvestables — same hit-detection pattern as asteroids
    const tryHarvestable = (
      type: 'ice_asteroid' | 'gas_pocket' | 'wreckage',
      labelFn: (r: string) => string,
      dataMap: Map<string, HarvestableEntityData>,
    ): boolean => {
      for (const [id, d] of dataMap) {
        const hitR = Math.max(harvestableSizeToRadius[d.size] || 80, CLICK_R);
        if (Math.sqrt((cx - d.x) ** 2 + (cy - d.y) ** 2) < hitR) {
          this.selectEntity({
            type, id,
            name: labelFn(d.resourceType),
            x: d.x, y: d.y,
            data: { resourceType: d.resourceType, resourceTier: d.resourceTier, health: d.health, maxHealth: d.maxHealth, size: d.size },
          });
          return true;
        }
      }
      return false;
    };
    if (tryHarvestable('ice_asteroid', r => `Ice (${r})`,   this.iceAsteroidData)) return;
    if (tryHarvestable('gas_pocket',   r => `Gas (${r})`,   this.gasPocketData))   return;
    if (tryHarvestable('wreckage',     r => `Wreck (${r})`, this.wreckageData))    return;

    for (const [id, drop] of this.drops) {
      if (Math.sqrt((cx - drop.x) ** 2 + (cy - drop.y) ** 2) < CLICK_R) {
        this.selectEntity({ type: 'drop', id, name: 'Loot Drop', x: drop.x, y: drop.y });
        return;
      }
    }

    for (const [id, drone] of this.drones) {
      if (Math.sqrt((cx - drone.x) ** 2 + (cy - drone.y) ** 2) < CLICK_R) {
        const info = drone.getSelectionInfo();
        this.selectEntity({ type: 'drone', id, name: info.name, x: drone.x, y: drone.y, data: info.data });
        return;
      }
    }

    for (const [id, settlement] of this.settlements) {
      if (Math.sqrt((cx - settlement.x) ** 2 + (cy - settlement.y) ** 2) < settlement.selectionRadius + 20) {
        const info = settlement.getSelectionInfo();
        this.selectEntity({ type: 'settlement', id, name: info.name, x: settlement.x, y: settlement.y, data: info.data });
        return;
      }
    }

    // Gateways
    for (const [id, gateway] of this.gateways) {
      if (Math.sqrt((cx - gateway.x) ** 2 + (cy - gateway.y) ** 2) < 80) {
        const gd = this.gatewayData.get(id);
        this.selectEntity({ type: 'gateway', id, name: `Gate → ${gd?.targetConstellationName || 'Unknown'}`, x: gateway.x, y: gateway.y });
        return;
      }
    }

    // Planets - use visual radius for hit detection (scenery layer — use sx/sy)
    for (const [id, planet] of this.planets) {
      const pd = this.planetData.get(id);
      const radius = planet.visualRadius || 100;
      if (Math.sqrt((sx - planet.x) ** 2 + (sy - planet.y) ** 2) < radius + 20) {
        this.selectEntity({ type: 'planet', id, name: pd?.name || 'Planet', x: planet.x, y: planet.y, data: { planetType: pd?.planetType } });
        return;
      }
    }

    // Suns - large hit detection radius (scenery layer — use sx/sy)
    for (const [id, sun] of this.suns) {
      const radius = sun.sunRadius || 200;
      if (Math.sqrt((sx - sun.x) ** 2 + (sy - sun.y) ** 2) < radius + 50) {
        this.selectEntity({ type: 'sun', id, name: sun.sunName || 'Star', x: sun.x, y: sun.y, data: { starType: sun.starType } });
        return;
      }
    }

    // Nebulae are scenery — not selectable by click (radius too large, blocks deselection)

    this.selectEntity({ type: 'empty', name: 'Empty Space', x: cx, y: cy, data: { coordinates: `${Math.round(cx)}, ${Math.round(cy)}` } });
  }

  private getDummyCount(): number {
    let count = 0;
    // Count dummies from both drones (escorts) and players (main dummies)
    for (const d of this.drones.values()) if (d.isDummy) count++;
    for (const rp of this.remotePlayers.values()) if (rp.isDummy) count++;
    return count;
  }

  private clearSelectionIfEntityDestroyed(entityId: string): void {
    if (this.selectedEntity && this.selectedEntity.id === entityId) {
      this.selectedEntity = null;
      this.selectionIndicator.clear();
      emitGameEvent('selectionChanged', null);
    }
  }

  private selectEntity(entity: SelectionEntity): void {
    this.selectedEntity = entity;
    this.updateSelectionIndicator();
    this.game.events.emit('selectionChanged', entity);

    // Also emit to React via game events
    if (entity.type !== 'empty' && entity.id) {
      emitGameEvent('selectionChanged', {
        id: entity.id,
        type: entity.type,
        name: entity.name || '',
        x: entity.x,
        y: entity.y,
      });
      // (tutorial v2 removed the select trigger — Continue is manual)
    } else {
      emitGameEvent('selectionChanged', null);
    }
  }

  private handleSelectEntityById(data: { id: string; type: string }): void {
    const { id, type } = data;
    if (type === 'station') {
      const station = this.stations.get(id);
      if (station) this.selectEntity({ type: 'station', id, name: station.stationName, x: station.x, y: station.y, data: { stationId: id } });
    } else if (type === 'player') {
      const player = this.remotePlayers.get(id);
      if (player) { const pos = player.getPosition(); this.selectEntity({ type: 'player', id, name: player.shipClass.name, x: pos.x, y: pos.y }); }
    } else if (type === 'asteroid') {
      const ad = this.asteroidData.get(id);
      if (ad) this.selectEntity({ type: 'asteroid', id, name: `Asteroid (${ad.resourceType})`, x: ad.x, y: ad.y, data: { resourceType: ad.resourceType, health: ad.health, maxHealth: ad.maxHealth, size: ad.size } });
    } else if (type === 'gateway') {
      const gw = this.gatewayData.get(id);
      if (gw) this.selectEntity({ type: 'gateway', id, name: `Gate → ${gw.targetConstellationName}`, x: gw.x, y: gw.y });
    }
  }

  private updateSelectionIndicator(): void {
    this.selectionIndicator.clear();
    if (!this.selectedEntity || this.selectedEntity.type === 'empty') return;

    let x = this.selectedEntity.x, y = this.selectedEntity.y;
    if (this.selectedEntity.id) {
      if (this.selectedEntity.type === 'player') { const p = this.remotePlayers.get(this.selectedEntity.id); if (p) { const pos = p.getPosition(); x = pos.x; y = pos.y; } }
      else if (this.selectedEntity.type === 'station') { const s = this.stations.get(this.selectedEntity.id); if (s) { x = s.x; y = s.y; } }
      else if (this.selectedEntity.type === 'gateway') { const g = this.gateways.get(this.selectedEntity.id); if (g) { x = g.x; y = g.y; } }
      else if (this.selectedEntity.type === 'planet') { const p = this.planets.get(this.selectedEntity.id); if (p) { x = p.x; y = p.y; } }
      else if (this.selectedEntity.type === 'sun') { const s = this.suns.get(this.selectedEntity.id); if (s) { x = s.x; y = s.y; } }
      else if (this.selectedEntity.type === 'nebula') { const n = this.nebulae.get(this.selectedEntity.id); if (n) { x = n.x; y = n.y; } }
      else if (this.selectedEntity.type === 'asteroid') { const a = this.asteroids.get(this.selectedEntity.id); if (a) { x = a.x; y = a.y; } }
      else if (this.selectedEntity.type === 'ice_asteroid') { const e = this.iceAsteroidData.get(this.selectedEntity.id); if (e) { x = e.x; y = e.y; } }
      else if (this.selectedEntity.type === 'gas_pocket')   { const e = this.gasPocketData.get(this.selectedEntity.id);   if (e) { x = e.x; y = e.y; } }
      else if (this.selectedEntity.type === 'wreckage')     { const e = this.wreckageData.get(this.selectedEntity.id);     if (e) { x = e.x; y = e.y; } }
      else if (this.selectedEntity.type === 'drop') { const d = this.drops.get(this.selectedEntity.id); if (d) { x = d.x; y = d.y; } }
      else if (this.selectedEntity.type === 'drone') { const d = this.drones.get(this.selectedEntity.id); if (d) { x = d.x; y = d.y; } }
      else if (this.selectedEntity.type === 'settlement') { const s = this.settlements.get(this.selectedEntity.id); if (s) { x = s.x; y = s.y; } }
    }

    // Offset for scenery entities (rendered in parallax container, indicator drawn in worldContainer)
    const sceneryTypes = new Set(['station', 'planet', 'sun', 'nebula']);
    if (sceneryTypes.has(this.selectedEntity.type)) {
      const parallaxDelta = 1 - SCENERY_PARALLAX;
      x += this.camera.scrollX * parallaxDelta;
      y += this.camera.scrollY * parallaxDelta;
    }

    // Determine radius based on entity type - generous padding to fully engulf
    const sizeToRadius: Record<string, number> = { small: 25, medium: 45, large: 65, huge: 95 };
    let radius: number;
    if (this.selectedEntity.type === 'station') {
      radius = 140; // Stations are large
    } else if (this.selectedEntity.type === 'gateway') {
      radius = 100; // Gateways are medium-large
    } else if (this.selectedEntity.type === 'asteroid') {
      radius = (sizeToRadius[this.selectedEntity.data?.size as string] || 40) + 20;
    } else if (
      this.selectedEntity.type === 'ice_asteroid' ||
      this.selectedEntity.type === 'gas_pocket' ||
      this.selectedEntity.type === 'wreckage'
    ) {
      const hRadius: Record<string, number> = { small: 80, medium: 120, large: 180, huge: 250 };
      radius = (hRadius[this.selectedEntity.data?.size as string] || 80) + 20;
    } else if (this.selectedEntity.type === 'player') {
      radius = 50; // Ships
    } else if (this.selectedEntity.type === 'planet') {
      const pd = this.planetData.get(this.selectedEntity.id!);
      radius = (pd?.radius || 100) + 30;
    } else if (this.selectedEntity.type === 'sun') {
      const sun = this.suns.get(this.selectedEntity.id!);
      radius = (sun?.sunRadius || 200) + 50;
    } else if (this.selectedEntity.type === 'nebula') {
      const neb = this.nebulae.get(this.selectedEntity.id!);
      radius = Math.min((neb?.nebulaRadius || 500), 300); // Cap radius for display
    } else if (this.selectedEntity.type === 'drone') {
      radius = 14; // Drones are small ships
    } else if (this.selectedEntity.type === 'settlement') {
      radius = 470; // Settlement nest structure (matches selectionRadius)
    } else {
      radius = 45;
    }

    const cornerLength = Math.max(16, radius * 0.3); // Corner bracket length
    const padding = 8; // Extra padding beyond radius

    // Color based on entity type
    let color = 0x00ffff; // Default cyan
    if (this.selectedEntity.type === 'player') color = 0x20a5a6; // Teal for players
    else if (this.selectedEntity.type === 'station') color = 0x22c55e; // Green for stations
    else if (this.selectedEntity.type === 'gateway') color = 0x00dddd; // Cyan for gateways
    else if (this.selectedEntity.type === 'planet') color = 0x8844ff; // Purple for planets
    else if (this.selectedEntity.type === 'sun') color = 0xffcc00; // Yellow for suns
    else if (this.selectedEntity.type === 'nebula') color = 0xff66cc; // Pink for nebulae
    else if (this.selectedEntity.type === 'ice_asteroid') color = 0x88ddff;
    else if (this.selectedEntity.type === 'gas_pocket') color = 0xbb99ff;
    else if (this.selectedEntity.type === 'wreckage') color = 0xcd7f32;

    const bo = radius + padding;
    const bs = cornerLength;

    // Draw thicker, more visible corner brackets
    this.selectionIndicator.setStrokeStyle({ width: 3, color, alpha: 1 });

    // Top-left corner
    this.selectionIndicator.moveTo(x - bo, y - bo + bs).lineTo(x - bo, y - bo).lineTo(x - bo + bs, y - bo).stroke();
    // Top-right corner
    this.selectionIndicator.moveTo(x + bo - bs, y - bo).lineTo(x + bo, y - bo).lineTo(x + bo, y - bo + bs).stroke();
    // Bottom-left corner
    this.selectionIndicator.moveTo(x - bo, y + bo - bs).lineTo(x - bo, y + bo).lineTo(x - bo + bs, y + bo).stroke();
    // Bottom-right corner
    this.selectionIndicator.moveTo(x + bo - bs, y + bo).lineTo(x + bo, y + bo).lineTo(x + bo, y + bo - bs).stroke();

    // Add subtle pulsing ring for better visibility
    const pulse = 0.4 + Math.sin(performance.now() / 400) * 0.2;
    this.selectionIndicator.setStrokeStyle({ width: 2, color, alpha: pulse });
    this.selectionIndicator.circle(x, y, bo - 4).stroke();
  }

  // Resolve live position + radius for any entity by ID
  private resolveEntityPosAndRadius(id: string): { x: number; y: number; r: number } | null {
    const rp = this.remotePlayers.get(id);
    if (rp) { const p = rp.getPosition(); return { x: p.x, y: p.y, r: 30 }; }
    const dr = this.drones.get(id);
    if (dr) return { x: dr.x, y: dr.y, r: 14 };
    const st = this.stations.get(id);
    if (st) return { x: st.x, y: st.y, r: 100 };
    const ast = this.asteroids.get(id);
    if (ast) return { x: ast.x, y: ast.y, r: ast.getRadius() };
    const gw = this.gateways.get(id);
    if (gw) return { x: gw.x, y: gw.y, r: 60 };
    const pl = this.planets.get(id);
    if (pl) return { x: pl.x, y: pl.y, r: (pl as any).planetRadius || 80 };
    const sn = this.suns.get(id);
    if (sn) return { x: sn.x, y: sn.y, r: sn.sunRadius || 150 };
    const dp = this.drops.get(id);
    if (dp) return { x: dp.x, y: dp.y, r: 15 };
    const nb = this.nebulae.get(id);
    if (nb) return { x: nb.x, y: nb.y, r: Math.min(nb.nebulaRadius || 500, 200) };
    const ia = this.iceAsteroids.get(id);
    if (ia) return { x: ia.x, y: ia.y, r: 50 };
    const gp = this.gasPockets.get(id);
    if (gp) return { x: gp.x, y: gp.y, r: 50 };
    const wr = this.wreckages.get(id);
    if (wr) return { x: wr.x, y: wr.y, r: 50 };
    const bc = this.beacons.get(id);
    if (bc) return { x: bc.x, y: bc.y, r: 20 };
    const ps = this.privateStations.get(id);
    if (ps) return { x: ps.x, y: ps.y, r: 60 };
    return null;
  }

  private updateScanOverlays(): void {
    this.scanOverlay.clear();

    if (this.scannedEntityIds.size === 0) {
      for (const [, label] of this.scanLabels) label.destroy();
      this.scanLabels.clear();
      return;
    }

    const now = performance.now();
    const scanColor = 0x22c55e;
    const activeIds = new Set<string>();

    for (const id of this.scannedEntityIds) {
      const info = this.resolveEntityPosAndRadius(id);
      if (!info) continue;
      if (!this.camera.isVisible(info.x, info.y, info.r + 50)) continue;

      activeIds.add(id);
      const { x, y, r } = info;

      // 1s scan animation: expanding ring that fades out
      const animStart = this.scanAnimTimers.get(id);
      if (animStart !== undefined) {
        const elapsed = now - animStart;
        if (elapsed < 1000) {
          const t = elapsed / 1000; // 0→1
          const ringRadius = r * (0.5 + t * 1.0); // expand from 50% to 150% of entity radius
          const ringAlpha = (1 - t) * 0.6; // fade out
          this.scanOverlay.circle(x, y, ringRadius);
          this.scanOverlay.stroke({ width: 2, color: scanColor, alpha: ringAlpha });
          // Second ring slightly behind
          if (t > 0.15) {
            const t2 = t - 0.15;
            const r2 = r * (0.5 + t2 * 1.0);
            const a2 = (1 - t2) * 0.3;
            this.scanOverlay.circle(x, y, r2);
            this.scanOverlay.stroke({ width: 1, color: scanColor, alpha: a2 });
          }
        } else {
          this.scanAnimTimers.delete(id);
        }
      }

      // Persistent "SCANNED" label
      let label = this.scanLabels.get(id);
      if (!label) {
        label = new PixiText({ text: 'SCANNED', style: { fontSize: 9, fill: scanColor, fontFamily: 'monospace' } });
        label.anchor.set(0.5, 1);
        label.zIndex = 998;
        this.worldContainer.addChild(label);
        this.scanLabels.set(id, label);
      }
      label.x = x;
      label.y = y - r - 8;
      label.alpha = 0.6;
    }

    // Remove labels for entities no longer scanned or gone
    for (const [id, label] of this.scanLabels) {
      if (!activeIds.has(id)) {
        label.destroy();
        this.scanLabels.delete(id);
      }
    }
  }

  private updateWaypointMarker(data: { x: number; y: number; name: string; distance: number; showWorldMarker?: boolean } | null): void {
    if (!data) { if (this.waypointMarker) this.waypointMarker.visible = false; return; }
    if (!this.waypointMarker) {
      this.waypointMarker = new Container();
      this.waypointMarker.zIndex = 50;
      this.worldContainer.addChild(this.waypointMarker);
      this.waypointGraphics = new Graphics();
      this.waypointMarker.addChild(this.waypointGraphics);
      // Add distance text label
      const textObj = new PixiText({
        text: '',
        style: { fontFamily: 'monospace', fontSize: 11, fill: 0xdd5639, stroke: { color: 0x000000, width: 3 } },
      });
      textObj.anchor.set(0.5, 0);
      textObj.position.set(0, 20);
      this.waypointMarker.addChild(textObj);
      this.waypointText = textObj;
    }
    this.waypointMarker.position.set(data.x, data.y);
    this.waypointMarker.visible = data.showWorldMarker === true;
    if (!data.showWorldMarker) return;
    this.waypointGraphics!.clear();

    const pulse = 0.85 + Math.sin(performance.now() / 300) * 0.15;
    const zoom = this.camera.zoom;
    const waypointColor = 0xdd5639; // Orange
    const waypointBright = 0xff7a5a; // Brighter orange for core

    // Zoom-based glow intensity (stronger when zoomed out)
    const ZOOM_THRESHOLD = 0.3;
    const ZOOM_FULL_EFFECT = 0.15;
    const zoomFactor = zoom < ZOOM_THRESHOLD
      ? Math.min(1, (ZOOM_THRESHOLD - zoom) / (ZOOM_THRESHOLD - ZOOM_FULL_EFFECT))
      : 0;

    // Base size - larger when zoomed out for visibility
    const baseSize = 12;
    const size = baseSize + zoomFactor * 8;

    // Neon glow effect when zoomed out
    if (zoomFactor > 0) {
      const glowAlpha = zoomFactor * 0.9;

      // Outermost glow layer (very faint, wide)
      this.waypointGraphics!.circle(0, 0, size + 25);
      this.waypointGraphics!.stroke({ width: 14, color: waypointColor, alpha: glowAlpha * 0.1 * pulse });

      // Middle glow layer
      this.waypointGraphics!.circle(0, 0, size + 15);
      this.waypointGraphics!.stroke({ width: 10, color: waypointColor, alpha: glowAlpha * 0.2 * pulse });

      // Inner glow layer
      this.waypointGraphics!.circle(0, 0, size + 8);
      this.waypointGraphics!.stroke({ width: 6, color: waypointColor, alpha: glowAlpha * 0.35 * pulse });

      // Core glow ring
      this.waypointGraphics!.circle(0, 0, size + 3);
      this.waypointGraphics!.stroke({ width: 3, color: waypointBright, alpha: glowAlpha * 0.7 * pulse });
    }

    // Inner pulsing fill
    this.waypointGraphics!.circle(0, 0, size * 1.5).fill({ color: waypointColor, alpha: 0.25 * pulse });

    // Diamond marker
    this.waypointGraphics!.moveTo(0, -size).lineTo(size, 0).lineTo(0, size).lineTo(-size, 0).closePath();
    this.waypointGraphics!.fill({ color: waypointColor, alpha: 0.9 });
    this.waypointGraphics!.stroke({ width: 2, color: waypointBright });

    // Update distance text
    if (this.waypointText) {
      const distStr = data.distance < 1000 ? `${Math.round(data.distance)}m` : `${(data.distance / 1000).toFixed(1)}km`;
      this.waypointText.text = distStr;
    }
  }

  private handleFastTravelRequest(data: { targetX: number; targetY: number; entityRadius?: number }): void {
    if (!this.player || this.player.isDead() || this.isPlayerDocked || this.isPlayerMining) return;
    // Approach and follow are mutually exclusive — cancel any active follow.
    if (this.followTarget) {
      this.followTarget = null;
      emitGameEvent('followUpdate', { isActive: false });
    }
    this.player.startAutopilot(data.targetX, data.targetY, undefined, data.entityRadius || 0);
  }

  private handleStopFastTravelRequest(): void {
    if (this.player?.isAutopiloting) this.player.stopAutopilot('manual');
  }

  /**
   * Resolve current world position of a tracked entity by id+type.
   * Returns null if the entity is no longer present (despawned, destroyed, AOI exit).
   */
  private resolveEntityPosition(
    type: SelectionEntity['type'],
    id: string,
  ): { x: number; y: number } | null {
    switch (type) {
      case 'player': {
        const p = this.remotePlayers.get(id);
        if (!p) return null;
        const pos = p.getPosition();
        return { x: pos.x, y: pos.y };
      }
      case 'drone':       { const d = this.drones.get(id);     return d ? { x: d.x, y: d.y } : null; }
      case 'station':     { const s = this.stations.get(id);   return s ? { x: s.x, y: s.y } : null; }
      case 'gateway':     { const g = this.gateways.get(id);   return g ? { x: g.x, y: g.y } : null; }
      case 'planet':      { const p = this.planets.get(id);    return p ? { x: p.x, y: p.y } : null; }
      case 'sun':         { const s = this.suns.get(id);       return s ? { x: s.x, y: s.y } : null; }
      case 'nebula':      { const n = this.nebulae.get(id);    return n ? { x: n.x, y: n.y } : null; }
      case 'asteroid':    { const a = this.asteroids.get(id);  return a ? { x: a.x, y: a.y } : null; }
      case 'ice_asteroid':{ const e = this.iceAsteroidData.get(id); return e ? { x: e.x, y: e.y } : null; }
      case 'gas_pocket':  { const e = this.gasPocketData.get(id);   return e ? { x: e.x, y: e.y } : null; }
      case 'wreckage':    { const e = this.wreckageData.get(id);    return e ? { x: e.x, y: e.y } : null; }
      case 'drop':        { const d = this.drops.get(id);       return d ? { x: d.x, y: d.y } : null; }
      case 'settlement':  { const s = this.settlements.get(id); return s ? { x: s.x, y: s.y } : null; }
      default: return null;
    }
  }

  private clampFollowClearance(value: number): number {
    return Math.max(GameScene.FOLLOW_CLEARANCE_MIN, Math.min(GameScene.FOLLOW_CLEARANCE_MAX, value));
  }

  private handleStartFollowRequest(data: { id: string; type: SelectionEntity['type']; name: string; distance?: number }): void {
    if (!this.player || this.player.isDead() || this.isPlayerDocked || this.isPlayerMining) return;

    const pos = this.resolveEntityPosition(data.type, data.id);
    if (!pos) {
      emitGameEvent('notification', { type: 'warning', message: 'Follow target not visible', duration: 1500 });
      return;
    }

    // (tutorial v2 removed the follow trigger — Continue is manual)

    // Clear any pending approach state — follow takes over autopilot
    this.approachTarget = null;

    if (data.distance !== undefined) this.followClearance = this.clampFollowClearance(data.distance);

    const entityRadius = this.getEntityCollisionRadius({ type: data.type, id: data.id, x: pos.x, y: pos.y });
    this.followTarget = {
      id: data.id,
      type: data.type,
      name: data.name,
      entityRadius,
      desiredCenterDistance: entityRadius + this.followClearance,
      mode: 'idle', // updateFollowTracking will pick the right mode on the next tick
    };

    // Don't start autopilot here — the next tick of updateFollowTracking()
    // decides whether to chase, idle, or retreat based on current geometry.
  }

  // Tutorial v2: each frame, check whether the player is inside any beacon's
  // radius. Emits `tutorialBeaconRange` on edge transitions AND writes the
  // current value to networkManager so the panel can read it on mount (covers
  // the spawn-inside-beacon case where the event fires before React mounts).
  // Also drives the cheap pulse animation on the central dot.
  private tutorialBeaconPulse: number = 0;
  private wasInTutorialBeaconInitialized: boolean = false;
  private updateTutorialBeacons(delta: number): void {
    if (this.tutorialBeacons.size === 0) return;
    // delta is in ms. 0.003 rad/ms → full cycle every ~2s (≈0.5 Hz). Gentle
    // amplitude so the dot breathes rather than flashing.
    this.tutorialBeaconPulse += delta * 0.003;
    const sinP = Math.sin(this.tutorialBeaconPulse);
    const dotScale = 1 + 0.08 * sinP;
    const dotAlpha = 0.75 + 0.15 * sinP;

    let inAny = false;
    const px = this.player?.x ?? 0;
    const py = this.player?.y ?? 0;
    for (const [, b] of this.tutorialBeacons) {
      b.dot.scale.set(dotScale);
      b.dot.alpha = dotAlpha;
      if (this.player) {
        const dx = b.x - px;
        const dy = b.y - py;
        if (dx * dx + dy * dy <= b.radius * b.radius) {
          inAny = true;
        }
      }
    }

    // Always write the current value to the cache (cheap boolean assignment),
    // so a late-mounting panel reads the truth. Emit the event only on edges.
    networkManager.setBeaconInRange(inAny);
    if (!this.wasInTutorialBeaconInitialized || inAny !== this.wasInTutorialBeacon) {
      this.wasInTutorialBeaconInitialized = true;
      this.wasInTutorialBeacon = inAny;
      emitGameEvent('tutorialBeaconRange', { inRange: inAny });
    }
  }

  private handleStopFollowRequest(): void {
    if (!this.followTarget) return;
    this.followTarget = null;
    if (this.player?.isAutopiloting) this.player.stopAutopilot('manual');
    emitGameEvent('followUpdate', { isActive: false });
  }

  // Drop any active follow/approach targeting on local death. Player.onDestroy
  // already stops autopilot itself — this just clears the scene-level state so
  // the UI doesn't keep claiming we're following or approaching something.
  private clearLocalCombatTargeting(): void {
    if (this.followTarget) {
      this.followTarget = null;
      emitGameEvent('followUpdate', { isActive: false });
    }
    this.approachTarget = null;
  }

  private handleSetFollowDistance(data: { distance: number }): void {
    this.followClearance = this.clampFollowClearance(data.distance);
    if (this.followTarget) {
      this.followTarget.desiredCenterDistance = this.followTarget.entityRadius + this.followClearance;
      // Mode is re-evaluated next tick by updateFollowTracking() — no need to touch autopilot here.
    }
  }

  /**
   * Drive the follow state machine. Runs every UI tick (~60Hz) but does only
   * trivial math + at most one autopilot transition per tick — no per-tick
   * restarts of motion state.
   *
   *   chasing    → target drifted too far, fly toward it
   *   idle       → within the desired band, hold position
   *   retreating → target got too close, back away to maintain clearance
   */
  private updateFollowTracking(): void {
    if (!this.followTarget) return;

    const pos = this.resolveEntityPosition(this.followTarget.type, this.followTarget.id);
    if (!pos) {
      const wasAutopiloting = this.player?.isAutopiloting ?? false;
      this.followTarget = null;
      if (wasAutopiloting) this.player.stopAutopilot('manual');
      emitGameEvent('followUpdate', { isActive: false });
      emitGameEvent('notification', { type: 'info', message: 'Follow target lost', duration: 1500 });
      return;
    }

    const playerPos = this.player.getCoordinates();
    const dx = pos.x - playerPos.x;
    const dy = pos.y - playerPos.y;
    const distanceToCenter = Math.sqrt(dx * dx + dy * dy);

    const desired = this.followTarget.desiredCenterDistance;
    const chaseAt = desired + GameScene.FOLLOW_CHASE_HYSTERESIS;
    const retreatAt = desired - GameScene.FOLLOW_RETREAT_HYSTERESIS;

    // Decide what the mode SHOULD be this tick based on geometry + current state.
    // Hysteresis bands prevent rapid mode flipping at boundaries.
    let nextMode: 'chasing' | 'idle' | 'retreating' = this.followTarget.mode;
    if (distanceToCenter > chaseAt) {
      nextMode = 'chasing';
    } else if (distanceToCenter < retreatAt) {
      nextMode = 'retreating';
    } else if (!this.player.isAutopiloting) {
      // Inside the comfort band AND autopilot already finished braking.
      nextMode = 'idle';
    }
    // else: stay in current mode until we leave the band (so braking completes).

    const modeChanged = nextMode !== this.followTarget.mode;
    this.followTarget.mode = nextMode;

    if (nextMode === 'idle') {
      // Nothing to do — autopilot already braked us into the comfort zone.
      // If somehow still autopiloting (e.g., target moved into us), let it finish.
    } else if (nextMode === 'chasing') {
      // Target point = entity itself. Autopilot stops at desiredCenterDistance from center.
      if (modeChanged || !this.player.isAutopiloting) {
        this.player.startAutopilot(pos.x, pos.y, undefined, this.followTarget.desiredCenterDistance);
      } else {
        this.player.updateAutopilotTarget(pos.x, pos.y, this.followTarget.desiredCenterDistance);
      }
    } else {
      // retreating: aim at a point on the OPPOSITE side of us from the target,
      // far enough that the autopilot's natural stop distance leaves us at
      // ~desiredCenterDistance from the target.
      // Player is at (px,py); target at (tx,ty). We want to flee directly away
      // from the target. Pick retreat point in the (player − target) direction.
      const invDist = distanceToCenter > 0.001 ? 1 / distanceToCenter : 0;
      const awayX = -dx * invDist; // unit vector from target toward player
      const awayY = -dy * invDist;
      const retreatTotal = desired + GameScene.FOLLOW_RETREAT_BUFFER;
      const retreatX = pos.x + awayX * retreatTotal;
      const retreatY = pos.y + awayY * retreatTotal;

      if (modeChanged || !this.player.isAutopiloting) {
        // entityRadius=0 → autopilot stops shipRadius+30 ≈ 50 short of the retreat point,
        // which lands us right around desiredCenterDistance from the actual target.
        this.player.startAutopilot(retreatX, retreatY, undefined, 0);
      } else {
        // Update the moving retreat point as the target keeps closing in.
        this.player.updateAutopilotTarget(retreatX, retreatY, 0);
      }
    }

    emitGameEvent('followUpdate', {
      isActive: true,
      targetId: this.followTarget.id,
      targetName: this.followTarget.name,
      distanceRemaining: Math.max(0, distanceToCenter - desired),
      followMode: nextMode,
    });
  }

  private handlePlayerDamageForAutopilot(data: { damage: number }): void {
    if (this.player?.isAutopiloting && data.damage > 0) {
      this.player.stopAutopilot('combat');
      emitGameEvent('notification', { message: 'Fast travel interrupted - taking damage!', type: 'warning' });
    }
  }

  private handleRadarScanRequest(data: { x: number; y: number; range: number }): void {
    if (!this.connected || !this.player) return;
    networkManager.sendRadarScan(data.x, data.y, data.range);
    // (tutorial v2 removed the scan trigger — Continue is manual)
  }

  private handleRespawnRequest(): void {
    // Respawn at a spawn point (server will place at nearest station)
    this.respawnPlayer();
    // Notify React that respawn completed
    emitGameEvent('playerRespawned');
  }

  // Debug
  private updateDebugGrid(): void {
    if (!this.debugMode) return;
    if (!this.debugGridGraphics) { this.debugGridGraphics = new Graphics(); this.debugGridGraphics.zIndex = 1000; this.worldContainer.addChild(this.debugGridGraphics); }
  }

  private logEntityCountsIfNeeded(time: number): void {
    if (time - this.lastDebugLogTime < this.DEBUG_LOG_INTERVAL) return;
    this.lastDebugLogTime = time;
    if (this.debugMode) console.log(`Entities: ${this.asteroids.size} asteroids, ${this.remotePlayers.size} players`);
  }

  // UI data
  private emitUIData(): void {
    const nearbyEntities = this.buildNearbyEntities();
    const position = this.player.getCoordinates();
    const rotation = this.player.getRotation();
    const speed = Math.floor(this.player.getSpeed());
    const constellationName = networkManager.getConstellationName();
    const constellationTier = networkManager.getConstellationTier();
    const threatLevel = networkManager.getThreatLevel();

    // Emit to internal event system (legacy)
    this.game.events.emit('updateUI', {
      position,
      rotation,
      speed,
      chunk: { x: 0, y: 0 },
      asteroids: this.asteroids.size,
      chunks: this.connected ? 1 : 0,
      zoom: Math.floor(this.camera.zoom * 100),
      fps: 60,
      players: this.remotePlayers.size + 1,
      connected: this.connected,
      planets: Array.from(this.planetData.values()),
      stations: Array.from(this.stationData.values()),
      asteroidZones: this.asteroidZones || [],
      inSafeZone: this.playerInSafeZone,
      inDockingRange: this.playerInDockingRange,
      nearestStationId: this.nearestStation?.stationId || null,
      constellationName,
      constellationTier,
      threatLevel,
      killsInLastHour: networkManager.getKillsInLastHour(),
      threatCapped: networkManager.isThreatCapped(),
    });
    this.game.events.emit('nearbyEntitiesUpdate', nearbyEntities);

    // Determine activity status for HUD
    let activityStatus: 'idle' | 'moving' | 'mining' | 'combat' | 'docking' | 'warping' | 'stunned' = 'idle';
    if (this.player.warpPhase === 'calculating' || this.player.warpPhase === 'traveling') {
      activityStatus = 'warping';
    } else if (this.player.isStunned) {
      // Stunned from collision - player has no control (3 seconds)
      activityStatus = 'stunned';
    } else if (this.isDockingAnimation) {
      activityStatus = 'docking';
    } else if (this.isPlayerMining || this.isMiningAnimation) {
      activityStatus = 'mining';
    } else if (this.isLaserActive || this.projectiles.length > 0) {
      activityStatus = 'combat';
    } else if (speed > 5) {
      activityStatus = 'moving';
    }

    // Dynamic music transitions based on threat level and combat state
    let desiredMusicTrack = 'space_calm';
    if (activityStatus === 'combat') {
      // In combat - play combat music regardless of threat level
      desiredMusicTrack = 'space_combat';
    } else if (threatLevel === 'red' || threatLevel === 'yellow') {
      // High threat constellation - play danger music
      desiredMusicTrack = 'space_danger';
    } else {
      // Safe constellation - play calm music
      desiredMusicTrack = 'space_calm';
    }

    // Only transition if the desired track is different from current
    if (desiredMusicTrack !== this.currentMusicTrack) {
      audioManager.playMusic(desiredMusicTrack);
      this.currentMusicTrack = desiredMusicTrack;
    }

    // Emit to React HUD via game events bridge
    emitGameEvent('playerStateUpdate', {
      hull: this.player.hull,
      maxHull: this.player.maxHull,
      shield: this.player.shield,
      maxShield: this.player.maxShield,
      credits: this.player.credits,
      x: position.x,
      y: position.y,
      rotation,
      speed,
      zoom: this.camera.zoom,
      totalPower: this.player.getTotalPower(),
      // Weapon heat system
      weaponHeat: this.player.getWeaponHeat(),
      weaponMaxHeat: 100, // overheatThreshold is always 100
      isOverheated: this.player.isWeaponOverheated(),
      hasWeapon: this.player.hasWeapon(),
      // Missile ammo system
      ammoCount: this.player.getAmmoCount(),
      magazineSize: this.player.getMagazineSize(),
      isReloading: this.player.getIsReloading(),
      reloadProgress: this.player.getReloadProgress(),
      isMissileWeapon: this.player.isMissileWeapon(),
      // Boost system
      boostEnergy: this.player.getBoostEnergy(),
      boostMaxEnergy: this.player.getBoostMaxEnergy(),
      isBoosting: this.player.getIsBoosting(),
      hasBooster: this.player.hasBooster(),
      // Ship info for status panel
      shipName: this.player.shipClass.name,
      shipClassType: this.player.shipClass.class,
      shipTier: this.player.shipClass.tier,
      // Activity status
      activityStatus,
      // Shield drag
      isShieldDraining: this.player.isShieldDragActive(),
      // Combat-overhaul ship-level state
      shipHeat: this.player.shipHeat,
      shipMaxHeat: this.player.shipMaxHeat,
      isShipOverheated: this.player.isShipOverheated,
      shieldActive: (this.player as any)._shieldActive ?? false,
      isVenting: this.player.isVenting,
      absorbedHeat: (this.player as any)._absorbedHeat ?? 0,
      powerCapacity: (this.player as any)._powerCapacity ?? 0,
      // Active weapon info for HUD
      ...(() => {
        const wi = this.player!.getActiveWeaponInfo();
        return wi ? {
          activeWeaponName: wi.name,
          activeWeaponType: wi.weaponType,
          activeWeaponIndex: wi.index,
          equippedWeaponCount: wi.total,
        } : {};
      })(),
    });

    // Emit utility hotbar state to React
    emitGameEvent('utilityHotbarUpdate', {
      slots: this.player.getUtilityHotbarState(),
    });

    // Emit constellation info to React
    emitGameEvent('constellationInfo', {
      name: constellationName || 'Unknown',
      tier: constellationTier || 1,
      threatLevel: (threatLevel as 'green' | 'yellow' | 'red') || 'green',
    });

    // Emit nearby entities to React HUD
    emitGameEvent('nearbyEntitiesUpdate', nearbyEntities);

    // Emit mining tool state to React HUD (for Mine button)
    emitGameEvent('miningToolInfo', {
      toolRange: this.getEquippedMiningToolRange(),
      toolTier: this.getEquippedMiningToolTier(),
      isMining: this.isPlayerMining,
      miningAsteroidId: this.miningAsteroidId,
      miningTargetIds: [...this.miningTargets.keys()],
      miningTargetCount: this.miningTargets.size,
      sessionResources: this.miningSessionResources,
      sessionStartTime: this.miningSessionStartTime,
    });

    // Emit waypoint update to React HUD
    this.emitWaypointUpdate(position);

    // Update follow tracking (re-targets autopilot to a moving entity each tick)
    this.updateFollowTracking();

    // Emit approach status to React HUD
    this.emitFastTravelUpdate();

    // Check sun radiation proximity for UI warning (continuous check, not just on damage)
    this.checkSunRadiationProximity();
  }

  // Check if player is in any sun's radiation zone and emit warning
  private checkSunRadiationProximity(): void {
    let highestProximity = 0;
    let estimatedDamage = 0;

    for (const [, sun] of this.suns) {
      const proximity = sun.getRadiationProximity(this.player.x, this.player.y);
      if (proximity > highestProximity) {
        highestProximity = proximity;
        // Estimate DPS using exponential formula from SUN_CONFIG
        // damage = baseDPS * e^(8 * proximity)
        const baseDPS = sun.baseDamagePerSecond;
        estimatedDamage = Math.floor(baseDPS * Math.exp(8 * proximity));
      }
    }

    // Emit radiation proximity (even when 0 to clear warning)
    if (highestProximity > 0) {
      emitGameEvent('radiationProximity', {
        proximity: highestProximity,
        damage: estimatedDamage,
      });
    } else {
      emitGameEvent('radiationProximity', null);
    }
  }

  // Load waypoint from localStorage
  private loadWaypoint(): void {
    const saved = localStorage.getItem('astra_waypoint');
    if (saved) {
      try {
        this.currentWaypoint = JSON.parse(saved);
        // Restore warp preparation for persisted waypoint
        if (this.currentWaypoint && this.player) {
          this.player.setWarpWaypoint(this.currentWaypoint.x, this.currentWaypoint.y);
        }
      } catch {
        // Invalid data, ignore
      }
    }
  }

  // Emit waypoint update to React for compass indicator
  private emitWaypointUpdate(playerPosition: { x: number; y: number }): void {
    if (!this.currentWaypoint) {
      emitGameEvent('waypointUpdate', null);
      this.updateWaypointMarker(null);
      return;
    }

    const waypoint = this.currentWaypoint;
    const dx = waypoint.x - playerPosition.x;
    const dy = waypoint.y - playerPosition.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    // If player reached the waypoint (within 100m), auto-clear it
    if (distance < 100) {
      this.currentWaypoint = null;
      localStorage.removeItem('astra_waypoint');
      this.player?.clearWarpWaypoint();
      emitGameEvent('waypointUpdate', null);
      this.updateWaypointMarker(null);
      emitGameEvent('notification', {
        type: 'info',
        message: 'Waypoint reached',
        duration: 2000,
      });
      // Stop approach if active
      if (this.player.isAutopiloting) {
        this.player.stopAutopilot('arrival');
      }
      return;
    }

    // Check if waypoint is visible on screen
    const screenWidth = this.app.screen.width;
    const screenHeight = this.app.screen.height;
    const zoom = this.camera.zoom;
    const screenWaypointX = screenWidth / 2 + dx * zoom;
    const screenWaypointY = screenHeight / 2 + dy * zoom;
    const margin = 80;
    const isOnScreen = screenWaypointX > margin && screenWaypointX < screenWidth - margin &&
                       screenWaypointY > margin && screenWaypointY < screenHeight - margin;

    // Update world marker (visible when waypoint is on screen)
    this.updateWaypointMarker({
      x: waypoint.x,
      y: waypoint.y,
      name: waypoint.name,
      distance,
      showWorldMarker: isOnScreen || distance < 500,
    });

    // Emit to React for edge compass indicator
    emitGameEvent('waypointUpdate', {
      x: waypoint.x,
      y: waypoint.y,
      name: waypoint.name,
      distance,
      angle,
      isOnScreen,
    });
  }

  // Emit approach status to React
  private emitFastTravelUpdate(): void {
    if (this.player.isAutopiloting && this.approachTarget) {
      const playerPos = this.player.getCoordinates();
      const dx = this.approachTarget.x - playerPos.x;
      const dy = this.approachTarget.y - playerPos.y;
      const distanceRemaining = Math.sqrt(dx * dx + dy * dy);
      const speed = this.player.getSpeed();
      const estimatedTimeSeconds = speed > 0 ? distanceRemaining / speed : 0;

      emitGameEvent('fastTravelUpdate', {
        isActive: true,
        targetX: this.approachTarget.x,
        targetY: this.approachTarget.y,
        targetName: this.approachTarget.name,
        distanceRemaining,
        estimatedTimeSeconds,
      });
    } else {
      // Clear approach target when not autopiloting
      this.approachTarget = null;
      emitGameEvent('fastTravelUpdate', {
        isActive: false,
      });
    }
  }

  private buildNearbyEntities(): NearbyEntity[] {
    // Detection range = base sensorRange + sensorBonus from equipped Sensor Array enhancer
    const baseSensorRange = this.player.shipClass?.stats.sensorRange || 500;
    const overviewRange = baseSensorRange + this.player.getSensorBonus();
    const pp = this.player.getCoordinates();
    const entities: NearbyEntity[] = [];

    // Remote players - include full ship info
    for (const [id, rp] of this.remotePlayers) {
      const pos = rp.getPosition();
      const dist = Math.sqrt((pos.x - pp.x) ** 2 + (pos.y - pp.y) ** 2);
      if (dist <= overviewRange) {
        entities.push({
          id,
          type: 'player',
          name: rp.username,
          distance: dist,
          x: pos.x,
          y: pos.y,
          shipName: rp.shipClass.name,
          shipClassType: rp.shipClass.class,
          shipTier: rp.shipClass.tier,
          shipSize: rp.shipClass.size,
          totalPower: rp.totalPower,
          hull: rp.hull,
          maxHull: rp.maxHull,
          shield: rp.shield,
          maxShield: rp.maxShield,
        });
      }
    }

    // Stations
    for (const [id, station] of this.stations) { const dist = Math.sqrt((station.x - pp.x) ** 2 + (station.y - pp.y) ** 2); if (dist <= overviewRange) entities.push({ id, type: 'station', name: station.stationName, distance: dist, x: station.x, y: station.y }); }

    const asteroidEntries: Array<{ id: string; data: AsteroidData; dist: number }> = [];
    for (const [id, ad] of this.asteroidData) { const dist = Math.sqrt((ad.x - pp.x) ** 2 + (ad.y - pp.y) ** 2); if (dist <= overviewRange) asteroidEntries.push({ id, data: ad, dist }); }
    asteroidEntries.sort((a, b) => a.dist - b.dist);
    const sTier = networkManager.getConstellationTier() || 1;
    for (const e of asteroidEntries.slice(0, 10)) {
      const liveAsteroid = this.asteroids.get(e.id);
      const aRadius = liveAsteroid ? liveAsteroid.getRadius() : (COLLISION_CONFIG.ASTEROID_RADII[e.data.size] || 80);
      entities.push({ id: e.id, type: 'asteroid', name: `T${sTier} Asteroid`, distance: e.dist, x: e.data.x, y: e.data.y, data: { resourceType: e.data.resourceType, health: e.data.health, maxHealth: e.data.maxHealth, size: e.data.size, radius: aRadius } });
    }

    // V1 harvestables — nearest 6 of each kind for overview
    const pushHarvestable = (
      type: 'ice_asteroid' | 'gas_pocket' | 'wreckage',
      labelFn: (resourceType: string, tier: number) => string,
      dataMap: Map<string, HarvestableEntityData>,
    ) => {
      const arr: Array<{ id: string; data: HarvestableEntityData; dist: number }> = [];
      for (const [id, d] of dataMap) {
        const dist = Math.sqrt((d.x - pp.x) ** 2 + (d.y - pp.y) ** 2);
        if (dist <= overviewRange) arr.push({ id, data: d, dist });
      }
      arr.sort((a, b) => a.dist - b.dist);
      for (const e of arr.slice(0, 6)) {
        entities.push({
          id: e.id,
          type,
          name: labelFn(e.data.resourceType, e.data.resourceTier ?? sTier),
          distance: e.dist,
          x: e.data.x, y: e.data.y,
          data: {
            resourceType: e.data.resourceType,
            resourceTier: e.data.resourceTier,
            health: e.data.health, maxHealth: e.data.maxHealth,
            size: e.data.size,
          },
        });
      }
    };
    pushHarvestable('ice_asteroid', (r, t) => `T${t} Ice`, this.iceAsteroidData);
    pushHarvestable('gas_pocket',   (r, t) => `T${t} Gas`, this.gasPocketData);
    pushHarvestable('wreckage',     (r, t) => `T${t} Wreckage`, this.wreckageData);

    for (const [id, drop] of this.drops) { const dist = Math.sqrt((drop.x - pp.x) ** 2 + (drop.y - pp.y) ** 2); if (dist <= overviewRange) entities.push({ id, type: 'drop', name: 'Loot Drop', distance: dist, x: drop.x, y: drop.y }); }

    // Drones (station drones, rogue drones)
    for (const [id, drone] of this.drones) {
      const dist = Math.sqrt((drone.x - pp.x) ** 2 + (drone.y - pp.y) ** 2);
      if (dist <= overviewRange) {
        const cap = drone.droneType.charAt(0).toUpperCase() + drone.droneType.slice(1);
        const label = drone.isRogue ? `⚠ Rogue ${cap}` : cap;
        entities.push({
          id,
          type: 'drone',
          name: label,
          distance: dist,
          x: drone.x,
          y: drone.y,
          hull: drone.hull,
          maxHull: drone.maxHull,
          shield: drone.shield,
          maxShield: drone.maxShield,
          data: { droneType: drone.droneType, isRogue: drone.isRogue, state: drone.droneState },
        });
      }
    }

    // Settlements (emergent rogue-drone nests)
    for (const [id, settlement] of this.settlements) {
      const dist = Math.sqrt((settlement.x - pp.x) ** 2 + (settlement.y - pp.y) ** 2);
      if (dist <= overviewRange) {
        entities.push({
          id,
          type: 'settlement',
          name: '⚠ Rogue Settlement',
          distance: dist,
          x: settlement.x,
          y: settlement.y,
          hull: settlement.coreHull,
          maxHull: settlement.coreMaxHull,
          data: { coreExposed: settlement.coreExposed },
        });
      }
    }

    for (const [id, gateway] of this.gateways) { const dist = Math.sqrt((gateway.x - pp.x) ** 2 + (gateway.y - pp.y) ** 2); if (dist <= overviewRange) entities.push({ id, type: 'gateway', name: `Gate → ${gateway.targetConstellationName}`, distance: dist, x: gateway.x, y: gateway.y }); }

    // Planets - detect when near the planet's surface (dist to edge, not center)
    for (const [id, planet] of this.planets) {
      const pd = this.planetData.get(id);
      const radius = pd?.radius || 100;
      const distToCenter = Math.sqrt((planet.x - pp.x) ** 2 + (planet.y - pp.y) ** 2);
      const distToEdge = Math.max(0, distToCenter - radius);
      if (distToEdge <= overviewRange) {
        entities.push({
          id,
          type: 'planet',
          name: pd?.name || 'Planet',
          distance: distToEdge,
          x: planet.x,
          y: planet.y,
          data: { planetType: pd?.planetType, radius },
        });
      }
    }

    // Suns - detect when near the sun (dangerous!)
    for (const [id, sun] of this.suns) {
      const radius = sun.sunRadius || 200;
      const distToCenter = Math.sqrt((sun.x - pp.x) ** 2 + (sun.y - pp.y) ** 2);
      const distToEdge = Math.max(0, distToCenter - radius);
      if (distToEdge <= overviewRange) {
        entities.push({
          id,
          type: 'sun',
          name: sun.sunName || 'Star',
          distance: distToEdge,
          x: sun.x,
          y: sun.y,
          data: { starType: sun.starType, radius, damageRadius: sun.damageRadius },
        });
      }
    }

    // Nebulae - detect when inside or near the nebula
    for (const [id, nebula] of this.nebulae) {
      const radius = nebula.nebulaRadius || 500;
      const distToCenter = Math.sqrt((nebula.x - pp.x) ** 2 + (nebula.y - pp.y) ** 2);
      const distToEdge = Math.max(0, distToCenter - radius);
      if (distToEdge <= overviewRange) {
        entities.push({
          id,
          type: 'nebula',
          name: nebula.nebulaName || 'Nebula',
          distance: distToEdge,
          x: nebula.x,
          y: nebula.y,
          data: { nebulaType: nebula.nebulaType, resourceType: nebula.resourceType, radius },
        });
      }
    }

    return entities;
  }

  // Helper to get equipped mining tool tier
  private getEquippedMiningToolTier(): number {
    if (!this.player?.slots) return 0;
    for (const slot of this.player.slots) {
      if (slot.equipped) {
        const equip = getEquipmentById(slot.equipped);
        if (equip?.category === 'TOOL') {
          return equip.tier;
        }
      }
    }
    return 0; // No tool equipped
  }

  // Helper to get equipped mining tool power (0 if none)
  private getEquippedMiningToolPower(): number {
    if (!this.player?.slots) return 0;
    for (const slot of this.player.slots) {
      if (slot.equipped) {
        const equip = getEquipmentById(slot.equipped);
        if (equip?.category === 'TOOL') {
          return (equip as any).stats?.miningPower || 0;
        }
      }
    }
    return 0;
  }

  // Helper to get equipped mining tool range (0 if none)
  private getEquippedMiningToolRange(): number {
    if (!this.player?.slots) return 0;
    for (const slot of this.player.slots) {
      if (slot.equipped) {
        const equip = getEquipmentById(slot.equipped);
        if (equip?.category === 'TOOL') {
          return (equip as any).stats?.range || 0;
        }
      }
    }
    return 0;
  }

  /**
   * Get collision radius for a selected entity (for approach stopping distance).
   */
  private getEntityCollisionRadius(entity: SelectionEntity): number {
    if (entity.type === 'asteroid' && entity.id) {
      const asteroid = this.asteroids.get(entity.id);
      if (asteroid) return asteroid.collisionRadius;
      // Fallback to COLLISION_CONFIG if entity not found
      const size = (entity.data?.size as string) || 'medium';
      return COLLISION_CONFIG.ASTEROID_RADII[size] || 80;
    }
    if ((entity.type === 'ice_asteroid' || entity.type === 'gas_pocket' || entity.type === 'wreckage') && entity.id) {
      const live =
        entity.type === 'ice_asteroid' ? this.iceAsteroids.get(entity.id) :
        entity.type === 'gas_pocket'   ? this.gasPockets.get(entity.id) :
                                         this.wreckages.get(entity.id);
      if (live) return live.collisionRadius;
      const size = (entity.data?.size as string) || 'medium';
      return COLLISION_CONFIG.ASTEROID_RADII[size] || 80;
    }
    if (entity.type === 'station') return 360;
    if (entity.type === 'settlement') return 450;
    if (entity.type === 'player') return this.player.collisionRadius;
    if (entity.type === 'gateway') return 40;
    if (entity.type === 'planet') return 200;
    if (entity.type === 'sun') return 300;
    return 0; // Waypoints, drops, etc. - no collision radius
  }

  /**
   * Get collision radius for an entity at a given position (for overview panel approach).
   */
  private getEntityCollisionRadiusAt(x: number, y: number): number {
    const threshold = 50; // Match within 50 units
    for (const [, asteroid] of this.asteroids) {
      if (Math.abs(asteroid.x - x) < threshold && Math.abs(asteroid.y - y) < threshold) {
        return asteroid.collisionRadius;
      }
    }
    for (const [, station] of this.stations) {
      if (Math.abs(station.x - x) < threshold && Math.abs(station.y - y) < threshold) {
        return 120;
      }
    }
    for (const [, h] of this.iceAsteroids) {
      if (Math.abs(h.x - x) < threshold && Math.abs(h.y - y) < threshold) return h.collisionRadius;
    }
    for (const [, h] of this.gasPockets) {
      if (Math.abs(h.x - x) < threshold && Math.abs(h.y - y) < threshold) return h.collisionRadius;
    }
    for (const [, h] of this.wreckages) {
      if (Math.abs(h.x - x) < threshold && Math.abs(h.y - y) < threshold) return h.collisionRadius;
    }
    return 0;
  }

  // Public API
  public getObstaclesForAvoidance(): Array<{ x: number; y: number; radius: number }> {
    const obstacles: Array<{ x: number; y: number; radius: number }> = [];
    if (!this.player) return obstacles;
    const px = this.player.x, py = this.player.y;
    // Dynamic check radius based on player speed - faster = check further ahead
    const speed = this.player.getSpeed();
    const checkR = Math.max(800, speed * 3); // Wider check range for multi-obstacle avoidance
    const checkRSq = checkR ** 2;

    // Helper: skip the approach target itself (we WANT to reach it)
    const isApproachTarget = (x: number, y: number) =>
      this.approachTarget &&
      Math.abs(x - this.approachTarget.x) < 5 &&
      Math.abs(y - this.approachTarget.y) < 5;

    // Use actual collision radii from COLLISION_CONFIG for accurate avoidance
    const asteroidRadii = COLLISION_CONFIG.ASTEROID_RADII;

    for (const [, asteroid] of this.asteroids) {
      const dxSq = (asteroid.x - px) ** 2 + (asteroid.y - py) ** 2;
      if (dxSq < checkRSq) {
        if (isApproachTarget(asteroid.x, asteroid.y)) continue;
        obstacles.push({ x: asteroid.x, y: asteroid.y, radius: asteroidRadii[asteroid.size] || 80 });
      }
    }

    for (const [, station] of this.stations) {
      const dxSq = (station.x - px) ** 2 + (station.y - py) ** 2;
      if (dxSq < checkRSq) {
        if (isApproachTarget(station.x, station.y)) continue;
        obstacles.push({ x: station.x, y: station.y, radius: 360 });
      }
    }

    // Suns — use damageRadius as the avoidance boundary (glow edge is dangerous)
    for (const [, sun] of this.suns) {
      const dxSq = (sun.x - px) ** 2 + (sun.y - py) ** 2;
      if (dxSq < (checkR + sun.damageRadius) ** 2) {
        obstacles.push({ x: sun.x, y: sun.y, radius: sun.damageRadius });
      }
    }

    // Planets — solid bodies with their visual radius
    for (const [, planet] of this.planets) {
      const dxSq = (planet.x - px) ** 2 + (planet.y - py) ** 2;
      const r = planet.planetRadius;
      if (dxSq < (checkR + r) ** 2) {
        if (isApproachTarget(planet.x, planet.y)) continue;
        obstacles.push({ x: planet.x, y: planet.y, radius: r });
      }
    }

    // Other players — avoid crashing into them (skip warping players, they're intangible)
    for (const [, rp] of this.remotePlayers) {
      if (rp.warpPhase === 'calculating' || rp.warpPhase === 'traveling') continue;
      const dxSq = (rp.x - px) ** 2 + (rp.y - py) ** 2;
      if (dxSq < checkRSq) {
        obstacles.push({ x: rp.x, y: rp.y, radius: rp.collisionRadius });
      }
    }

    return obstacles;
  }

  // Cleanup
  shutdown(): void {
    audioManager.stopMusic();
    tweens.killAll();

    // Clear scanned radar entities when leaving game
    emitGameEvent('radarClearEntities', undefined);

    // Remove canvas event listeners
    if (this.wheelHandler) {
      this.app.canvas.removeEventListener('wheel', this.wheelHandler);
      this.wheelHandler = null;
    }

    // Remove local EventEmitter listeners
    const events = this.game.events;
    for (const { event, callback } of this.boundEventListeners) {
      events.off(event, callback);
    }
    this.boundEventListeners = [];

    // Unsubscribe from React game events
    for (const unsub of this.reactEventUnsubscribers) {
      unsub();
    }
    this.reactEventUnsubscribers = [];

    for (const [, p] of this.remotePlayers) p.destroy(); this.remotePlayers.clear();
    for (const [, a] of this.asteroids) a.destroy(); this.asteroids.clear(); this.asteroidData.clear();
    for (const [, e] of this.iceAsteroids) e.destroy(); this.iceAsteroids.clear(); this.iceAsteroidData.clear();
    for (const [, e] of this.gasPockets) e.destroy(); this.gasPockets.clear(); this.gasPocketData.clear();
    for (const [, e] of this.wreckages) e.destroy(); this.wreckages.clear(); this.wreckageData.clear();
    for (const [, s] of this.stations) s.destroy(); this.stations.clear();
    for (const [, d] of this.drops) d.destroy(); this.drops.clear();
    for (const [, s] of this.suns) s.destroy(); this.suns.clear();
    for (const [, p] of this.planets) p.destroy(); this.planets.clear(); this.planetData.clear();
    for (const [, n] of this.nebulae) n.destroy(); this.nebulae.clear();
    for (const p of this.projectiles) p.destroy(); this.projectiles = [];
    for (const p of this.networkProjectiles) p.destroy(); this.networkProjectiles = [];
    this.remoteMinerAsteroids.clear();
    for (const [, laser] of this.remoteMiningLasers) { laser.stopEmitting(); laser.destroy(); }
    this.remoteMiningLasers.clear();
    this.droneMiningBeams.clear();
    for (const [, viz] of this.droneWarpVisuals) viz.destroy();
    this.droneWarpVisuals.clear();
    for (const particle of this.activeHitParticles) particle.sprite.destroy(); this.activeHitParticles.clear();
    for (const [, t] of this.hitParticleTextures) t.destroy(); this.hitParticleTextures.clear();
  }
}
